import React from 'react';
import PropTypes from 'prop-types';
import { safeRest } from '@wds/utils';
import { createUseStyles, useTheme } from 'react-jss';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var objectWithoutPropertiesLoose = _objectWithoutPropertiesLoose;

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var objectWithoutProperties = _objectWithoutProperties;

const SEMANTIC_COLORS = Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
const TYPE_COLORS = Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
const SIZES = Object.freeze(['lg', 'md', 'sm']);
Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
Object.freeze([...SIZES, 'xs']);
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var defineProperty = _defineProperty;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const useStyles = createUseStyles(theme => ({
  wdsIcons: ({
    color,
    size
  }) => {
    const fontSize = size ? {
      fontSize: theme.iconSize[size]
    } : {
      fontSize: 'inherit'
    };
    const chosenColor = theme.typeColor[color] || theme.semanticColor[color] || 'currentColor';
    return _objectSpread(_objectSpread({}, fontSize), {}, {
      width: '1em',
      height: '1em',
      display: 'inline-block',
      margin: '0 auto',
      verticalAlign: 'middle',
      textAlign: 'center',
      fill: chosenColor
    });
  }
}));

function getDisplayName(Component) {
  return Component.displayName || Component.name || 'Component';
}

function withIcon(Component) {
  function WithIcon(_ref) {
    let color = _ref.color,
        title = _ref.title,
        size = _ref.size,
        rest = objectWithoutProperties(_ref, ["color", "title", "size"]);

    const theme = useTheme();
    const classes = useStyles({
      color,
      size,
      theme
    });
    return /*#__PURE__*/React.createElement("svg", Object.assign({
      className: classes.wdsIcons,
      viewBox: "0 0 16 16",
      preserveAspectRatio: "xMidYMid",
      "aria-label": title,
      role: "img"
    }, safeRest(rest)), /*#__PURE__*/React.createElement(Component, null));
  }

  WithIcon.displayName = `WithIcon(${getDisplayName(Component)})`;

  WithIcon.getOriginalComponent = () => Component;

  WithIcon.propTypes = {
    /**
     * Required prop for a11y
     */
    // eslint-disable-next-line consistent-return
    title: (props, propName) => {
      if (typeof props['aria-hidden'] === 'undefined' && typeof props[propName] === 'undefined') {
        return new Error('title is required');
      }
    },

    /**
     * Different icon variant sizes
     */
    size: PropTypes.oneOf(SIZES.concat(['xl'])),

    /**
     * Color variants
     */
    color: PropTypes.oneOf(SEMANTIC_COLORS.concat(TYPE_COLORS))
  };
  WithIcon.defaultProps = {
    title: undefined,
    color: undefined,
    size: undefined
  };
  return WithIcon;
}

const IconAirplane = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.89 1.1893-.001-.0092a.6981.6981 0 0 0 -.1324-.2468c-.0128-.0154-.0311-.0222-.0449-.0362-.0089-.009-.01-.0225-.02-.0311-.0151-.0138-.0367-.0147-.0527-.0271a.6751.6751 0 0 0 -.229-.1178.66.66 0 0 0 -.08-.0235.6882.6882 0 0 0 -.33.0224l-.01.0007-14.1487 4.9079a1.12 1.12 0 0 0 -.1929 2.0278l4.0235 2.3215.0009 4.0863a1.1819 1.1819 0 0 0 .7319 1.1543 1.182 1.182 0 0 0 1.3369-.2925l2.2522-2.4545 1.97 1.1366a1.4349 1.4349 0 0 0 2.1074-.8906l2.8262-11.1641c.0035-.0141-.0036-.0278-.0009-.042a.691.691 0 0 0 -.0065-.3221zm-4.07 2.1121-6.5134 5.4262-3.4273-1.9776zm-5.7478 10.2838v-2.7994l1.68.9691zm5.5913-1.19-5.0643-2.9219 7.4014-6.1659z"
  }));
};

IconAirplane.displayName = "IconAirplane";
IconAirplane.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconAirplane"
};
var Airplane = withIcon(IconAirplane);

const IconArrow180 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.7941 11.7632c-.0051-.012-.0153-.0206-.0209-.0322a.7353.7353 0 0 0 -.13-.2017l-2.2046-2.2632a.75.75 0 0 0 -1.0742 1.0469l.9645.99h-7.34a4.342 4.342 0 1 1 0-8.6841h9.1094a.75.75 0 0 0 0-1.5h-9.1089a5.842 5.842 0 1 0 0 11.6841h7.2892l-.8039.794a.75.75 0 0 0 1.0547 1.0674l2.1035-2.0781a.7475.7475 0 0 0 .1612-.8231z"
  }));
};

IconArrow180.displayName = "IconArrow180";
IconArrow180.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrow180"
};
var Arrow180 = withIcon(IconArrow180);

const IconArrow360 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m10.4021 3.3418a.75.75 0 1 0 -.0576 1.498 4.2814 4.2814 0 0 1 -.167 8.56h-4.3545a4.2813 4.2813 0 1 1 0-8.5625h.2044l-.78.7705a.75.75 0 1 0 1.0551 1.0661l2.0775-2.0539a.75.75 0 0 0 .01-1.0566l-2.1769-2.2362a.75.75 0 0 0 -1.0751 1.0469l.9375.9629h-.2525a5.7812 5.7812 0 1 0 0 11.5625h4.3545a5.781 5.781 0 0 0 .2246-11.5576z"
  }));
};

IconArrow360.displayName = "IconArrow360";
IconArrow360.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrow360"
};
var Arrow360 = withIcon(IconArrow360);

const IconArrowBox = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M12.429,14.422H1.8a.6636.6636,0,0,1-.6644-.6644V3.1281A.6635.6635,0,0,1,1.8,2.4638H5.3426a.6644.6644,0,1,1,0,1.3287H2.4638v9.3008h9.3008V10.2145a.6644.6644,0,1,1,1.3287,0v3.5431A.6635.6635,0,0,1,12.429,14.422Z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "ArrowBox-2",
    "data-name": "ArrowBox",
    d: "M14.3733,1.5559c0-.0045,0-.0089-.0045-.0089a.6783.6783,0,0,0-.3631-.3632c-.0045,0-.0089,0-.0089-.0044a.6952.6952,0,0,0-.2392-.0443H8.4429a.6644.6644,0,0,0,0,1.3287h3.7115L7.9734,6.6447a.66.66,0,0,0,0,.939.6629.6629,0,0,0,.939,0l4.1809-4.181V7.1142a.6644.6644,0,1,0,1.3287,0V1.8A.646.646,0,0,0,14.3733,1.5559Z"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "0.0279",
    y: "0.0279",
    width: "15.9442",
    height: "15.9442",
    fill: "none"
  }));
};

IconArrowBox.displayName = "IconArrowBox";
IconArrowBox.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowBox"
};
var ArrowBox = withIcon(IconArrowBox);

const IconArrowCombine = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.5322 7.3935-4.125-2.3814a.7.7 0 0 0 -1.05.6065v1.6814h-1.6912a5.0313 5.0313 0 0 1 -4.3092-2.3146 2.3761 2.3761 0 0 0 .56-1.522 2.4 2.4 0 1 0 -2.4 2.4 2.3665 2.3665 0 0 0 .6751-.1086 6.816 6.816 0 0 0 2.5032 2.2452 6.816 6.816 0 0 0 -2.5034 2.2453 2.3665 2.3665 0 0 0 -.6751-.1086 2.4 2.4 0 1 0 2.4 2.4 2.376 2.376 0 0 0 -.56-1.522 5.0312 5.0312 0 0 1 4.3094-2.3147h1.6914v1.6811a.7.7 0 0 0 1.05.6065l4.125-2.3814a.7.7 0 0 0 0-1.2129zm-14.0146-3.9301a1 1 0 1 1 .999 1 1 1 0 0 1 -.999-1zm.999 10.0727a1 1 0 1 1 1-1 1 1 0 0 1 -1 1zm9.2412-4.3672v-2.3379l2.0244 1.169z"
  }));
};

IconArrowCombine.displayName = "IconArrowCombine";
IconArrowCombine.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowCombine"
};
var ArrowCombine = withIcon(IconArrowCombine);

const IconArrowDashed = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8.9956 14.9531a.75.75 0 0 1 -.53-1.28l5.6726-5.6731-5.6729-5.6729a.75.75 0 1 1 1.0606-1.0605l6.2031 6.2034a.75.75 0 0 1 0 1.0605l-6.2031 6.2029a.7467.7467 0 0 1 -.5303.2197z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.5386 8.75h-3.7373a.75.75 0 1 1 0-1.5h3.7373a.75.75 0 0 1 0 1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.1353 8.75h-3.7383a.75.75 0 0 1 0-1.5h3.7383a.75.75 0 0 1 0 1.5z"
  }));
};

IconArrowDashed.displayName = "IconArrowDashed";
IconArrowDashed.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowDashed"
};
var ArrowDashed = withIcon(IconArrowDashed);

const IconArrowDown = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.57 8.655a.75.75 0 0 0 -1.0605 0l-4.76 4.76v-12.6403a.75.75 0 1 0 -1.5 0v12.64l-4.7595-4.7597a.75.75 0 0 0 -1.06 1.0606l6.04 6.04a.7514.7514 0 0 0 1.0606 0l6.04-6.04a.75.75 0 0 0 -.0006-1.0606z"
  }));
};

IconArrowDown.displayName = "IconArrowDown";
IconArrowDown.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowDown"
};
var ArrowDown = withIcon(IconArrowDown);

const IconArrowExit = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.6843 7.47-4.97-4.97a.75.75 0 1 0 -1.0603 1.06l3.69 3.69h-9.254a.75.75 0 0 0 0 1.5h9.2539l-3.6898 3.6895a.75.75 0 1 0 1.0605 1.0605l4.97-4.97a.75.75 0 0 0 -.0003-1.06z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.6585 13.875h-3.0625v-11.75h3.0625a.75.75 0 0 0 0-1.5h-3.8125a.75.75 0 0 0 -.75.75v13.25a.75.75 0 0 0 .75.75h3.8125a.75.75 0 0 0 0-1.5z"
  }));
};

IconArrowExit.displayName = "IconArrowExit";
IconArrowExit.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowExit"
};
var ArrowExit = withIcon(IconArrowExit);

const IconArrowForward = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.9035 7.7136a.75.75 0 0 0 -.1618-.2436l-6.0517-6.0518a.75.75 0 1 0 -1.06 1.0606l4.7709 4.7712h-7.02c-2.6695 0-4.8409-1.5237-4.8409-3.3972a.75.75 0 1 0 -1.5 0c0 2.7007 2.8443 4.8972 6.3414 4.8972h7.02l-4.7714 4.7712a.75.75 0 1 0 1.06 1.0606l6.0513-6.0513a.7527.7527 0 0 0 .1618-.8169z"
  }));
};

IconArrowForward.displayName = "IconArrowForward";
IconArrowForward.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowForward"
};
var ArrowForward = withIcon(IconArrowForward);

const IconArrowIntegrate = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m5.26 12.1213a4.1328 4.1328 0 1 1 4.2429-5.368l-.76-.4582a.65.65 0 0 0 -.6719 1.1133l2.06 1.2422a.6508.6508 0 0 0 .8926-.2207l1.2422-2.0591a.65.65 0 0 0 -1.1133-.6719l-.3274.5429a5.5316 5.5316 0 1 0 -5.6726 7.275c.0181.0015.0366.0024.0547.0024a.7.7 0 0 0 .0527-1.398z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m10.894 2.4871a.7.7 0 0 0 -.1191 1.3945 4.1327 4.1327 0 1 1 -4.2899 5.3274l.783.4724a.65.65 0 0 0 .672-1.1133l-2.06-1.2422a.6506.6506 0 0 0 -.8926.2207l-1.2418 2.0591a.65.65 0 0 0 1.1133.6719l.3147-.5218a5.5316 5.5316 0 1 0 5.72-7.2687z"
  }));
};

IconArrowIntegrate.displayName = "IconArrowIntegrate";
IconArrowIntegrate.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowIntegrate"
};
var ArrowIntegrate = withIcon(IconArrowIntegrate);

const IconArrowLeft = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.2246 7.25h-12.6397l4.7593-4.76a.75.75 0 1 0 -1.0605-1.06l-6.0396 6.04a.7527.7527 0 0 0 0 1.0605l6.04 6.04a.75.75 0 1 0 1.0601-1.0605l-4.7593-4.76h12.64a.75.75 0 1 0 0-1.5z"
  }));
};

IconArrowLeft.displayName = "IconArrowLeft";
IconArrowLeft.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowLeft"
};
var ArrowLeft = withIcon(IconArrowLeft);

const IconArrowRandom = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m10.6768 7.2315h-5.3477a.75.75 0 0 0 0 1.5h5.3477a.75.75 0 0 0 0-1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.7222 2.5977-1.7281-2.2549c-.0084-.011-.0226-.0145-.0317-.0249a.7247.7247 0 0 0 -1.1271 0c-.009.01-.0232.0139-.0317.0249l-1.728 2.2549a.75.75 0 1 0 1.19.9121l.3828-.4993v4.7131c0 1.3574-.7622 2.5049-1.6641 2.5049h-5.6552a.75.75 0 0 0 0 1.5h5.6558c1.7446 0 3.1641-1.7969 3.1641-4.0049v-4.7136l.3828.4993a.75.75 0 1 0 1.19-.9121z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m3.7339 12.49-.3828.4993v-4.7129c0-1.3574.7622-2.5049 1.6641-2.5049h5.6616a.75.75 0 0 0 0-1.5h-5.6617c-1.7446 0-3.1641 1.7969-3.1641 4.0049v4.7136l-.3827-.5a.75.75 0 1 0 -1.19.9121l1.728 2.2549c.009.0118.0238.0164.0334.0275a.7354.7354 0 0 0 .1714.1411.7224.7224 0 0 0 .0779.0525.7068.7068 0 0 0 .6249 0 .7222.7222 0 0 0 .0779-.0525.7358.7358 0 0 0 .1714-.1411c.01-.0112.0245-.0157.0334-.0275l1.728-2.2549a.75.75 0 1 0 -1.19-.9121z"
  }));
};

IconArrowRandom.displayName = "IconArrowRandom";
IconArrowRandom.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowRandom"
};
var ArrowRandom = withIcon(IconArrowRandom);

const IconArrowRefresh = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m1.1851 5.54a.7531.7531 0 0 0 .6921.4634h2.9321a.75.75 0 0 0 0-1.5h-1.1212l.4073-.4072a5.5215 5.5215 0 0 1 9.4258 3.8462.75.75 0 0 0 .75.7422h.0078a.75.75 0 0 0 .7422-.7578 7.0216 7.0216 0 0 0 -11.9863-4.8916l-.4077.4076v-1.1215a.75.75 0 0 0 -1.5 0v2.9317l.001.0048a.75.75 0 0 0 .0569.2822z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.4089 10.055a.75.75 0 0 0 -.2866-.0579h-2.9316a.75.75 0 0 0 0 1.5h1.1211l-.4072.4072a5.5216 5.5216 0 0 1 -9.4259-3.8452.7408.7408 0 0 0 -.7578-.7422.75.75 0 0 0 -.7422.7578 7.0241 7.0241 0 0 0 11.9863 4.89l.4072-.4072v1.1211a.75.75 0 0 0 1.5 0v-2.9315a.753.753 0 0 0 -.4634-.6921z"
  }));
};

IconArrowRefresh.displayName = "IconArrowRefresh";
IconArrowRefresh.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowRefresh"
};
var ArrowRefresh = withIcon(IconArrowRefresh);

const IconArrowRight = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.9177 7.7134a.75.75 0 0 0 -.1618-.2436l-6.04-6.04a.75.75 0 1 0 -1.0601 1.0602l4.7593 4.76h-12.6397a.75.75 0 1 0 0 1.5h12.64l-4.7596 4.76a.75.75 0 1 0 1.0605 1.06l6.04-6.04a.7527.7527 0 0 0 .1618-.8169z"
  }));
};

IconArrowRight.displayName = "IconArrowRight";
IconArrowRight.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowRight"
};
var ArrowRight = withIcon(IconArrowRight);

const IconArrowSkip = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m7.7925 7.7344a2.2473 2.2473 0 1 0 2.2475 2.2475 2.25 2.25 0 0 0 -2.2475-2.2475zm0 2.9946a.7473.7473 0 1 1 .7475-.7471.7481.7481 0 0 1 -.7475.7471z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.6963 8.4951a.75.75 0 0 0 -1.0605 0l-.6894.6894a6.1941 6.1941 0 0 0 -11.9356-1.4177 2.2477 2.2477 0 1 0 1.4834.3015 4.7067 4.7067 0 0 1 8.9123.9623l-.5355-.5355a.75.75 0 0 0 -1.0605 1.0605l1.9126 1.9126a.75.75 0 0 0 1.0605 0l1.9126-1.9126a.75.75 0 0 0 .0001-1.0605z"
  }));
};

IconArrowSkip.displayName = "IconArrowSkip";
IconArrowSkip.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowSkip"
};
var ArrowSkip = withIcon(IconArrowSkip);

const IconArrowSpread = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.6584 7.4678-1.9062-1.9073a.75.75 0 1 0 -1.0605 1.0606l.6345.6348h-6.0568a10.26 10.26 0 0 0 2.3232-3.211l.2152.7422a.7514.7514 0 0 0 .7207.541.74.74 0 0 0 .209-.03.75.75 0 0 0 .5117-.9292l-.751-2.5879a.783.783 0 0 0 -.1367-.2622.7506.7506 0 0 0 -.7959-.251l-2.5869.7544a.75.75 0 0 0 .42 1.44l.9394-.2742c-1.4122 2.9261-2.9222 4.06-5.3281 4.06h-2.1377a.75.75 0 0 0 -.75.75l.0004.002v.0022a.75.75 0 0 0 .75.75h.1038c.006 0 .0109.0034.0169.0034h2.0658c2.376.0159 3.877 1.1516 5.2789 4.0561l-.94-.2739a.75.75 0 0 0 -.42 1.44l2.5869.7544a.7238.7238 0 0 0 .208.03.7579.7579 0 0 0 .59-.2832.767.767 0 0 0 .1348-.26l.751-2.5879a.75.75 0 0 0 -1.4414-.418l-.2152.7423a10.2837 10.2837 0 0 0 -2.3109-3.2h6.0337l-.6234.6235a.75.75 0 1 0 1.0606 1.0611l1.9063-1.9067a.7567.7567 0 0 0 0-1.0654z"
  }));
};

IconArrowSpread.displayName = "IconArrowSpread";
IconArrowSpread.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowSpread"
};
var ArrowSpread = withIcon(IconArrowSpread);

const IconArrowUp = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.57 6.2844-6.04-6.04a.75.75 0 0 0 -1.0605 0l-6.04 6.04a.75.75 0 0 0 1.0605 1.0606l4.76-4.7597v12.64a.75.75 0 0 0 1.5 0v-12.6401l4.76 4.76a.75.75 0 1 0 1.06-1.0608z"
  }));
};

IconArrowUp.displayName = "IconArrowUp";
IconArrowUp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconArrowUp"
};
var ArrowUp = withIcon(IconArrowUp);

const IconBadge = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8.4 6.7383a.7.7 0 0 0 .7.7h3.7715a.7.7 0 0 0 0-1.4h-3.7709a.7.7 0 0 0 -.7006.7z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.8721 8.2729h-3.7715a.7.7 0 1 0 0 1.4h3.7715a.7.7 0 0 0 0-1.4z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.8721 10.5083h-3.7715a.7.7 0 1 0 0 1.4h3.7715a.7.7 0 0 0 0-1.4z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.6738 6.0381h-3.5361a.7.7 0 0 0 -.7.7v4.47a.7.7 0 0 0 .7.7h3.5361a.7.7 0 0 0 .7-.7v-4.47a.7.7 0 0 0 -.7-.7zm-.7 4.47h-2.1359v-3.07h2.1357z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.1943 2.8745h-5.4648v-.0361a1.4179 1.4179 0 0 0 -1.416-1.4155h-.6279a1.417 1.417 0 0 0 -1.415 1.4155v.0361h-5.4649a.7.7 0 0 0 -.7.7v10.3025a.7.7 0 0 0 .7.7h14.3886a.7.7 0 0 0 .7-.7v-10.3023a.7.7 0 0 0 -.7-.7002zm-7.5234 0h.6582v1.4h-.6582zm6.8232 10.3022h-12.9882v-8.9018h4.7646v.0361a1.417 1.417 0 0 0 1.415 1.4155h.6279a1.4179 1.4179 0 0 0 1.4166-1.4155v-.0361h4.7647v8.9019z"
  }));
};

IconBadge.displayName = "IconBadge";
IconBadge.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconBadge"
};
var Badge = withIcon(IconBadge);

const IconBalloon = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m4.5237 3.1748a2.4426 2.4426 0 0 0 -1.42 2.5727.7.7 0 0 0 .6983.6611l.04-.0015a.7.7 0 0 0 .66-.7378c-.05-.9038.4438-1.1528.5117-1.1831a.7.7 0 0 0 -.4888-1.3115z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.4441 14.4673c-1.5093-1.8726-4.6255-.7515-6.1226-.2119a1.9249 1.9249 0 0 1 -1.5532-.0264 1.6173 1.6173 0 0 1 -.54-.458h.4779a.7.7 0 0 0 .6748-.8867l-.4623-1.6717a5.7087 5.7087 0 1 0 -3.6393 0l-.4623 1.6716a.7.7 0 0 0 .6748.8867h1.05a3.2844 3.2844 0 0 0 1.5461 1.7944 3.1557 3.1557 0 0 0 1.44.3369 3.852 3.852 0 0 0 1.3022-.2358c1.08-.3887 3.6084-1.3 4.4468-.2578a.75.75 0 1 0 1.1679-.9414zm-13.5541-8.6612a4.2087 4.2087 0 1 1 4.2085 4.209 4.213 4.213 0 0 1 -4.2085-4.209zm4.2085 5.709c.15 0 .2974-.0111.4446-.0225l.2429.878h-1.3746l.2429-.878c.1471.0114.2942.0225.4441.0225z"
  }));
};

IconBalloon.displayName = "IconBalloon";
IconBalloon.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconBalloon"
};
var Balloon = withIcon(IconBalloon);

const IconBell = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.4976 12.4153-1.0464-3.13v-2.0053a5.455 5.455 0 0 0 -4.7012-5.3938v-1.1076a.75.75 0 1 0 -1.5 0v1.1076a5.455 5.455 0 0 0 -4.7007 5.3938v2.0049l-1.0469 3.13a.75.75 0 0 0 .7114.9878h3.2062a2.58 2.58 0 0 0 5.16 0h3.2059a.75.75 0 0 0 .7114-.9878zm-6.4976 2.0561a1.0811 1.0811 0 0 1 -1.0784-1.0684h2.1573a1.0815 1.0815 0 0 1 -1.0789 1.0684zm-4.7446-2.5683.7554-2.2583a.7556.7556 0 0 0 .0386-.2378v-2.127a3.9509 3.9509 0 1 1 7.9018 0v2.127a.7556.7556 0 0 0 .0386.2378l.7549 2.2583z"
  }));
};

IconBell.displayName = "IconBell";
IconBell.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconBell"
};
var Bell = withIcon(IconBell);

const IconBlocks = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M15.2 15.95H.8a.75.75 0 0 1-.75-.75v-4.8a.75.75 0 0 1 .75-.75h4.05V5.6a.75.75 0 0 1 .75-.75h4.05V.8a.75.75 0 0 1 .75-.75h4.8a.75.75 0 0 1 .75.75v14.4a.75.75 0 0 1-.75.75zm-4.05-1.5h3.3v-3.3h-3.3zm-4.8 0h3.3v-3.3h-3.3zm-4.8 0h3.3v-3.3h-3.3zm9.6-4.8h3.3v-3.3h-3.3zm-4.8 0h3.3v-3.3h-3.3zm4.8-4.8h3.3v-3.3h-3.3z"
  }));
};

IconBlocks.displayName = "IconBlocks";
IconBlocks.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconBlocks"
};
var Blocks = withIcon(IconBlocks);

const IconBooks = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M14.2 15.2H1.8c-.4 0-.8-.3-.8-.8s.3-.8.8-.8h12.4c.4 0 .8.3.8.8s-.4.8-.8.8zM3 12.8c-.4 0-.8-.3-.8-.8V1.5c0-.4.3-.8.8-.8s.8.3.8.8V12c0 .4-.3.8-.8.8zM6.9 12.8c-.4 0-.8-.3-.8-.8V1.5c0-.4.3-.8.8-.8s.8.3.8.8V12c-.1.4-.4.8-.8.8zM13 12.8c-.3 0-.7-.2-.7-.6L9.9 1.9c-.1-.4.1-.8.5-.9.4-.1.8.2.9.6l2.4 10.3c.1.4-.2.8-.6.9H13z"
  }));
};

IconBooks.displayName = "IconBooks";
IconBooks.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconBooks"
};
var Books = withIcon(IconBooks);

const IconBrush = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.45 1.7785a2.017 2.017 0 0 0 -2.8184.166l-7.2196 7.1743a3.03 3.03 0 0 0 -1.2645.3658c-.3232.1807-.6318.3867-.9395.5928-.2217.1484-.4434.2978-.6728.4346a2.78 2.78 0 0 1 -1.6768.5156.75.75 0 0 0 -.6631 1.2139 7.22 7.22 0 0 0 1.9756 1.7725 4.9041 4.9041 0 0 0 2.4795.7109c.0693 0 .1387-.001.208-.0049a3.7971 3.7971 0 0 0 2.6367-1.21 3.0057 3.0057 0 0 0 .7539-1.6658l7.0664-7.29a1.949 1.949 0 0 0 .1346-2.7757zm-9.07 10.7276a2.3227 2.3227 0 0 1 -1.5947.7158 3.4039 3.4039 0 0 1 -1.8535-.501 4.7472 4.7472 0 0 1 -.6182-.4355 6.0144 6.0144 0 0 0 .9922-.4863c.251-.15.4941-.3125.7373-.4756.2744-.1836.5488-.3691.8379-.53a1.5834 1.5834 0 0 1 1.1924-.1279 1.0268 1.0268 0 0 1 .625.5127 1.3188 1.3188 0 0 1 -.3184 1.3278zm7.8672-9.0049-6.4341 6.64a2.4777 2.4777 0 0 0 -.6817-.616l6.5586-6.5182c.2139-.2139.5674-.2988.6992-.167.1528.1524.0522.4668-.1421.6612z"
  }));
};

IconBrush.displayName = "IconBrush";
IconBrush.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconBrush"
};
var Brush = withIcon(IconBrush);

const IconCalendar = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.6943 1.38h-1.5343v-.5958a.7.7 0 0 0 -1.4 0v.5958h-5.52v-.5958a.7.7 0 0 0 -1.4 0v.5958h-1.5343a1.7021 1.7021 0 0 0 -1.7 1.7v11.1358a1.7021 1.7021 0 0 0 1.7 1.7h11.3886a1.7021 1.7021 0 0 0 1.7-1.7v-11.1358a1.7021 1.7021 0 0 0 -1.7-1.7zm-11.3886 1.4h1.5343v.5967a.7.7 0 0 0 1.4 0v-.5967h5.52v.5967a.7.7 0 0 0 1.4 0v-.5967h1.5342a.303.303 0 0 1 .3.3v1.8721h-11.9883v-1.8721a.3031.3031 0 0 1 .2998-.3zm11.3886 11.7356h-11.3886a.303.303 0 0 1 -.3-.3v-7.8631h11.9884v7.8633a.303.303 0 0 1 -.2998.2998z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8.3125 7.291a.7011.7011 0 0 0 -.75.1113l-1.36 1.19a.7.7 0 1 0 .9215 1.0532l.1992-.1738v3.63a.7.7 0 1 0 1.4 0v-5.173a.701.701 0 0 0 -.4107-.6377z"
  }));
};

IconCalendar.displayName = "IconCalendar";
IconCalendar.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCalendar"
};
var Calendar = withIcon(IconCalendar);

const IconCamera = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.2065 3.4824h-3.7486l-.6005-2.084a.7008.7008 0 0 0 -.6728-.5068h-4.37a.7008.7008 0 0 0 -.6729.5068l-.6 2.084h-3.7482a.7.7 0 0 0 -.7.7v10.2258a.7.7 0 0 0 .7.7h14.413a.7.7 0 0 0 .7-.7v-10.2256a.7.7 0 0 0 -.7-.7002zm-8.8647-1.1904h3.3159l.3433 1.19h-4.002zm8.1645 11.416h-13.0126v-8.8252h13.0127z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 5.6846a3.61 3.61 0 1 0 3.61 3.61 3.6148 3.6148 0 0 0 -3.61-3.61zm0 5.82a2.21 2.21 0 1 1 2.21-2.21 2.2122 2.2122 0 0 1 -2.21 2.2103z"
  }));
};

IconCamera.displayName = "IconCamera";
IconCamera.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCamera"
};
var Camera = withIcon(IconCamera);

const IconCaretDown = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m1.7438 4.55c-.605 0-.75.35-.3222.7778l5.8006 5.8006a1.1032 1.1032 0 0 0 1.5556 0l5.8006-5.8006c.4278-.4278.2828-.7778-.3222-.7778z"
  }));
};

IconCaretDown.displayName = "IconCaretDown";
IconCaretDown.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCaretDown"
};
var CaretDown = withIcon(IconCaretDown);

const IconCaretLeft = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.45 1.7437c0-.605-.35-.75-.7778-.3222l-5.801 5.8007a1.1032 1.1032 0 0 0 0 1.5556l5.8006 5.8006c.4278.4278.7778.2828.7778-.3222z"
  }));
};

IconCaretLeft.displayName = "IconCaretLeft";
IconCaretLeft.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCaretLeft"
};
var CaretLeft = withIcon(IconCaretLeft);

const IconCaretRight = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m4.55 14.2562c0 .605.35.75.7778.3222l5.8006-5.8006a1.1032 1.1032 0 0 0 0-1.5556l-5.8002-5.8006c-.4282-.4278-.7782-.2828-.7782.3222z"
  }));
};

IconCaretRight.displayName = "IconCaretRight";
IconCaretRight.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCaretRight"
};
var CaretRight = withIcon(IconCaretRight);

const IconCaretUp = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.2563 11.45c.605 0 .75-.35.3222-.7778l-5.8007-5.801a1.1032 1.1032 0 0 0 -1.5556 0l-5.8006 5.8006c-.4278.4278-.2828.7778.3222.7778h12.5125z"
  }));
};

IconCaretUp.displayName = "IconCaretUp";
IconCaretUp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCaretUp"
};
var CaretUp = withIcon(IconCaretUp);

const IconCaretUpDown = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.8884 6.7694c.605 0 .75-.35.3222-.7778l-5.4328-5.4328a1.1033 1.1033 0 0 0 -1.5557 0l-5.4327 5.4328c-.4278.4278-.2828.7778.3222.7778z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m2.1116 9.2306c-.605 0-.75.35-.3222.7778l5.4328 5.4328a1.1033 1.1033 0 0 0 1.5557 0l5.4328-5.4328c.4278-.4278.2828-.7778-.3222-.7778z"
  }));
};

IconCaretUpDown.displayName = "IconCaretUpDown";
IconCaretUpDown.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCaretUpDown"
};
var CaretUpDown = withIcon(IconCaretUpDown);

const IconCart = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.746 3.2629a.75.75 0 0 0 -.5781-.272h-10.6861l-.1113-1.8886a.75.75 0 0 0 -.7485-.7061h-2.7901a.75.75 0 1 0 0 1.5h2.083l.4439 7.5259a.75.75 0 0 0 .7485.706h9.9827a.7506.7506 0 0 0 .7368-.6094l1.0776-5.6372a.75.75 0 0 0 -.1584-.6186zm-2.276 5.3653h-8.6556l-.2437-4.1372h9.69z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.5448 11.3289a2.1375 2.1375 0 1 0 2.1372 2.1372 2.14 2.14 0 0 0 -2.1372-2.1372zm0 2.8745a.7371.7371 0 1 1 .7368-.7373.7379.7379 0 0 1 -.7368.7373z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.1576 11.3289a2.1375 2.1375 0 1 0 2.1377 2.1372 2.14 2.14 0 0 0 -2.1377-2.1372zm0 2.8745a.7371.7371 0 1 1 .7373-.7373.7378.7378 0 0 1 -.7373.7373z"
  }));
};

IconCart.displayName = "IconCart";
IconCart.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCart"
};
var Cart = withIcon(IconCart);

const IconChartBarHorz = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.8389 8.9814h-1.6055v-2.663a.7.7 0 0 0 -.7-.7h-4.3654v-2.6624a.7.7 0 0 0 -.7-.7h-5.6067v-1.2746a.7.7 0 0 0 -1.4 0v14.0372a.7.7 0 1 0 1.4 0v-1.2745h12.9776a.7.7 0 0 0 .7-.7v-3.3625a.7.7 0 0 0 -.7-.7002zm-3.0059 0h-9.9707v-1.9629h9.9707zm-9.9707-5.3252h4.9053v1.962h-4.9053zm12.2764 8.6875h-12.2764v-1.9619h12.2764z"
  }));
};

IconChartBarHorz.displayName = "IconChartBarHorz";
IconChartBarHorz.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChartBarHorz"
};
var ChartBarHorz = withIcon(IconChartBarHorz);

const IconChartBarVert = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.0185 14.1387h-1.2744v-12.9776a.7.7 0 0 0 -.7-.7h-3.3625a.7.7 0 0 0 -.7.7v1.6055h-2.6632a.7.7 0 0 0 -.7.7v4.3654h-2.6624a.7.7 0 0 0 -.7.7v5.6065h-1.2746a.7.7 0 0 0 0 1.4h14.0372a.7.7 0 1 0 0-1.4zm-4.6367-12.2774h1.9619v12.2764h-1.9619zm-3.3633 2.3057h1.9629v9.9707h-1.9629zm-3.3623 5.0654h1.962v4.9053h-1.962z"
  }));
};

IconChartBarVert.displayName = "IconChartBarVert";
IconChartBarVert.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChartBarVert"
};
var ChartBarVert = withIcon(IconChartBarVert);

const IconChartDonut = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.8538 11.9136a7.84 7.84 0 0 0 0-7.8271c-.0059-.0115-.0054-.0244-.012-.0357-.0082-.0142-.0222-.022-.0312-.0353a7.9476 7.9476 0 0 0 -2.8259-2.826c-.0134-.009-.0212-.023-.0355-.0312-.0113-.0065-.0241-.0061-.0356-.012a7.8988 7.8988 0 1 0 2.9037 10.8273c.0065-.01.0184-.0138.0245-.0243s.0061-.0242.012-.0357zm-6.1538-7.9287v-2.4456a6.44 6.44 0 0 1 1.9222.5181l-1.2233 2.1192a4.0331 4.0331 0 0 0 -.6989-.1917zm-.7 1.3295a2.6855 2.6855 0 1 1 -2.6856 2.6856 2.689 2.689 0 0 1 2.6856-2.6856zm3.8234 1.2866 2.1193-1.2235a6.4482 6.4482 0 0 1 0 5.2448l-2.1194-1.2231a3.8586 3.8586 0 0 0 0-2.7982zm1.4148-2.4334-2.12 1.2238a4.09 4.09 0 0 0 -.51-.51l1.2238-2.12a6.546 6.546 0 0 1 1.4062 1.4063zm-5.2382 10.3324a6.4992 6.4992 0 0 1 -.7-12.9607v2.4456a4.0789 4.0789 0 1 0 3.8184 6.6238l2.12 1.2236a6.4887 6.4887 0 0 1 -5.2384 2.6677z"
  }));
};

IconChartDonut.displayName = "IconChartDonut";
IconChartDonut.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChartDonut"
};
var ChartDonut = withIcon(IconChartDonut);

const IconChartDonutFilled = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .05a7.95 7.95 0 1 0 7.95 7.95 7.95 7.95 0 0 0 -7.95-7.95zm0 4.8891a3.0609 3.0609 0 1 1 -3.0609 3.0609 3.0609 3.0609 0 0 1 3.0609-3.0609zm0 9.3053a6.2444 6.2444 0 0 1 -6.2444-6.2444v-.0027a.5185.5185 0 1 1 1.0369 0l.0025.0027a5.205 5.205 0 1 0 5.205-5.2049v-1.0394a6.2444 6.2444 0 1 1 0 12.4887z"
  }));
};

IconChartDonutFilled.displayName = "IconChartDonutFilled";
IconChartDonutFilled.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChartDonutFilled"
};
var ChartDonutFilled = withIcon(IconChartDonutFilled);

const IconChartLine = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.7813.8164a2.1616 2.1616 0 0 0 -2.1582 2.1592 2.1377 2.1377 0 0 0 .46 1.3164l-2.5626 4.1357-2.3916-3.0312a.71.71 0 0 0 -.6358-.2832.7482.7482 0 0 0 -.5957.36l-3.2774 5.4325a2.1577 2.1577 0 1 0 1.757 2.1186 2.1381 2.1381 0 0 0 -.4762-1.3359l2.7213-4.5118 2.3838 3.0205a.75.75 0 0 0 1.2266-.0693l3.1209-5.0364a2.159 2.159 0 1 0 .4279-4.2751zm-11.5625 12.9668a.7588.7588 0 1 1 .7578-.7588.7589.7589 0 0 1 -.7578.7588zm11.5625-10.0488a.7588.7588 0 1 1 .7588-.7588.7587.7587 0 0 1 -.7588.7588z"
  }));
};

IconChartLine.displayName = "IconChartLine";
IconChartLine.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChartLine"
};
var ChartLine = withIcon(IconChartLine);

const IconChartPie = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.8538 11.9135a7.84 7.84 0 0 0 0-7.827c-.0059-.0115-.0054-.0244-.012-.0357-.0082-.0142-.0223-.0221-.0313-.0355a7.9482 7.9482 0 0 0 -2.8258-2.8258c-.0134-.009-.0212-.023-.0355-.0312-.0113-.0065-.0241-.0061-.0356-.012a7.8988 7.8988 0 1 0 2.9037 10.8273c.0065-.01.0184-.0138.0245-.0243s.0062-.0243.012-.0358zm-6.1538-10.3742a6.44 6.44 0 0 1 1.9222.5181l-1.9222 3.3296zm5.8 6.4607a6.4553 6.4553 0 0 1 -.5573 2.6224l-4.5427-2.6224 4.5426-2.6224a6.4553 6.4553 0 0 1 .5574 2.6224zm-1.2618-3.8323-3.3261 1.92 1.92-3.3261a6.5459 6.5459 0 0 1 1.4061 1.4061zm-5.2382 10.3323a6.4992 6.4992 0 0 1 -.7-12.9607v6.4607c0 .0147.0075.0269.0084.0414a.6676.6676 0 0 0 .0123.0909.7319.7319 0 0 0 .06.178c.0064.0127.006.0269.0132.0393.0061.01.018.0143.0245.0244a.6711.6711 0 0 0 .0585.0792.681.681 0 0 0 .11.0971.6594.6594 0 0 0 .0637.0562l5.5878 3.2258a6.4887 6.4887 0 0 1 -5.2384 2.6677z"
  }));
};

IconChartPie.displayName = "IconChartPie";
IconChartPie.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChartPie"
};
var ChartPie = withIcon(IconChartPie);

const IconChartSegment = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.12 9.3642a.7517.7517 0 0 0 -.9072.55 5.8884 5.8884 0 1 1 -7.3105-7.0793.75.75 0 0 0 -.4043-1.4443 7.3886 7.3886 0 1 0 9.172 8.8809.75.75 0 0 0 -.55-.9073z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8.5049.1074a.75.75 0 0 0 -.75.75v6.6377a.75.75 0 0 0 .75.75h6.6377a.75.75 0 0 0 .75-.75 7.3962 7.3962 0 0 0 -7.3877-7.3877zm.75 6.6377v-5.09a5.9 5.9 0 0 1 5.09 5.09z"
  }));
};

IconChartSegment.displayName = "IconChartSegment";
IconChartSegment.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChartSegment"
};
var ChartSegment = withIcon(IconChartSegment);

const IconChat = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.6372 4.3755h-2.958v-1.8008a1.2139 1.2139 0 0 0 -1.2041-1.2207h-9.1094a1.216 1.216 0 0 0 -1.208 1.2207v6.0908a1.2181 1.2181 0 0 0 1.208 1.2245h.916l.002 1.0381a.7.7 0 0 0 1.2256.46l.8125-.924v1.2249a1.2139 1.2139 0 0 0 1.2041 1.221h5.6494l1.3184 1.498a.7.7 0 0 0 1.2256-.4619v-1.0361h.918a1.2148 1.2148 0 0 0 1.2051-1.2207v-6.0931a1.2147 1.2147 0 0 0 -1.2052-1.2207zm-10.6523 4.3525-.3125.3555a.7011.7011 0 0 0 -.6924-.5935h-1.4219v-5.7356h8.7207v1.6211h-4.7529a1.2139 1.2139 0 0 0 -1.2041 1.2207v2.9343a.6865.6865 0 0 0 -.3369.1975zm10.457 2.7812h-1.4219a.7.7 0 0 0 -.6914.5918l-.3115-.3535a.6991.6991 0 0 0 -.5254-.2383h-5.77v-5.7333h8.72z"
  }));
};

IconChat.displayName = "IconChat";
IconChat.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChat"
};
var Chat = withIcon(IconChat);

const IconCheck = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m6.4372 13.125a.7475.7475 0 0 1 -.53-.22l-5.6247-5.6242a.75.75 0 0 1 1.0605-1.0608l5.0942 5.0942 8.22-8.22a.75.75 0 0 1 1.0605 1.0605l-8.75 8.75a.7476.7476 0 0 1 -.5305.2203z"
  }));
};

IconCheck.displayName = "IconCheck";
IconCheck.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCheck"
};
var Check = withIcon(IconCheck);

const IconCheckBox = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.6646 2.33a.75.75 0 0 0 -1.0605 0l-7.2341 7.234-3.2191-3.2193a.75.75 0 1 0 -1.0609 1.0606l3.75 3.75a.75.75 0 0 0 1.0605 0l7.7642-7.7642a.75.75 0 0 0 -.0001-1.0611z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.8457 7.12a.75.75 0 0 0 -.75.75v6.5835h-11.48v-12.9071h11.5288a.75.75 0 0 0 0-1.5h-12.2788a.75.75 0 0 0 -.75.75v14.4072a.75.75 0 0 0 .75.75h12.98a.75.75 0 0 0 .75-.75v-7.3336a.75.75 0 0 0 -.75-.75z"
  }));
};

IconCheckBox.displayName = "IconCheckBox";
IconCheckBox.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCheckBox"
};
var CheckBox = withIcon(IconCheckBox);

const IconCheckCircle = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.8877 7.3a.75.75 0 0 0 -1.4941.1328c.0166.1865.0244.376.0244.5669a6.4161 6.4161 0 1 1 -2.83-5.3228.75.75 0 0 0 .84-1.2432 7.9194 7.9194 0 1 0 3.49 6.5663q0-.3538-.0303-.7z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.2451 6.4853a.75.75 0 0 0 -1.0605 1.0606l3.2854 3.2841a.75.75 0 0 0 1.0605 0l6.8005-6.8007a.75.75 0 0 0 -1.061-1.0606l-6.27 6.2706z"
  }));
};

IconCheckCircle.displayName = "IconCheckCircle";
IconCheckCircle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCheckCircle"
};
var CheckCircle = withIcon(IconCheckCircle);

const IconChevronDown = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 11.77a.7494.7494 0 0 1 -.53-.22l-6.04-6.04a.75.75 0 1 1 1.0607-1.06l5.5093 5.5095 5.5088-5.51a.75.75 0 0 1 1.0612 1.0605l-6.0392 6.04a.75.75 0 0 1 -.5308.22z"
  }));
};

IconChevronDown.displayName = "IconChevronDown";
IconChevronDown.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChevronDown"
};
var ChevronDown = withIcon(IconChevronDown);

const IconChevronLeft = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.02 14.79a.7467.7467 0 0 1 -.53-.22l-6.04-6.0395a.75.75 0 0 1 0-1.0605l6.0391-6.04a.75.75 0 1 1 1.0609 1.06l-5.509 5.51 5.509 5.51a.75.75 0 0 1 -.53 1.28z"
  }));
};

IconChevronLeft.displayName = "IconChevronLeft";
IconChevronLeft.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChevronLeft"
};
var ChevronLeft = withIcon(IconChevronLeft);

const IconChevronRight = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m4.98 14.79a.75.75 0 0 1 -.53-1.28l5.5095-5.51-5.5095-5.51a.75.75 0 0 1 1.06-1.06l6.04 6.04a.75.75 0 0 1 0 1.0605l-6.04 6.0395a.7467.7467 0 0 1 -.53.22z"
  }));
};

IconChevronRight.displayName = "IconChevronRight";
IconChevronRight.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChevronRight"
};
var ChevronRight = withIcon(IconChevronRight);

const IconChevronUp = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.04 11.77a.7467.7467 0 0 1 -.53-.22l-5.51-5.5095-5.51 5.51a.75.75 0 1 1 -1.06-1.0605l6.04-6.04a.75.75 0 0 1 .53-.22.7494.7494 0 0 1 .53.22l6.04 6.04a.75.75 0 0 1 -.53 1.28z"
  }));
};

IconChevronUp.displayName = "IconChevronUp";
IconChevronUp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconChevronUp"
};
var ChevronUp = withIcon(IconChevronUp);

const IconCircle = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 15.8574a7.8574 7.8574 0 1 1 7.8572-7.8574 7.8661 7.8661 0 0 1 -7.8572 7.8574zm0-14.2148a6.3574 6.3574 0 1 0 6.3572 6.3574 6.3645 6.3645 0 0 0 -6.3572-6.3574z"
  }));
};

IconCircle.displayName = "IconCircle";
IconCircle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCircle"
};
var Circle = withIcon(IconCircle);

const IconClock = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .0874a7.9126 7.9126 0 1 0 7.9128 7.9126 7.9217 7.9217 0 0 0 -7.9128-7.9126zm0 14.3252a6.4126 6.4126 0 1 1 6.4128-6.4126 6.42 6.42 0 0 1 -6.4128 6.4126z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.6746 9.896-3.9246-2.3243v-3.9917a.75.75 0 1 0 -1.5 0v4.42a.6964.6964 0 0 0 .0175.0865.7258.7258 0 0 0 .0308.1525.94.94 0 0 0 .14.2449.7266.7266 0 0 0 .1112.1.7083.7083 0 0 0 .068.0613l4.2925 2.5418a.75.75 0 0 0 .7646-1.291z"
  }));
};

IconClock.displayName = "IconClock";
IconClock.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconClock"
};
var Clock = withIcon(IconClock);

const IconClone = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1826 5.79h-3.4883v-3.663a.75.75 0 0 0 -.75-.75h-10.1269a.75.75 0 0 0 -.75.75v7.333a.75.75 0 0 0 .75.75h3.4883v3.663a.75.75 0 0 0 .75.75h10.1269a.75.75 0 0 0 .75-.75v-7.333a.75.75 0 0 0 -.75-.75zm-13.6152 2.92v-5.833h8.627v2.913h-5.1387a.75.75 0 0 0 -.75.75v2.17zm12.8652 4.413h-8.6269v-5.833h8.6269z"
  }));
};

IconClone.displayName = "IconClone";
IconClone.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconClone"
};
var Clone = withIcon(IconClone);

const IconCloud = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.6689 8.2637a5.3086 5.3086 0 1 0 -10.3534-2.305 4.2991 4.2991 0 0 0 1.0678 8.4656h8.3877a3.1445 3.1445 0 0 0 .8979-6.1606zm-.8979 4.6606h-8.3877a2.8079 2.8079 0 1 1 .7944-5.502.75.75 0 0 0 .4229-1.4394 4.2852 4.2852 0 0 0 -.7358-.14 3.8234 3.8234 0 1 1 6.1166 4.001.75.75 0 1 0 .959 1.1533 5.3486 5.3486 0 0 0 .7949-.8193 5.3688 5.3688 0 0 0 .3472-.5286 1.6523 1.6523 0 0 1 -.3116 3.2752z"
  }));
};

IconCloud.displayName = "IconCloud";
IconCloud.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCloud"
};
var Cloud = withIcon(IconCloud);

const IconCloudArrow = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.1821 6.9551a4.7865 4.7865 0 0 0 .1328-1.1172 4.9125 4.9125 0 0 0 -4.9072-4.9072 4.8593 4.8593 0 0 0 -4.291 2.5449 2.898 2.898 0 0 0 -.4009-.0283 2.9707 2.9707 0 0 0 -2.9673 2.9677 2.8726 2.8726 0 0 0 .0532.5488 3.47 3.47 0 0 0 -1.7685 3.0313 3.506 3.506 0 0 0 3.5019 3.502h2.0137a.75.75 0 0 0 0-1.5h-2.0136a1.9941 1.9941 0 0 1 -.5869-3.9023.75.75 0 0 0 .4531-1.0448 1.4437 1.4437 0 0 1 -.1528-.6348 1.4767 1.4767 0 0 1 2.0088-1.36.7509.7509 0 0 0 .977-.4287 3.3992 3.3992 0 1 1 6.3325 2.4717.75.75 0 0 0 .478.9951 1.9837 1.9837 0 0 1 1.4219 1.9023 2.0044 2.0044 0 0 1 -2.002 2.002h-2.0141a.75.75 0 1 0 0 1.5h2.0142a3.506 3.506 0 0 0 3.502-3.502 3.4662 3.4662 0 0 0 -1.7848-3.0405z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m9.92 10.0391a.75.75 0 1 0 1.061-1.0606l-2.451-2.4502a.7527.7527 0 0 0 -1.0605 0l-2.4512 2.45a.75.75 0 1 0 1.0605 1.0605l1.1712-1.17v5.4506a.75.75 0 1 0 1.5 0v-5.4504z"
  }));
};

IconCloudArrow.displayName = "IconCloudArrow";
IconCloudArrow.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCloudArrow"
};
var CloudArrow = withIcon(IconCloudArrow);

const IconComment = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m10.3594.6035h-4.7188a5.6035 5.6035 0 0 0 0 11.207h1.6094v2.8359a.7512.7512 0 0 0 .4385.6826.7588.7588 0 0 0 .3115.0675.7494.7494 0 0 0 .4922-.1836l5.7978-5.0381c.0137-.0117.0264-.0244.0391-.0371a5.5412 5.5412 0 0 0 1.6338-3.9307 5.61 5.61 0 0 0 -5.6035-5.6035zm2.9248 8.457-4.5342 3.9405v-1.94a.75.75 0 0 0 -.75-.75h-2.3594a4.1035 4.1035 0 0 1 0-8.207h4.7187a4.1082 4.1082 0 0 1 4.1036 4.103 4.0463 4.0463 0 0 1 -1.1787 2.8535z"
  }));
};

IconComment.displayName = "IconComment";
IconComment.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconComment"
};
var Comment = withIcon(IconComment);

const IconCommentCheck = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m5.7926 4.3838a.7414.7414 0 0 0 -1.0485 1.0485l3.1759 3.1771a.7412.7412 0 0 0 1.0484 0l6.4516-6.4509a.7414.7414 0 1 0 -1.0488-1.0485l-5.9266 5.9268z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.8638 6.2316a5.6034 5.6034 0 0 0 -.0608-.8206.7419.7419 0 0 0 -1.4674.22 3.9891 3.9891 0 0 1 -1.1264 3.4241l-4.4673 3.8832v-1.9144a.7416.7416 0 0 0 -.7419-.7414h-2.33a4.0514 4.0514 0 1 1 0-8.1027h5.24a.7414.7414 0 0 0 0-1.4829h-5.2391a5.5342 5.5342 0 1 0 0 11.0685h1.5881v2.8a.7575.7575 0 0 0 .4335.6748.7872.7872 0 0 0 .7945-.1149l5.7249-4.9757-.006-.007c.0139-.0119.0315-.0164.0447-.03a5.4657 5.4657 0 0 0 1.6132-3.881z"
  }));
};

IconCommentCheck.displayName = "IconCommentCheck";
IconCommentCheck.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCommentCheck"
};
var CommentCheck = withIcon(IconCommentCheck);

const IconCommentFilled = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m10.2436.93h-4.4873a5.3652 5.3652 0 0 0 0 10.73h1.4937v2.66a.75.75 0 0 0 1.2417.5659l5.5137-4.791c.0137-.0117.0273-.0239.04-.0366a5.3073 5.3073 0 0 0 1.5635-3.7637 5.3712 5.3712 0 0 0 -5.3653-5.3646z"
  }));
};

IconCommentFilled.displayName = "IconCommentFilled";
IconCommentFilled.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCommentFilled"
};
var CommentFilled = withIcon(IconCommentFilled);

const IconCopyright = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .03a7.97 7.97 0 1 0 7.97 7.97 7.9787 7.9787 0 0 0 -7.97-7.97zm0 14.44a6.47 6.47 0 1 1 6.47-6.47 6.4773 6.4773 0 0 1 -6.47 6.47z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m9.8665 6.1333a.75.75 0 1 0 1.0605-1.0605 4.14 4.14 0 1 0 0 5.8535.75.75 0 1 0 -1.0605-1.0606 2.7 2.7 0 0 1 -3.7329 0 2.6394 2.6394 0 1 1 3.7329-3.7324z"
  }));
};

IconCopyright.displayName = "IconCopyright";
IconCopyright.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconCopyright"
};
var Copyright = withIcon(IconCopyright);

const IconDanger = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.6357 2.3672a7.9653 7.9653 0 1 0 0 11.2705 7.9164 7.9164 0 0 0 0-11.2705zm-1.0605 1.0605a6.4766 6.4766 0 0 1 .497 8.5858l-9.0839-9.0828a6.462 6.462 0 0 1 8.5869.4971zm-9.15 9.1494a6.4766 6.4766 0 0 1 -.4969-8.5858l9.0834 9.0825a6.4791 6.4791 0 0 1 -8.5869-.4967z"
  }));
};

IconDanger.displayName = "IconDanger";
IconDanger.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDanger"
};
var Danger = withIcon(IconDanger);

const IconDashboard = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 11.8784a2 2 0 0 0 1.3146-3.5136l.7772-2.0655a.7.7 0 0 0 -1.3105-.4932l-.774 2.0569-.0073-.0007a2.0081 2.0081 0 0 0 0 4.0161zm0-2.6157a.6077.6077 0 1 1 -.6074.6079.6082.6082 0 0 1 .6074-.6079z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.8877 9.8706a7.8787 7.8787 0 0 0 -.1211-1.33c-.0016-.0136.0034-.0263.001-.04-.002-.0109-.0092-.019-.0117-.03a7.8824 7.8824 0 0 0 -2.6607-4.6116c-.01-.0094-.0143-.0222-.0249-.0311-.0071-.006-.0162-.0071-.0235-.0128a7.8646 7.8646 0 0 0 -10.0936 0c-.0073.0057-.0164.0068-.0235.0128-.0106.0089-.015.0217-.0249.0311a7.8825 7.8825 0 0 0 -2.6607 4.611c-.0024.0109-.01.019-.0117.03-.0024.0137.0026.0263.001.04a7.7236 7.7236 0 0 0 .7988 5.0184.6854.6854 0 0 0 .033.0707l.009.0194c.0032.0058.01.0077.0131.0134a.6869.6869 0 0 0 .2322.2385c.0112.007.02.0151.0318.0214a.6812.6812 0 0 0 .3323.0954h12.625a.6888.6888 0 0 0 .5806-.3318.6526.6526 0 0 0 .0464-.0554 7.9037 7.9037 0 0 0 .9521-3.7594zm-12.5644-4.4847.6718.8011a.7.7 0 1 0 1.0723-.8994l-.6754-.8054a6.4462 6.4462 0 0 1 2.908-1.06v1.0524a.7.7 0 1 0 1.4 0v-1.0524a6.4462 6.4462 0 0 1 2.9078 1.06l-.6754.8054a.7.7 0 1 0 1.0723.8994l.6718-.8011a6.4832 6.4832 0 0 1 1.549 2.6757l-1.0342.1826a.7.7 0 1 0 .2441 1.3789l1.03-.1818c.0094.1426.0218.2844.0218.4294a6.4994 6.4994 0 0 1 -.6221 2.7466h-11.7303a6.497 6.497 0 0 1 -.6221-2.7466c0-.145.0124-.2868.0218-.4294l1.03.1818a.7.7 0 0 0 .2441-1.3789l-1.0342-.1826a6.4832 6.4832 0 0 1 1.5489-2.6757z"
  }));
};

IconDashboard.displayName = "IconDashboard";
IconDashboard.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDashboard"
};
var Dashboard = withIcon(IconDashboard);

const IconDesktop = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.1773 1.1894h-12.3545a1.7021 1.7021 0 0 0 -1.7 1.7v7.4824a1.7021 1.7021 0 0 0 1.7 1.7h3.9658l-.4847 1.3382h-1.2199a.7.7 0 1 0 0 1.4h7.832a.7.7 0 0 0 0-1.4h-1.22l-.4847-1.3379h3.9658a1.7021 1.7021 0 0 0 1.7-1.7v-7.4821a1.7021 1.7021 0 0 0 -1.6998-1.7006zm-4.9703 12.2206h-2.414l.4847-1.3379h1.4447zm5.27-3.0381a.3.3 0 0 1 -.3.3h-12.3542a.3.3 0 0 1 -.3-.3v-7.4819a.3.3 0 0 1 .3-.3h12.3544a.3.3 0 0 1 .3.3z"
  }));
};

IconDesktop.displayName = "IconDesktop";
IconDesktop.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDesktop"
};
var Desktop = withIcon(IconDesktop);

const IconDivisions = () => {
  return /*#__PURE__*/React.createElement("g", {
    transform: "translate(1 2)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12.2914726 11.4613495c-.4160778 0-.7542205-.3430414-.7542205-.7644277 0-.4217088.3381427-.76475016.7542205-.76475016.4160778 0 .7542205.34304136.7542205.76475016 0 .4213863-.3381427.7644277-.7542205.7644277m-4.53709301-.7644277c0 .4213863-.33814274.7644277-.75422054.7644277-.4157597 0-.75422053-.3430414-.75422053-.7644277 0-.4217088.33846083-.76475016.75422053-.76475016.4160778 0 .75422054.34304136.75422054.76475016m-5.29099544 0c0 .4213863-.33877894.7644277-.75485674.7644277-.41575969 0-.75422053-.3430414-.75422053-.7644277 0-.4217088.33846084-.76475016.75422053-.76475016.4160778 0 .75485674.34304136.75485674.76475016M12.7524028 9.03716896V6.33088201c0-.26727565-.2137647-.48361096-.4771534-.48361096H7.44772898V4.06726031c.77489718-.21536809 1.346527-.93304675 1.346527-1.78549168 0-1.02203116-.82038581-1.8531972-1.82877008-1.8531972-.84487969 0-1.55583831.58452445-1.76451342 1.37474476H1.69548522c-.26179819 0-.47492672.21407845-.47715344.47941967l-.02926541 4.37055346v2.40257926C.50164732 9.28026407 0 9.92669072 0 10.6969218c0 .954648.76662652 1.7316496 1.70852741 1.7316496.94221899 0 1.70916362-.7770016 1.70916362-1.7316496 0-.801827-.54331871-1.47178938-1.27431778-1.66813543V6.65748062l.02544818-3.88694251H5.2035173c.17113904.63256314.66451569 1.12971521 1.2899048 1.29962386v4.98151532c-.693463.22020419-1.20179046.87017732-1.20179046 1.64524451 0 .954648.76630842 1.7316496 1.70852741 1.7316496.94221899 0 1.70852741-.7770016 1.70852741-1.7316496 0-.79699089-.53663856-1.46308439-1.26095748-1.66394414V6.81449298h4.35036692v2.23299301c-.7004612.2160129-1.2151507.86953251-1.2151507 1.64943581 0 .954648.7663084 1.7316496 1.7085274 1.7316496.942219 0 1.7085274-.7770016 1.7085274-1.7316496 0-.79215478-.5302765-1.45437939-1.2475972-1.65975284"
  }));
};

IconDivisions.displayName = "IconDivisions";
IconDivisions.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDivisions"
};
var Divisions = withIcon(IconDivisions);

const IconDocument = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.4845.0425h-9.4482a.7608.7608 0 0 0 -.53.22l-3.5247 3.5236a.7433.7433 0 0 0 -.2158.55v10.871a.75.75 0 0 0 .75.75h12.9687a.75.75 0 0 0 .75-.75v-14.4146a.75.75 0 0 0 -.75-.75zm-9.668 2.03v2.02h-2.02zm8.918 12.3849h-11.4687v-8.8646h3.3008a.75.75 0 0 0 .75-.75v-3.3h7.418z"
  }));
};

IconDocument.displayName = "IconDocument";
IconDocument.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocument"
};
var Document = withIcon(IconDocument);

const IconDocumentArrow = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m7.2545 10.671-2.0971-2.0968a.75.75 0 0 0 -1.0606 1.0606l3.3774 3.377a.7527.7527 0 0 0 1.0605 0l3.377-3.377a.75.75 0 0 0 -1.0605-1.0605l-2.0967 2.0966v-4.6582a.75.75 0 0 0 -1.5 0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.4845.042h-9.4482a.7608.7608 0 0 0 -.53.22l-3.5247 3.5241a.7433.7433 0 0 0 -.2158.55v10.8719a.75.75 0 0 0 .75.75h12.9687a.75.75 0 0 0 .75-.75v-14.416a.75.75 0 0 0 -.75-.75zm-9.668 2.03v2.0208h-2.0205zm8.918 12.3857h-11.4687v-8.8649h3.3008a.75.75 0 0 0 .75-.75v-3.3008h7.418z"
  }));
};

IconDocumentArrow.displayName = "IconDocumentArrow";
IconDocumentArrow.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentArrow"
};
var DocumentArrow = withIcon(IconDocumentArrow);

const IconDocumentClone = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.5185 3.2832h-2.356v-2.3555a.75.75 0 0 0 -.75-.75h-6.2836a.7608.7608 0 0 0 -.53.22l-3.6477 3.6472a.7608.7608 0 0 0 -.22.53v7.3916a.75.75 0 0 0 .75.75h2.3562v2.3555a.75.75 0 0 0 .75.75h9.9312a.75.75 0 0 0 .75-.75v-11.0388a.75.75 0 0 0 -.7501-.75zm-9.9546-.73v1.4568h-1.4565zm-2.3325 2.9568h3.0826a.75.75 0 0 0 .75-.75v-3.0823h4.5986v9.5391h-8.4312zm11.5371 8.8125h-8.4311v-1.6057h6.0752a.75.75 0 0 0 .75-.75v-7.1836h1.606z"
  }));
};

IconDocumentClone.displayName = "IconDocumentClone";
IconDocumentClone.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentClone"
};
var DocumentClone = withIcon(IconDocumentClone);

const IconDocumentDocx = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M14.4.2H5.1c-.2 0-.4.1-.5.2L1.1 3.9c-.1.1-.2.3-.2.5V15c0 .4.3.7.7.7h12.7c.4 0 .7-.3.7-.7V.9c0-.3-.2-.7-.6-.7zm-9.5 2v2h-2l2-2zm8.7 12.1H2.4V5.6h3.2c.4 0 .7-.3.7-.7V1.7h7.3v12.6z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.4 9.9h.9c.7 0 1.2.4 1.2 1.2s-.5 1.2-1.2 1.2h-.9V9.9zm.6 1.9h.3c.4 0 .6-.2.6-.7s-.3-.7-.6-.7H4v1.4zM8.1 11.1c0 .8-.5 1.3-1.2 1.3s-1.2-.5-1.2-1.3.5-1.3 1.2-1.3 1.2.5 1.2 1.3zm-1.7 0c0 .5.2.8.5.8s.5-.3.5-.8-.1-.8-.4-.8c-.4 0-.6.3-.6.8zM9.4 9.8c.5 0 .8.2 1 .7l-.5.2c0-.2-.2-.4-.4-.4-.3 0-.5.3-.5.8s.2.8.5.8c.2 0 .4-.2.4-.4l.6.2c-.1.4-.5.7-1 .7-.7 0-1.2-.5-1.2-1.3s.4-1.3 1.1-1.3zM11.7 11.5l-.5.8h-.7l.8-1.3-.8-1.2h.7l.5.7.5-.7h.7l-.8 1.2.8 1.3h-.7l-.5-.8z"
  }));
};

IconDocumentDocx.displayName = "IconDocumentDocx";
IconDocumentDocx.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentDocx"
};
var DocumentDocx = withIcon(IconDocumentDocx);

const IconDocumentList = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.3628.1875h-12.7256a.75.75 0 0 0 -.75.75v14.125a.75.75 0 0 0 .75.75h12.7256a.75.75 0 0 0 .75-.75v-14.125a.75.75 0 0 0 -.75-.75zm-.75 14.125h-11.2256v-12.625h11.2256z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.895 6.0313h4.5723a.75.75 0 0 0 0-1.5h-4.5723a.75.75 0 0 0 0 1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.28 6.09a.9292.9292 0 0 0 .33.07.98.98 0 0 0 .34-.07.8924.8924 0 0 0 .54-.81.8947.8947 0 0 0 -.26-.62.92.92 0 0 0 -1.24 0 .8834.8834 0 0 0 -.25.62.8542.8542 0 0 0 .54.81z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.28 8.81a.9292.9292 0 0 0 .33.07.98.98 0 0 0 .34-.07 1.0578 1.0578 0 0 0 .28-.19.8719.8719 0 0 0 .26-.62.7543.7543 0 0 0 -.07-.33.81.81 0 0 0 -.19-.29.9018.9018 0 0 0 -.95-.19.9414.9414 0 0 0 -.48.48.916.916 0 0 0 -.06.33.87.87 0 0 0 .26.62.9127.9127 0 0 0 .28.19z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.895 8.75h4.5723a.75.75 0 0 0 0-1.5h-4.5723a.75.75 0 0 0 0 1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m3.99 11.34a.9035.9035 0 0 0 .62.25.9934.9934 0 0 0 .34-.06 1.085 1.085 0 0 0 .28-.19.81.81 0 0 0 .19-.29.726.726 0 0 0 .07-.33.8.8 0 0 0 -.07-.34.8744.8744 0 0 0 -.19-.28.8736.8736 0 0 0 -1.24 0 .8355.8355 0 0 0 -.18.28.7933.7933 0 0 0 -.07.34.8788.8788 0 0 0 .06.33.9634.9634 0 0 0 .19.29z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.895 11.4688h4.5723a.75.75 0 0 0 0-1.5h-4.5723a.75.75 0 0 0 0 1.5z"
  }));
};

IconDocumentList.displayName = "IconDocumentList";
IconDocumentList.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentList"
};
var DocumentList = withIcon(IconDocumentList);

const IconDocumentPdf = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M14.4.2H5.1c-.2 0-.4.1-.5.2L1.1 3.9c-.1.1-.2.3-.2.5V15c0 .4.3.7.7.7h12.7c.4 0 .7-.3.7-.7V.9c0-.4-.3-.7-.6-.7zm-9.5 2v2h-2l2-2zm8.7 12.1H2.4V5.6h3.2c.4 0 .7-.3.7-.7V1.7h7.3v12.6z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.5 11.5v.8h-.6V9.9h1c.6 0 .9.3.9.8s-.4.8-.9.8h-.4zm.3-1.1h-.3v.7h.3c.3 0 .4-.1.4-.3 0-.3-.1-.4-.4-.4zM7.1 9.9H8c.7 0 1.2.4 1.2 1.2 0 .8-.5 1.2-1.2 1.2h-.9V9.9zm.6 1.9H8c.4 0 .6-.2.6-.7 0-.5-.3-.7-.6-.7h-.3v1.4zM9.6 12.3V9.9h1.7v.5h-1.1v.6h.9v.5h-.9v.9h-.6z"
  }));
};

IconDocumentPdf.displayName = "IconDocumentPdf";
IconDocumentPdf.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentPdf"
};
var DocumentPdf = withIcon(IconDocumentPdf);

const IconDocumentPlus = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m7.2482 8.3848h-2.5933a.75.75 0 0 0 0 1.5h2.5933v2.5937a.75.75 0 1 0 1.5 0v-2.5937h2.5933a.75.75 0 0 0 0-1.5h-2.5933v-2.5928a.75.75 0 0 0 -1.5 0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.4845.042h-9.4482a.7608.7608 0 0 0 -.53.22l-3.5247 3.5241a.7433.7433 0 0 0 -.2158.55v10.8719a.75.75 0 0 0 .75.75h12.9687a.75.75 0 0 0 .75-.75v-14.416a.75.75 0 0 0 -.75-.75zm-9.668 2.03v2.0208h-2.0205zm8.918 12.3857h-11.4687v-8.8649h3.3008a.75.75 0 0 0 .75-.75v-3.3008h7.418z"
  }));
};

IconDocumentPlus.displayName = "IconDocumentPlus";
IconDocumentPlus.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentPlus"
};
var DocumentPlus = withIcon(IconDocumentPlus);

const IconDocumentSearch = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.7432 12.8037-2.31-2.31a4.3289 4.3289 0 0 0 -1.4194-6.2927v-2.5535a.75.75 0 0 0 -.75-.75h-6.901a.7607.7607 0 0 0 -.53.22l-3.576 3.5754a.7609.7609 0 0 0 -.22.53v9.1294a.75.75 0 0 0 .75.75h10.4774a.75.75 0 0 0 .75-.75v-2.584c.1215-.0693.2449-.1335.3588-.2137l2.31 2.31a.75.75 0 0 0 1.0605-1.0605zm-5.8794-1.9468a2.8721 2.8721 0 1 1 2.8726-2.8721 2.8755 2.8755 0 0 1 -2.8726 2.8721zm-5.9131-7.7369v1.691h-1.6907zm6.5635 10.4824h-8.9771v-7.2914h3.1636a.75.75 0 0 0 .75-.75v-3.1635h5.0635v1.281a4.32 4.32 0 0 0 -.65-.0657 4.3721 4.3721 0 1 0 0 8.7441 4.3216 4.3216 0 0 0 .65-.0657z"
  }));
};

IconDocumentSearch.displayName = "IconDocumentSearch";
IconDocumentSearch.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentSearch"
};
var DocumentSearch = withIcon(IconDocumentSearch);

const IconDocumentUser = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 10.7448c1.4893 0 2.271-2.3412 1.6728-3.6546a1.7923 1.7923 0 0 0 -1.6728-1.0416 1.7924 1.7924 0 0 0 -1.6728 1.0414c-.598 1.3136.1836 3.6548 1.6728 3.6548z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.4845.042h-9.4482a.7608.7608 0 0 0 -.53.22l-3.5247 3.5241a.7433.7433 0 0 0 -.2158.55v10.8719a.75.75 0 0 0 .75.75h12.9687a.75.75 0 0 0 .75-.75v-14.416a.75.75 0 0 0 -.75-.75zm-9.668 2.03v2.0208h-2.0205zm8.918 12.3857h-1.6045c-.0039-2.8742-1.004-2.07-2.5243-3.0574a.407.407 0 0 0 -.5287.0661l-.8569.8625a.31.31 0 0 1 -.44 0l-.8569-.8625a.4072.4072 0 0 0 -.5287-.0661c-1.52.9879-2.52.1833-2.524 3.0574h-1.6047v-8.8649h3.3008a.75.75 0 0 0 .75-.75v-3.3008h7.418z"
  }));
};

IconDocumentUser.displayName = "IconDocumentUser";
IconDocumentUser.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentUser"
};
var DocumentUser = withIcon(IconDocumentUser);

const IconDocumentX = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m7.822 9.877-2.259 2.889h2.108l1.202-1.624 1.191 1.624h2.2l-2.317-2.95 2.13-2.665h-2.115l-1.087 1.435-1.032-1.435h-2.191z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.4845.0425h-9.4482a.7608.7608 0 0 0 -.53.22l-3.5247 3.5236a.7433.7433 0 0 0 -.2158.55v10.871a.75.75 0 0 0 .75.75h12.9687a.75.75 0 0 0 .75-.75v-14.4146a.75.75 0 0 0 -.75-.75zm-9.668 2.03v2.02h-2.02zm8.918 12.3849h-11.4687v-8.8646h3.3008a.75.75 0 0 0 .75-.75v-3.3h7.418z"
  }));
};

IconDocumentX.displayName = "IconDocumentX";
IconDocumentX.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconDocumentX"
};
var DocumentX = withIcon(IconDocumentX);

const IconEmail = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.91 2.7692a.7258.7258 0 0 0 -.0319-.1581.7209.7209 0 0 0 -.0787-.1371.7092.7092 0 0 0 -.0547-.0951c-.0056-.0064-.0137-.0087-.0195-.0148a.7418.7418 0 0 0 -.1309-.0994.7537.7537 0 0 0 -.1047-.0706.7531.7531 0 0 0 -.1281-.0338.7384.7384 0 0 0 -.1564-.0316c-.0084 0-.0154-.0048-.0239-.0048h-14.3624c-.0085 0-.0156.0045-.024.0048a.7447.7447 0 0 0 -.1571.0317.7581.7581 0 0 0 -.1277.0337.7553.7553 0 0 0 -.1058.0714.7392.7392 0 0 0 -.13.0987c-.0052.0058-.0132.0084-.0188.0147a.711.711 0 0 0 -.0547.0951.7218.7218 0 0 0 -.0787.1371.7258.7258 0 0 0 -.032.1581.7021.7021 0 0 0 -.0212.1048v10.252a.75.75 0 0 0 .75.75h14.3624a.75.75 0 0 0 .75-.75v-10.252a.7018.7018 0 0 0 -.0212-.1048zm-2.7191.8548-5.1909 4.5601-5.1909-4.5601zm-11.6221 8.752v-7.8447l5.936 5.2148a.75.75 0 0 0 .99 0l5.936-5.2148v7.8447z"
  }));
};

IconEmail.displayName = "IconEmail";
IconEmail.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconEmail"
};
var Email = withIcon(IconEmail);

const IconEye = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 4.43a3.57 3.57 0 1 0 3.57 3.57 3.574 3.574 0 0 0 -3.57-3.57zm0 5.74a2.17 2.17 0 1 1 2.1694-2.17 2.1715 2.1715 0 0 1 -2.1694 2.17z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.7974 7.635c-.1174-.1914-2.919-4.6894-7.7974-4.6894s-7.68 4.498-7.7974 4.6894a.7.7 0 0 0 0 .7295c.1172.1914 2.919 4.69 7.7969 4.69s7.6807-4.4985 7.7978-4.69a.7.7 0 0 0 .0001-.7295zm-7.7974 4.0191c-3.3394 0-5.6167-2.6631-6.3491-3.6541.7324-.9914 3.0098-3.654 6.3491-3.654s5.6167 2.6626 6.3491 3.654c-.7324.991-3.0097 3.6541-6.3491 3.6541z"
  }));
};

IconEye.displayName = "IconEye";
IconEye.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconEye"
};
var Eye = withIcon(IconEye);

const IconFilter = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.83 1.2461a.75.75 0 0 0 -.689-.4541h-14.2821a.75.75 0 0 0 -.544 1.2666l5.5933 5.8887v6.5107a.75.75 0 0 0 1.5 0v-6.8105a.7506.7506 0 0 0 -.206-.5166l-4.5967-4.8389h10.789l-4.5967 4.8389a.7506.7506 0 0 0 -.206.5166v5.2334a.75.75 0 1 0 1.5 0v-4.9336l5.5933-5.8887a.75.75 0 0 0 .1449-.8125z"
  }));
};

IconFilter.displayName = "IconFilter";
IconFilter.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFilter"
};
var Filter = withIcon(IconFilter);

const IconFlag = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.4082 2.12a.708.708 0 0 0 -.0214-.1058.7057.7057 0 0 0 -.01-.109c-.0042-.0138-.0147-.0232-.02-.0366a.72.72 0 0 0 -.0731-.1368.7418.7418 0 0 0 -.076-.1127.7343.7343 0 0 0 -.1107-.0907.7377.7377 0 0 0 -.118-.08.7329.7329 0 0 0 -.1264-.0395.72.72 0 0 0 -.1572-.0318c-.0133-.0007-.0245-.0076-.0379-.0076a.7042.7042 0 0 0 -.103.0208.6983.6983 0 0 0 -.1118.01 8.312 8.312 0 0 1 -3.3877.3511 4.0238 4.0238 0 0 1 -1.0928-.6006 5.0583 5.0583 0 0 0 -3.0335-1.1056 6.2638 6.2638 0 0 0 -2.9206.752.7451.7451 0 0 0 -1.4163.3285v14.0791a.75.75 0 0 0 1.5 0v-5.0821c.0184-.0124.04-.0159.0576-.03a4.9274 4.9274 0 0 1 2.7793-.9219 3.6482 3.6482 0 0 1 2.19.8467 5.2365 5.2365 0 0 0 1.5283.8042 4.3089 4.3089 0 0 0 1.1553.1372 12.2 12.2 0 0 0 3.07-.4941c.0139-.0042.0232-.0147.0366-.02a.7193.7193 0 0 0 .1355-.0723.628.628 0 0 0 .3243-.4354.7248.7248 0 0 0 .0313-.1509c.0008-.0141.0081-.0261.0081-.04zm-4.3525 7.2582a4.024 4.024 0 0 1 -1.0928-.6006 5.0583 5.0583 0 0 0 -3.0342-1.1069 6.2394 6.2394 0 0 0 -2.8369.7093v-5.8829c.0184-.0123.04-.0159.0576-.03a4.9274 4.9274 0 0 1 2.7793-.9219 3.6481 3.6481 0 0 1 2.19.8467 5.2364 5.2364 0 0 0 1.5283.8042 4.3089 4.3089 0 0 0 1.1553.1372 10.4541 10.4541 0 0 0 2.1059-.2533v6.086a7.0647 7.0647 0 0 1 -2.8525.2122z"
  }));
};

IconFlag.displayName = "IconFlag";
IconFlag.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFlag"
};
var Flag = withIcon(IconFlag);

const IconFlask = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m10.5547 4.5806v-3.0684h.73a.7.7 0 1 0 0-1.4h-6.57a.7.7 0 1 0 0 1.4h.73v3.0684a5.95 5.95 0 1 0 5.1094 0zm-2.5547 9.9072a4.5327 4.5327 0 0 1 -3.6412-1.85h1.7867a.7.7 0 1 0 0-1.4h-2.4833a4.47 4.47 0 0 1 -.1412-.6h2.6245a.7.7 0 1 0 0-1.4h-2.642a4.5027 4.5027 0 0 1 .1392-.6h2.5028a.7.7 0 0 0 0-1.4h-1.7955a4.561 4.561 0 0 1 2.0436-1.5415.699.699 0 0 0 .4521-.6543v-3.53h2.3086v3.53a.699.699 0 0 0 .4522.6543 4.5409 4.5409 0 0 1 -1.6065 8.7915z"
  }));
};

IconFlask.displayName = "IconFlask";
IconFlask.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFlask"
};
var Flask = withIcon(IconFlask);

const IconFlowChart = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m10.5547 4.5806v-3.0684h.73a.7.7 0 1 0 0-1.4h-6.57a.7.7 0 1 0 0 1.4h.73v3.0684a5.95 5.95 0 1 0 5.1094 0zm-2.5547 9.9072a4.5327 4.5327 0 0 1 -3.6412-1.85h1.7867a.7.7 0 1 0 0-1.4h-2.4833a4.47 4.47 0 0 1 -.1412-.6h2.6245a.7.7 0 1 0 0-1.4h-2.642a4.5027 4.5027 0 0 1 .1392-.6h2.5028a.7.7 0 0 0 0-1.4h-1.7955a4.561 4.561 0 0 1 2.0436-1.5415.699.699 0 0 0 .4521-.6543v-3.53h2.3086v3.53a.699.699 0 0 0 .4522.6543 4.5409 4.5409 0 0 1 -1.6065 8.7915z"
  }));
};

IconFlowChart.displayName = "IconFlowChart";
IconFlowChart.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFlowChart"
};
var FlowChart = withIcon(IconFlowChart);

const IconFolder = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1772 3.63h-7.0947l-1.3476-2.3453a.75.75 0 0 0 -.65-.377h-5.2621a.75.75 0 0 0 -.75.75v12.6846a.75.75 0 0 0 .75.75h14.3544a.75.75 0 0 0 .75-.75v-9.9623a.75.75 0 0 0 -.75-.75zm-9.5272-1.2223.7024 1.2223h-4.78v-1.2223zm8.7773 11.1846h-12.8545v-8.4623h12.8545v8.4619z"
  }));
};

IconFolder.displayName = "IconFolder";
IconFolder.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFolder"
};
var Folder = withIcon(IconFolder);

const IconFolderArrow = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.828 9.7124a.75.75 0 0 0 -.647-.3706h-2.102v-2.1343a.75.75 0 0 0 -.75-.75h-5.8447v-.9253a.75.75 0 0 0 -.75-.75h-4.9155a.75.75 0 0 0 -.75.75v9.6631l.0008.0041c0 .009.0049.017.0053.026a.733.733 0 0 0 .0865.3117c.0062.0119.0036.026.01.0377.0111.0188.0324.0269.0449.0445a.7311.7311 0 0 0 .1841.1821.7088.7088 0 0 0 .08.054.7318.7318 0 0 0 .3378.09h11.51a.7506.7506 0 0 0 .6548-.3843l2.852-5.1035a.749.749 0 0 0 -.007-.7452zm-10.8437-3.4302v.9253a.75.75 0 0 0 .75.75h5.8447v1.3843h-7.9077a.7506.7506 0 0 0 -.6548.3843l-1.4477 2.59v-6.0339zm6.9048 8.1631h-9.792l2.0142-3.6035h9.7915z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.8339 5.7422a.7029.7029 0 0 0 .99 0l2.0991-2.0991a.7.7 0 0 0 -.99-.99l-.9038.9041v-2.8023a.7.7 0 0 0 -1.4 0v2.802l-.9038-.9041a.7.7 0 0 0 -.99.99z"
  }));
};

IconFolderArrow.displayName = "IconFolderArrow";
IconFolderArrow.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFolderArrow"
};
var FolderArrow = withIcon(IconFolderArrow);

const IconFolderPencil = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m12.769 8.1772a.75.75 0 0 0 -.75.75v4.127h-10.4843v-7.8174h7.0141a.75.75 0 1 0 0-1.5h-1.6315l-1.0897-1.8968a.7506.7506 0 0 0 -.65-.376h-4.3929a.75.75 0 0 0 -.75.75v11.59a.75.75 0 0 0 .75.75h11.9844a.75.75 0 0 0 .75-.75v-4.8768a.75.75 0 0 0 -.7501-.75zm-8.0258-5.2128.4437.7725h-3.6522v-.7725z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.7456 3.5474-1.8906-1.8907a.772.772 0 0 0 -1.0605 0l-1.6129 1.6133-4.0293 4.0293a.75.75 0 0 0 -.22.541l.0277 1.8633a.75.75 0 0 0 .7388.7393l1.8632.0271h.0112a.75.75 0 0 0 .53-.22l4.0268-4.0262.0018-.0009.0022-.0029 1.6116-1.6118a.75.75 0 0 0 0-1.0608zm-2.4209-.3.83.83-.5531.5531-.83-.83zm-4.0576 5.7179-.8179-.0117-.0122-.8183 3.2743-3.2743.83.83z"
  }));
};

IconFolderPencil.displayName = "IconFolderPencil";
IconFolderPencil.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFolderPencil"
};
var FolderPencil = withIcon(IconFolderPencil);

const IconFolderUser = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1775 3.63h-7.0952l-1.3477-2.3458a.7506.7506 0 0 0 -.65-.376h-5.2621a.75.75 0 0 0 -.75.75v12.6836a.75.75 0 0 0 .75.75h14.355a.75.75 0 0 0 .75-.75v-9.9618a.75.75 0 0 0 -.75-.75zm-9.5275-1.2218.702 1.2218h-4.78v-1.2218zm8.7773 11.1836h-2.2115c-.1233-2.3251-1.1139-1.6335-2.5717-2.5808a.4166.4166 0 0 0 -.5413.0677l-.8774.8831a.3173.3173 0 0 1 -.45 0l-.8775-.8831a.4168.4168 0 0 0 -.5414-.0677c-1.4575.9473-2.4481.2557-2.5714 2.5808h-2.2126v-8.4618h12.855z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.2872 7.0741c-.6124 1.3449.188 3.7421 1.7128 3.7421s2.3256-2.3972 1.7131-3.7421a1.8353 1.8353 0 0 0 -1.7131-1.0666 1.8353 1.8353 0 0 0 -1.7128 1.0666z"
  }));
};

IconFolderUser.displayName = "IconFolderUser";
IconFolderUser.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFolderUser"
};
var FolderUser = withIcon(IconFolderUser);

const IconFootprint = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m5.3337.3208a2.4263 2.4263 0 0 0 -2.0928.0492c-.2773.172-2.6709 1.8619-1.3838 7.9728a.6989.6989 0 0 0 .6846.5562h3.1583a.7.7 0 0 0 .52-.231c.0684-.0752 1.6572-1.8867.8789-5.5566a3.6918 3.6918 0 0 0 -1.7652-2.7906zm.0063 7.1777h-2.2221c-.832-4.6367.82-5.9077.8047-5.9077a1.017 1.017 0 0 1 .8213 0 2.5268 2.5268 0 0 1 .9854 1.811 5.6227 5.6227 0 0 1 -.3893 4.0967z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.5983 9.4346h-2.955a.7.7 0 0 0 -.7.7109c.0039.2378.0664 2.3511 1.124 3.3125a1.8293 1.8293 0 0 0 1.2539.4888c.0527 0 .1065-.002.1611-.0063a2.097 2.097 0 0 0 1.6416-.8755 3.6706 3.6706 0 0 0 .1473-3.125.7009.7009 0 0 0 -.6729-.5054zm-.627 2.8345c-.041.06-.167.2412-.6006.2759a.4356.4356 0 0 1 -.3613-.1221 2.9743 2.9743 0 0 1 -.6016-1.5879h1.6422a2.0206 2.0206 0 0 1 -.0786 1.434z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.2819 4.1748a2.529 2.529 0 0 0 -1.6719-1.9986 2.9173 2.9173 0 0 0 -2.498.99c-.1074.1328-2.623 3.2959-1.3945 6.98a.7011.7011 0 0 0 .48.4546l3.0059.8149a.7246.7246 0 0 0 .1836.0244.6994.6994 0 0 0 .5967-.335c.0932-.1496 2.2406-3.7145 1.2982-6.9303zm-2.2451 5.7452-2.0947-.568a6.7518 6.7518 0 0 1 1.2187-5.26c.2627-.271.8047-.6367 1.12-.5552.2432.06.4834.436.6572 1.0313.5939 2.0266-.41 4.3796-.9012 5.3519z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.0769 11.9541-3.1084-.917a.6943.6943 0 0 0 -.82.3506c-.1934.3735-1.123 2.3018-.4883 3.5493a1.7356 1.7356 0 0 0 1.36.9277 2.69 2.69 0 0 0 .4131.0322c1.7978 0 2.8008-1.894 3.1211-3.09a.6994.6994 0 0 0 -.4775-.8528zm-2.8438 2.5268c-.2539-.0391-.3018-.1333-.3252-.1787a2.7692 2.7692 0 0 1 .252-1.749l1.8018.5317c-.294.646-.8624 1.5332-1.7286 1.396z"
  }));
};

IconFootprint.displayName = "IconFootprint";
IconFootprint.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconFootprint"
};
var Footprint = withIcon(IconFootprint);

const IconGear = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m5.4419 5.4429a3.617 3.617 0 1 0 5.1152 0 3.6231 3.6231 0 0 0 -5.1152 0zm4.125 4.125a2.2166 2.2166 0 1 1 0-3.1348 2.2174 2.2174 0 0 1 0 3.1348z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.3511 6.3462-.875-.1934a6.6869 6.6869 0 0 0 -.59-1.4277l.4815-.7539a.7.7 0 0 0 -.0956-.8712l-1.372-1.372a.7006.7006 0 0 0 -.8711-.0947l-.7539.4795a6.7171 6.7171 0 0 0 -1.458-.5986l-.1593-.8428a.6993.6993 0 0 0 -.6877-.5714h-1.9412a.6992.6992 0 0 0 -.6875.5713l-.1592.8428a6.6222 6.6222 0 0 0 -1.4863.6133l-.7058-.4824a.6979.6979 0 0 0 -.89.083l-1.3729 1.372a.7.7 0 0 0 -.083.89l.4824.707a6.6218 6.6218 0 0 0 -.6133 1.4863l-.8432.159a.6993.6993 0 0 0 -.57.6877v1.9412a.6993.6993 0 0 0 .57.6875l.8428.1592a6.6222 6.6222 0 0 0 .6133 1.4863l-.4824.706a.7.7 0 0 0 .083.89l1.3733 1.3727a.7016.7016 0 0 0 .89.083l.706-.4824a6.6818 6.6818 0 0 0 1.456.6025l.1934.875a.7009.7009 0 0 0 .6836.5488h1.941a.7009.7009 0 0 0 .6836-.5488l.1934-.875a6.6713 6.6713 0 0 0 1.4277-.5889l.7529.48a.7013.7013 0 0 0 .8721-.0947l1.3723-1.3724a.7.7 0 0 0 .0957-.8711l-.48-.7539a6.6923 6.6923 0 0 0 .5889-1.4277l.874-.1924a.7.7 0 0 0 .55-.6836v-1.9413a.7009.7009 0 0 0 -.5495-.6838zm-.8511 2.0625-.7109.1563a.7.7 0 0 0 -.5723.5469 5.27 5.27 0 0 1 -.74 1.793.7035.7035 0 0 0 -.0879.5654.69.69 0 0 0 .0986.2119l.3975.625-.5781.5791-.707-.4482a.6922.6922 0 0 0 -.6953.0391 5.2924 5.2924 0 0 1 -1.793.74.7041.7041 0 0 0 -.541.5508l-.1629.732h-.8164l-.1572-.71a.7.7 0 0 0 -.5459-.5723 5.29 5.29 0 0 1 -1.8282-.7612.7007.7007 0 0 0 -.78.0059l-.6006.41-.5518-.5518.41-.6006a.7005.7005 0 0 0 .0059-.7812 5.277 5.277 0 0 1 -.7705-1.8651.6972.6972 0 0 0 -.5557-.5479l-.7168-.1358v-.7792l.7168-.1357a.6972.6972 0 0 0 .5557-.5479 5.277 5.277 0 0 1 .7705-1.8652.7005.7005 0 0 0 -.0059-.7812l-.41-.6006.5518-.5518.6006.41a.7023.7023 0 0 0 .7813.0059 5.277 5.277 0 0 1 1.8652-.7705.6971.6971 0 0 0 .5479-.5566l.1356-.7172h.7793l.1357.7168a.6971.6971 0 0 0 .5478.5566 5.2681 5.2681 0 0 1 1.831.75.7049.7049 0 0 0 .77-.0049l.6338-.4033.5772.5772-.3945.62a.7.7 0 0 0 -.0137.7832 5.289 5.289 0 0 1 .74 1.792.7015.7015 0 0 0 .5723.5469l.7109.1572z"
  }));
};

IconGear.displayName = "IconGear";
IconGear.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconGear"
};
var Gear = withIcon(IconGear);

const IconGlobe = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .1626a7.8374 7.8374 0 1 0 7.8374 7.8374 7.847 7.847 0 0 0 -7.8374-7.8374zm5.7029 4.8638h-2.7229a10.76 10.76 0 0 0 -1.465-3.2764 6.4548 6.4548 0 0 1 4.1884 3.2764zm.7341 2.9736a6.4073 6.4073 0 0 1 -.247 1.7466h-3.1179a10.8857 10.8857 0 0 0 .1641-3.32h2.9984a6.4075 6.4075 0 0 1 .2024 1.5734zm-8.367 1.7466a9.2635 9.2635 0 0 1 .03-3.32h3.7392a9.46 9.46 0 0 1 -.1564 3.32zm3.2011 1.4a12.8 12.8 0 0 1 -1.3688 2.6967 10.0518 10.0518 0 0 1 -1.46-2.6967zm-7.7081-3.1466a6.407 6.407 0 0 1 .2026-1.5732h2.95a10.7047 10.7047 0 0 0 -.0457 3.32h-2.8599a6.407 6.407 0 0 1 -.247-1.7468zm4.8963-2.9736a12.7285 12.7285 0 0 1 1.4935-3.06 9.907 9.907 0 0 1 1.58 3.06zm-.0082-3.267a14.544 14.544 0 0 0 -1.4149 3.267h-2.74a6.455 6.455 0 0 1 4.1549-3.267zm-4.0606 9.3876h2.5867a10.9571 10.9571 0 0 0 1.4957 3.0992 6.4546 6.4546 0 0 1 -4.0824-3.0992zm6.8552 3.1667a14.6892 14.6892 0 0 0 1.4648-3.1667h2.8995a6.4525 6.4525 0 0 1 -4.3643 3.1667z"
  }));
};

IconGlobe.displayName = "IconGlobe";
IconGlobe.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconGlobe"
};
var Globe = withIcon(IconGlobe);

const IconGovBuilding = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m1.0078 6.1713h1.8522v5.49h-.3922a.7.7 0 0 0 0 1.4h11.0644a.7.7 0 0 0 0-1.4h-.3922v-5.49h1.8525a.7.7 0 0 0 .3506-1.3057l-6.9925-4.0522a.6977.6977 0 0 0 -.7012 0l-6.9922 4.0522a.7.7 0 0 0 .3506 1.3057zm3.2529 0h1.5593v5.49h-1.56zm4.52 0v5.49h-1.56v-5.49zm2.959 5.49h-1.559v-5.49h1.5586zm-3.7397-9.4334 4.3887 2.543h-8.7774z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.083 13.8807h-14.166a.7.7 0 0 0 0 1.4h14.166a.7.7 0 1 0 0-1.4z"
  }));
};

IconGovBuilding.displayName = "IconGovBuilding";
IconGovBuilding.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconGovBuilding"
};
var GovBuilding = withIcon(IconGovBuilding);

const IconGraduationHat = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.5341 5.5032-7.1768-3.25a.75.75 0 0 0 -.5967-.01l-7.2724 3.0105a.75.75 0 0 0 -.4629.6929c0 .3547.0131.4552 3.0049 1.835v3.8491a.7478.7478 0 0 0 .1475.4458c.78 1.0552 2.6934 1.7368 4.874 1.7368a6.56 6.56 0 0 0 4.7266-1.5561.75.75 0 0 0 .1914-.5v-3.8261l.78-.3229v2.3486a.75.75 0 1 0 1.5 0v-2.9693l.2617-.1083a.75.75 0 0 0 .0225-1.376zm-4.0644 5.93a5.8766 5.8766 0 0 1 -3.418.88 5.5877 5.5877 0 0 1 -3.5217-.9663v-2.8789c.8657.394 1.88.8534 3.1123 1.4112a.75.75 0 0 0 .5967.01l3.23-1.337zm-3.5039-3.0545c-.8858-.4009-3.5625-1.6128-5.3037-2.4009l5.372-2.2237 5.3037 2.4014z"
  }));
};

IconGraduationHat.displayName = "IconGraduationHat";
IconGraduationHat.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconGraduationHat"
};
var GraduationHat = withIcon(IconGraduationHat);

const IconGrid = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.146.1533h-14.292a.7.7 0 0 0 -.7.7v14.293a.7.7 0 0 0 .7.7h14.292a.7.7 0 0 0 .7-.7v-14.2928a.7.7 0 0 0 -.7-.7002zm-13.5918 6.1651h3.3638v3.3632h-3.3638zm4.7642 0h3.3632v3.3632h-3.3632zm4.7637 0h3.3638v3.3632h-3.3639zm3.3638-1.4h-3.3639v-3.3647h3.3638zm-4.7642 0h-3.3633v-3.3647h3.3632zm-8.1275-3.3647h3.3638v3.3643h-3.3638zm0 9.5283h3.3638v3.3643h-3.3638zm4.7642 0h3.3632v3.3643h-3.3632zm8.1274 3.3643h-3.3638v-3.3643h3.3638z"
  }));
};

IconGrid.displayName = "IconGrid";
IconGrid.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconGrid"
};
var Grid = withIcon(IconGrid);

const IconGridCarets = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m6.98 2.1757h-5.4976c-.3583 0-.5.2508-.3142.5574l2.7257 4.505a.3624.3624 0 0 0 .6746 0l2.7259-4.505c.1856-.3065.0441-.5574-.3144-.5574z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.4315 7.2381a.3624.3624 0 0 0 .6746 0l2.7258-4.5049c.1855-.3066.0441-.5574-.3142-.5574h-5.4977c-.3584 0-.5.2508-.3143.5574z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.98 8.532h-5.4976c-.3583 0-.5.2508-.3142.5574l2.7258 4.5049a.3624.3624 0 0 0 .6746 0l2.7258-4.5049c.1856-.3066.0441-.5574-.3144-.5574z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.5176 8.532h-5.4976c-.3584 0-.5.2508-.3143.5574l2.7258 4.5049a.3624.3624 0 0 0 .6746 0l2.7258-4.5049c.1855-.3066.0441-.5574-.3143-.5574z"
  }));
};

IconGridCarets.displayName = "IconGridCarets";
IconGridCarets.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconGridCarets"
};
var GridCarets = withIcon(IconGridCarets);

const IconGridCircles = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m2.6357.1377a2.4981 2.4981 0 1 0 2.498 2.498 2.5015 2.5015 0 0 0 -2.498-2.498zm0 3.5957a1.0977 1.0977 0 1 1 1.0977-1.0977 1.0986 1.0986 0 0 1 -1.0977 1.0977z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 .1377a2.498 2.498 0 1 0 2.498 2.498 2.5015 2.5015 0 0 0 -2.498-2.498zm0 3.5957a1.0977 1.0977 0 1 1 1.0977-1.0977 1.0986 1.0986 0 0 1 -1.0977 1.0977z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.3643 5.1338a2.498 2.498 0 1 0 -2.498-2.498 2.5015 2.5015 0 0 0 2.498 2.498zm0-3.5957a1.0977 1.0977 0 1 1 -1.0977 1.0977 1.0986 1.0986 0 0 1 1.0977-1.0977z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m2.6357 10.8662a2.498 2.498 0 1 0 2.498 2.498 2.5015 2.5015 0 0 0 -2.498-2.498zm0 3.5957a1.0977 1.0977 0 1 1 1.0977-1.0977 1.0986 1.0986 0 0 1 -1.0977 1.0977z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 10.8662a2.498 2.498 0 1 0 2.498 2.498 2.5015 2.5015 0 0 0 -2.498-2.498zm0 3.5957a1.0977 1.0977 0 1 1 1.0977-1.0977 1.0986 1.0986 0 0 1 -1.0977 1.0977z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.3643 10.8662a2.498 2.498 0 1 0 2.498 2.498 2.5015 2.5015 0 0 0 -2.498-2.498zm0 3.5957a1.0977 1.0977 0 1 1 1.0977-1.0977 1.0986 1.0986 0 0 1 -1.0977 1.0977z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m2.6357 5.5019a2.498 2.498 0 1 0 2.4981 2.4981 2.5015 2.5015 0 0 0 -2.4981-2.4981zm0 3.5957a1.0977 1.0977 0 1 1 1.0977-1.0976 1.0986 1.0986 0 0 1 -1.0977 1.0977z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 5.5019a2.498 2.498 0 1 0 2.498 2.4981 2.5015 2.5015 0 0 0 -2.498-2.4981zm0 3.5958a1.0977 1.0977 0 1 1 1.0977-1.0977 1.0986 1.0986 0 0 1 -1.0977 1.0977z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.3643 5.5019a2.498 2.498 0 1 0 2.498 2.4981 2.5015 2.5015 0 0 0 -2.498-2.4981zm0 3.5957a1.0977 1.0977 0 1 1 1.0976-1.0976 1.0986 1.0986 0 0 1 -1.0976 1.0977z"
  }));
};

IconGridCircles.displayName = "IconGridCircles";
IconGridCircles.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconGridCircles"
};
var GridCircles = withIcon(IconGridCircles);

const IconHappy = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.873 8.83a.7482.7482 0 0 0 -.96.4522 3.074 3.074 0 0 1 -5.7754-.001.75.75 0 0 0 -1.41.51 4.5741 4.5741 0 0 0 8.5977-.001.7513.7513 0 0 0 -.4523-.9602z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 .125a7.875 7.875 0 1 0 7.875 7.875 7.8833 7.8833 0 0 0 -7.875-7.875zm0 14.25a6.375 6.375 0 1 1 6.375-6.375 6.3817 6.3817 0 0 1 -6.375 6.375z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "4.6871",
    cy: "6.3912",
    r: "1.1271"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "11.4028",
    cy: "6.3912",
    r: "1.1271"
  }));
};

IconHappy.displayName = "IconHappy";
IconHappy.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconHappy"
};
var Happy = withIcon(IconHappy);

const IconHappyFilled = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .125a7.875 7.875 0 1 0 7.875 7.875 7.8839 7.8839 0 0 0 -7.875-7.875zm-3.3129 5.2833a.9829.9829 0 1 1 -.983.9829.9829.9829 0 0 1 .983-.9829zm7.497 4.3317a4.4245 4.4245 0 0 1 -8.3159 0 .6.6 0 1 1 1.1289-.4072 3.2244 3.2244 0 0 0 6.0581 0 .6.6 0 1 1 1.1289.4072zm-.7812-2.3657a.9829.9829 0 1 1 .9829-.9829.9829.9829 0 0 1 -.983.9827z"
  }));
};

IconHappyFilled.displayName = "IconHappyFilled";
IconHappyFilled.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconHappyFilled"
};
var HappyFilled = withIcon(IconHappyFilled);

const IconHealth = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m12.8716 9.9934a2.0425 2.0425 0 0 0 -1.4516 3.4808 2.0536 2.0536 0 0 1 -2.4937.8375 2.2821 2.2821 0 0 1 -1.6463-2.2241v-1.5143a5.295 5.295 0 0 0 4.6953-5.1351c.001-.0137.001-.0273.001-.041v-4.5893a.75.75 0 0 0 -.75-.75h-1.925a.75.75 0 1 0 0 1.5h1.1748v3.7714a3.809 3.809 0 0 1 -3.8047 3.8047h-.2832a3.809 3.809 0 0 1 -3.8047-3.8047v-3.7714h1.1865a.75.75 0 1 0 0-1.5h-1.9365a.75.75 0 0 0 -.75.75v4.5893c0 .0137 0 .0273.001.041a5.295 5.295 0 0 0 4.6955 5.1351v1.4938a3.796 3.796 0 0 0 2.7129 3.6807 4.3132 4.3132 0 0 0 1.2461.1943 3.5329 3.5329 0 0 0 3.073-1.8659c.02.0006.0393.006.06.006a2.0444 2.0444 0 1 0 0-4.0889zm0 2.6885a.644.644 0 1 1 .6445-.644.6445.6445 0 0 1 -.6445.644z"
  }));
};

IconHealth.displayName = "IconHealth";
IconHealth.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconHealth"
};
var Health = withIcon(IconHealth);

const IconHeart = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.72 2.4795a4.1282 4.1282 0 0 0 -5.84 0l-.8811.8805-.8809-.8805a4.1294 4.1294 0 0 0 -5.84 5.84l6.19 6.1914a.7509.7509 0 0 0 1.0615 0l6.1905-6.1916a4.1282 4.1282 0 0 0 0-5.84zm-1.0609 4.7793-5.6602 5.6612-5.66-5.6611a2.63 2.63 0 1 1 3.7186-3.7189l1.4111 1.4112a.75.75 0 0 0 1.0606 0l1.4108-1.4112a2.6912 2.6912 0 0 1 3.7188 0 2.6287 2.6287 0 0 1 0 3.7187z"
  }));
};

IconHeart.displayName = "IconHeart";
IconHeart.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconHeart"
};
var Heart = withIcon(IconHeart);

const IconHeartFilled = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.7192 2.48a4.1282 4.1282 0 0 0 -5.84 0l-.8809.88-.8809-.88a4.1294 4.1294 0 0 0 -5.84 5.84l6.19 6.1909a.7509.7509 0 0 0 1.0615 0l6.1903-6.1909a4.1282 4.1282 0 0 0 0-5.84z"
  }));
};

IconHeartFilled.displayName = "IconHeartFilled";
IconHeartFilled.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconHeartFilled"
};
var HeartFilled = withIcon(IconHeartFilled);

const IconHeartHand = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8.9813 8.3425a.7008.7008 0 0 0 .9907 0l3.644-3.6445a2.69 2.69 0 1 0 -3.8042-3.8037l-.3354.3357-.3355-.3357a2.69 2.69 0 0 0 -3.8037 3.8037zm-2.6539-6.458a1.292 1.292 0 0 1 1.8237 0l.83.8306a.7.7 0 0 0 .99 0l.83-.8306a1.29 1.29 0 1 1 1.8242 1.8232l-3.1494 3.15-3.1489-3.15a1.29 1.29 0 0 1 .0004-1.8232z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.7479 10.5974a1.799 1.799 0 0 0 -2.2363-.977.7071.7071 0 0 0 -.1069.0405l-2.2222 1.0649a1.7737 1.7737 0 0 0 -1.2838-1.3681.7072.7072 0 0 0 -.1753-.0225h-1.7485a4.3161 4.3161 0 0 0 -2.9014-1.5781h-4.2729a.7.7 0 0 0 -.7.7v5.2529a.7.7 0 0 0 .7.7h2.1959l2.7758 1.041a4.4955 4.4955 0 0 0 1.9751.4409 4.968 4.968 0 0 0 2.07-.4609l5.272-2.7666a1.5438 1.5438 0 0 0 .6585-2.067zm-14.2471-1.4399h.9228v3.8525h-.9228zm12.9678 2.2525-5.2657 2.7641a3.2594 3.2594 0 0 1 -2.7964.0293.6873.6873 0 0 0 -.1016-.0474l-2.481-.93v-4.0707l1.16-.0037c.4053.0518 1.625.3706 1.9053 1.127a.7.7 0 0 0 .6563.457h2.061a.3085.3085 0 0 1 .2041.2671.6649.6649 0 0 1 -.1123.42h-3.2747a.7.7 0 1 0 0 1.4h3.375a1.2212 1.2212 0 0 0 .773-.3063.6881.6881 0 0 0 .2421-.0613l3.13-1.5c.127-.0254.416-.0581.5044.1616.0292.0733.0893.2488.0205.2933z"
  }));
};

IconHeartHand.displayName = "IconHeartHand";
IconHeartHand.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconHeartHand"
};
var HeartHand = withIcon(IconHeartHand);

const IconHelp = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .05a7.95 7.95 0 1 0 7.95 7.95 7.9591 7.9591 0 0 0 -7.95-7.95zm.75 12.5654a.75.75 0 0 1 -1.5 0v-.6a.75.75 0 0 1 1.5 0zm.7685-4.3047a1.5452 1.5452 0 0 0 -.7685 1.3321.75.75 0 1 1 -1.5 0 3.05 3.05 0 0 1 1.5171-2.63 1.5379 1.5379 0 1 0 -2.3049-1.3339.75.75 0 0 1 -1.5 0 3.0376 3.0376 0 1 1 4.5561 2.6319z"
  }));
};

IconHelp.displayName = "IconHelp";
IconHelp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconHelp"
};
var Help = withIcon(IconHelp);

const IconHouse = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.7173 7.4534-7.2227-6.3179a.7515.7515 0 0 0 -.9883 0l-3.8154 3.337v-2.0225h1.0117a.75.75 0 0 0 0-1.5h-1.7617a.75.75 0 0 0 -.75.75v4.0844l-1.9082 1.669a.75.75 0 0 0 .9883 1.1289l.92-.8046v6.5223a.75.75 0 0 0 .75.75h10.1181a.75.75 0 0 0 .75-.75v-6.5224l.92.8047a.75.75 0 0 0 .9883-1.1289zm-6.7803 6.0966h-1.873v-4.7822h1.873zm3.3721 0h-1.8721v-5.5322a.75.75 0 0 0 -.75-.75h-3.373a.75.75 0 0 0 -.75.75v5.5322h-1.8731v-7.0842l4.3091-3.7693 4.3086 3.7689z"
  }));
};

IconHouse.displayName = "IconHouse";
IconHouse.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconHouse"
};
var House = withIcon(IconHouse);

const IconImage = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1553 1.145h-14.3106a.75.75 0 0 0 -.75.75v12.21a.75.75 0 0 0 .75.75h14.3106a.75.75 0 0 0 .75-.75v-12.21a.75.75 0 0 0 -.75-.75zm-.75 1.5v6.9432l-2.1084-1.8982a.77.77 0 0 0 -.544-.1914.7531.7531 0 0 0 -.52.2515l-2.0288 2.2909-3.3135-3.314a.7517.7517 0 0 0 -1.0117-.0454l-3.2842 2.7452v-6.7818zm-12.8106 10.71v-1.972l3.7207-3.11 3.3916 3.392a.71.71 0 0 0 .5527.2192.7493.7493 0 0 0 .5391-.252l2.0567-2.3212 2.55 2.2961v1.7479z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8.99 7.9575a2.35 2.35 0 1 0 -2.35-2.3501 2.3523 2.3523 0 0 0 2.35 2.3501zm0-3.3a.95.95 0 1 1 -.95.95.95.95 0 0 1 .95-.9498z"
  }));
};

IconImage.displayName = "IconImage";
IconImage.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconImage"
};
var Image = withIcon(IconImage);

const IconInbox = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.8957 7.3561a.7411.7411 0 0 0 -.0678-.2561.706.706 0 0 0 -.0413-.1035l-3.2295-4.9413a.751.751 0 0 0 -.6279-.34h-8.1064a.7494.7494 0 0 0 -.6426.3638l-2.9802 4.9576c-.0089.0148-.009.0317-.0167.0468a.7269.7269 0 0 0 -.0544.1525.73.73 0 0 0 -.0264.1307c-.002.0204-.0125.0379-.0125.0587v4.6856a2.2378 2.2378 0 0 0 2.292 2.1738h11.2357a2.2378 2.2378 0 0 0 2.292-2.1738v-4.6856a.6916.6916 0 0 0 -.014-.0692zm-11.6491-4.1408h7.2764l2.2618 3.46h-2.88a.7567.7567 0 0 0 -.7129.5171 2.5184 2.5184 0 0 1 -2.3232 1.6919 2.2048 2.2048 0 0 1 -2.333-1.6113.75.75 0 0 0 -.7344-.5976h-2.6348zm10.1634 8.8955a.75.75 0 0 1 -.792.6738h-11.2357a.75.75 0 0 1 -.792-.6738v-3.9355h2.6739a3.7578 3.7578 0 0 0 3.6045 2.209 3.9485 3.9485 0 0 0 3.53-2.209h3.0113z"
  }));
};

IconInbox.displayName = "IconInbox";
IconInbox.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconInbox"
};
var Inbox = withIcon(IconInbox);

const IconInfo = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .05a7.95 7.95 0 1 0 7.95 7.95 7.959 7.959 0 0 0 -7.95-7.95zm-.707 3.1956a.75.75 0 1 1 1.5 0v1.0264a.75.75 0 0 1 -1.5 0zm2.8428 8.7266-1.5626 1.5615a.751.751 0 0 1 -1.28-.53v-4.2464l-.2812.2813a.75.75 0 0 1 -1.0608-1.0606l1.5615-1.5615a.7511.7511 0 0 1 1.28.53v4.2469l.2822-.282a.75.75 0 1 1 1.0605 1.0605z"
  }));
};

IconInfo.displayName = "IconInfo";
IconInfo.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconInfo"
};
var Info = withIcon(IconInfo);

const IconKey = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m3.5095 3.5115a1.9416 1.9416 0 0 0 2.7461 2.7456 1.9451 1.9451 0 0 0 0-2.7451 1.99 1.99 0 0 0 -2.7461-.0005zm1.7558 1.7553a.5533.5533 0 0 1 -.7656 0 .5414.5414 0 1 1 .7656 0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.6481 13.0544a.7523.7523 0 0 0 -.2139-.4346l-4.8166-4.8166a5.5361 5.5361 0 1 0 -2.8157 2.8168l4.816 4.816a.7474.7474 0 0 0 .4346.2134l2.0156.26a.701.701 0 0 0 .0957.0063.7505.7505 0 0 0 .7441-.8457zm-12.8916-4.6298a4.0066 4.0066 0 1 1 5.666 0 4.0127 4.0127 0 0 1 -5.666 0zm10.7412 5.77-4.3971-4.3972c.1268-.1058.2635-.1933.3825-.3123s.2067-.2559.3125-.3828l4.3975 4.3977.1025.7974z"
  }));
};

IconKey.displayName = "IconKey";
IconKey.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconKey"
};
var Key = withIcon(IconKey);

const IconKeyboard = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1594 2.5762h-14.3188a.7.7 0 0 0 -.7.7v9.4473a.7.7 0 0 0 .7.7h14.3188a.7.7 0 0 0 .7-.7v-9.4471a.7.7 0 0 0 -.7-.7002zm-.7 9.4473h-12.9186v-8.0469h12.9184z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "3.7196",
    cy: "5.8834",
    r: ".7916"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "6.5092",
    cy: "5.8834",
    r: ".7916"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "9.2988",
    cy: "5.8834",
    r: ".7916"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "12.0884",
    cy: "5.8834",
    r: ".7916"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "5.175",
    cy: "8.1272",
    r: ".7916"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "7.9646",
    cy: "8.1272",
    r: ".7916"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "10.7543",
    cy: "8.1272",
    r: ".7916"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.44 9.7661h-7.033a.625.625 0 0 0 0 1.25h7.033a.625.625 0 0 0 0-1.25z"
  }));
};

IconKeyboard.displayName = "IconKeyboard";
IconKeyboard.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconKeyboard"
};
var Keyboard = withIcon(IconKeyboard);

const IconLaunchpad = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m.9178.274h2.5754a.6438.6438 0 0 1 .6438.6438v2.5753a.6438.6438 0 0 1 -.6438.6438h-2.5754a.6438.6438 0 0 1 -.6438-.6438v-2.5753a.6438.6438 0 0 1 .6438-.6438z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m.9178 6.0685h2.5754a.6438.6438 0 0 1 .6438.6438v2.5754a.6438.6438 0 0 1 -.6438.6438h-2.5754a.6438.6438 0 0 1 -.6438-.6438v-2.5754a.6438.6438 0 0 1 .6438-.6438z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m.9178 11.863h2.5754a.6438.6438 0 0 1 .6438.6438v2.5753a.6438.6438 0 0 1 -.6438.6438h-2.5754a.6438.6438 0 0 1 -.6438-.6438v-2.5752a.6438.6438 0 0 1 .6438-.6439z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.7123.274h2.5754a.6438.6438 0 0 1 .6438.6438v2.5753a.6438.6438 0 0 1 -.6438.6438h-2.5754a.6438.6438 0 0 1 -.6438-.6438v-2.5753a.6438.6438 0 0 1 .6438-.6438z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.7123 6.0685h2.5754a.6438.6438 0 0 1 .6438.6438v2.5754a.6438.6438 0 0 1 -.6438.6438h-2.5754a.6438.6438 0 0 1 -.6438-.6438v-2.5754a.6438.6438 0 0 1 .6438-.6438z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.7123 11.863h2.5754a.6438.6438 0 0 1 .6438.6438v2.5753a.6438.6438 0 0 1 -.6438.6438h-2.5754a.6438.6438 0 0 1 -.6438-.6438v-2.5752a.6438.6438 0 0 1 .6438-.6439z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.5069.274h2.5753a.6438.6438 0 0 1 .6438.6438v2.5753a.6438.6438 0 0 1 -.6438.6438h-2.5753a.6438.6438 0 0 1 -.6438-.6438v-2.5753a.6438.6438 0 0 1 .6438-.6438z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.5069 6.0685h2.5753a.6438.6438 0 0 1 .6438.6438v2.5754a.6438.6438 0 0 1 -.6438.6438h-2.5753a.6438.6438 0 0 1 -.6438-.6438v-2.5754a.6438.6438 0 0 1 .6438-.6438z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.5069 11.863h2.5753a.6438.6438 0 0 1 .6438.6438v2.5753a.6438.6438 0 0 1 -.6438.6438h-2.5753a.6438.6438 0 0 1 -.6438-.6438v-2.5752a.6438.6438 0 0 1 .6438-.6439z"
  }));
};

IconLaunchpad.displayName = "IconLaunchpad";
IconLaunchpad.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLaunchpad"
};
var Launchpad = withIcon(IconLaunchpad);

const IconLighting = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.6086 7.25h-5.4067l5.937-5.9365a.75.75 0 0 0 -1.0605-1.0606l-7.2173 7.2171a.75.75 0 0 0 .53 1.28h5.407l-5.937 5.9365a.75.75 0 1 0 1.0605 1.0605l7.2173-7.217a.75.75 0 0 0 -.53-1.28z"
  }));
};

IconLighting.displayName = "IconLighting";
IconLighting.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLighting"
};
var Lighting = withIcon(IconLighting);

const IconLink = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8.25 11.0693-2.6753 2.6758a2.4111 2.4111 0 0 1 -3.3193 0 2.35 2.35 0 0 1 0-3.3193l2.6757-2.6758a2.3509 2.3509 0 0 1 3.3193 0 .75.75 0 0 0 1.0606-1.0605 3.8512 3.8512 0 0 0 -5.44 0l-2.6762 2.6757a3.847 3.847 0 0 0 5.44 5.44l2.6762-2.6752a.75.75 0 1 0 -1.061-1.0607z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.8052 1.1963a3.8512 3.8512 0 0 0 -5.44 0l-2.6772 2.6758a.75.75 0 0 0 1.0605 1.0605l2.6768-2.6758a2.3471 2.3471 0 0 1 3.3193 3.3193l-2.6767 2.6759a2.3518 2.3518 0 0 1 -3.3193 0 .75.75 0 0 0 -1.0606 1.0605 3.8512 3.8512 0 0 0 5.44 0l2.6768-2.6758a3.853 3.853 0 0 0 .0004-5.4404z"
  }));
};

IconLink.displayName = "IconLink";
IconLink.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLink"
};
var Link = withIcon(IconLink);

const IconList = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m.7891 2.4619h14.4218a.75.75 0 0 0 0-1.5h-14.4218a.75.75 0 0 0 0 1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.2109 5.1543h-14.4218a.75.75 0 0 0 0 1.5h14.4218a.75.75 0 0 0 0-1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.2109 9.3457h-14.4218a.75.75 0 1 0 0 1.5h14.4218a.75.75 0 0 0 0-1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.2109 13.5381h-14.4218a.75.75 0 0 0 0 1.5h14.4218a.75.75 0 0 0 0-1.5z"
  }));
};

IconList.displayName = "IconList";
IconList.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconList"
};
var List = withIcon(IconList);

const IconListBulleted = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m5.56 3.3535 9.6226-.0029a.75.75 0 0 0 0-1.5l-9.6226.0029a.75.75 0 1 0 0 1.5z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "1.3556",
    cy: "2.5735",
    r: "1.2876"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.1821 7.2774-9.6221.0019a.75.75 0 0 0 0 1.5l9.6226-.002a.75.75 0 0 0 0-1.5z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "1.3556",
    cy: "8",
    r: "1.2877"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.1821 12.7031-9.6221.003a.75.75 0 0 0 0 1.5l9.6226-.0029a.75.75 0 0 0 0-1.5z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "1.3556",
    cy: "13.4265",
    r: "1.2877"
  }));
};

IconListBulleted.displayName = "IconListBulleted";
IconListBulleted.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconListBulleted"
};
var ListBulleted = withIcon(IconListBulleted);

const IconListChecks = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1895 12.627h-8.2695a.75.75 0 1 0 0 1.5h8.269a.75.75 0 0 0 0-1.5z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "3.0315",
    cy: "13.3504",
    r: "1.5143"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.92 4.4707h8.269a.75.75 0 0 0 0-1.5h-8.269a.75.75 0 1 0 0 1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.7222 1.355-2.1734 2.1733-1.208-1.2075a.75.75 0 1 0 -1.0608 1.0606l1.7386 1.7378a.75.75 0 0 0 1.0605 0l2.7036-2.7036a.75.75 0 0 0 -1.0605-1.0606z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.1895 7.8115h-8.2695a.75.75 0 1 0 0 1.5h8.269a.75.75 0 1 0 0-1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.7222 6.1958-2.1734 2.1734-1.208-1.2076a.75.75 0 0 0 -1.0608 1.0606l1.7386 1.7378a.75.75 0 0 0 1.0605 0l2.7036-2.7036a.75.75 0 1 0 -1.0605-1.0606z"
  }));
};

IconListChecks.displayName = "IconListChecks";
IconListChecks.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconListChecks"
};
var ListChecks = withIcon(IconListChecks);

const IconLock = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.7268 6.3958h-2.083v-2.75a3.6527 3.6527 0 0 0 -7.2876.0068v2.7432h-2.083a.75.75 0 0 0 -.75.75v7.8535a.75.75 0 0 0 .75.75h11.4536a.75.75 0 0 0 .75-.75v-7.8535a.75.75 0 0 0 -.75-.75zm-7.8706-2.7437a2.16 2.16 0 0 1 4.2876-.0068v2.75h-4.2876zm7.1206 10.5972h-9.9536v-6.3535h9.9536z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 9.05a.75.75 0 0 0 -.75.75v2.545a.75.75 0 0 0 1.5 0v-2.545a.75.75 0 0 0 -.75-.75z"
  }));
};

IconLock.displayName = "IconLock";
IconLock.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLock"
};
var Lock = withIcon(IconLock);

const IconLogoDrive = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.258 9.786h-4.726l-4.887-8.466h4.726z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.038 14.68 2.35-4.129h9.163l-2.351 4.129z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m2.817 14.603-2.368-4.123 4.523-8.11 2.405 4.235z"
  }));
};

IconLogoDrive.displayName = "IconLogoDrive";
IconLogoDrive.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoDrive"
};
var LogoDrive = withIcon(IconLogoDrive);

const IconLogoFacebook = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8.89 4.5583v-1.1128c0-.64.5731-.8688 1.2133-.8688a5.56 5.56 0 0 1 1.326.1982l.4118-2.4388a10.3689 10.3689 0 0 0 -2.9511-.2961 2.9276 2.9276 0 0 0 -2.5546 1.1985 4.2757 4.2757 0 0 0 -.53 2.4662v1.6h-1.6465v2.3813h1.6462v8.274h3.0849v-8.2741h2.4417l.1829-2.3808h-2.6246z"
  }));
};

IconLogoFacebook.displayName = "IconLogoFacebook";
IconLogoFacebook.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoFacebook"
};
var LogoFacebook = withIcon(IconLogoFacebook);

const IconLogoGmail = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.398 1.6h-12.796a1.6042 1.6042 0 0 0 -1.602 1.6015v9.597a1.6068 1.6068 0 0 0 1.602 1.6015h12.796a1.6068 1.6068 0 0 0 1.602-1.6015v-9.597a1.6043 1.6043 0 0 0 -1.602-1.6015zm0 11.199h-1.597v-7.039l-4.801 3.0385-4.801-3.0385v7.039h-1.597v-9.5975h.957l5.441 3.359 5.441-3.359h.957z"
  }));
};

IconLogoGmail.displayName = "IconLogoGmail";
IconLogoGmail.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoGmail"
};
var LogoGmail = withIcon(IconLogoGmail);

const IconLogoGoldie = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.092 8.7862a1.9006 1.9006 0 0 0 -.4508.0561 5.81 5.81 0 0 0 -4.6-4.2858c-.1-.0181-.1908-.0314-.2928-.0443a1.9049 1.9049 0 0 1 .85-1.7536l-.1284-.3219s-1.573.4888-1.7553 1.8349c-.0784-.3663-.812-.8245-1.1752-.9114l-.1806.2913a1.266 1.266 0 0 1 .5987.9052 5.81 5.81 0 0 0 -4.5988 4.2856 1.8847 1.8847 0 1 0 .2376 3.5819 5.7775 5.7775 0 0 0 .6123 1.139l1.564-1.0544-.0133-.0174a3.853 3.853 0 0 1 -.7094-2.0933 2.787 2.787 0 0 1 .7113-2.2146 2.0953 2.0953 0 0 1 3.1347.3991h.2074a2.0953 2.0953 0 0 1 3.1347-.3991 2.787 2.787 0 0 1 .7113 2.2146 3.853 3.853 0 0 1 -.7091 2.0933l-.0133.0174 1.564 1.0544a5.7786 5.7786 0 0 0 .6123-1.139 1.8842 1.8842 0 1 0 .6884-3.6381zm-12.22 2.44a.556.556 0 0 1 0-1.1121.5608.5608 0 0 1 .3116.0961 8.4468 8.4468 0 0 0 .0593.8735.5563.5563 0 0 1 -.3711.1424zm12.2565 0a.5563.5563 0 0 1 -.3709-.1425 8.4467 8.4467 0 0 0 .0592-.8735.5551.5551 0 0 1 .8673.4611.5547.5547 0 0 1 -.5559.5548z"
  }));
};

IconLogoGoldie.displayName = "IconLogoGoldie";
IconLogoGoldie.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoGoldie"
};
var LogoGoldie = withIcon(IconLogoGoldie);

const IconLogoGoogle = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.06 7.34h-6.8181v2.0054h4.8523c-.2368 2.8126-2.5768 4.0121-4.7856 4.0121a5.2707 5.2707 0 0 1 -5.2919-5.34 5.28 5.28 0 0 1 5.2982-5.374 5.2027 5.2027 0 0 1 3.6139 1.45l1.4044-1.4543a7.14 7.14 0 0 0 -5.0902-2.007 7.49 7.49 0 0 0 -7.425 7.35 7.4656 7.4656 0 0 0 7.532 7.3856 6.4875 6.4875 0 0 0 6.832-6.6983 6.0112 6.0112 0 0 0 -.122-1.3295z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.06 7.339v.001z"
  }));
};

IconLogoGoogle.displayName = "IconLogoGoogle";
IconLogoGoogle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoGoogle"
};
var LogoGoogle = withIcon(IconLogoGoogle);

const IconLogoGooglePlus = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m5.1407 7.2852v1.7155h2.8379a2.7022 2.7022 0 0 1 -2.8379 2.1593 3.16 3.16 0 0 1 0-6.319 2.8159 2.8159 0 0 1 1.9943.772l1.3582-1.3086a4.7965 4.7965 0 0 0 -3.3525-1.3081 5.0037 5.0037 0 1 0 0 10.0075 4.6155 4.6155 0 0 0 4.8036-4.8894 4.6022 4.6022 0 0 0 -.0786-.8292z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.863 7.285h-1.43v-1.429h-1.429v1.429h-1.43v1.43h1.43v1.429h1.429v-1.429h1.43z"
  }));
};

IconLogoGooglePlus.displayName = "IconLogoGooglePlus";
IconLogoGooglePlus.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoGooglePlus"
};
var LogoGooglePlus = withIcon(IconLogoGooglePlus);

const IconLogoLinkedIn = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.4777.3973h-12.9587a1.11 1.11 0 0 0 -1.1215 1.0964v13.0118a1.11 1.11 0 0 0 1.1215 1.0972h12.9587a1.1124 1.1124 0 0 0 1.1248-1.0972v-13.0118a1.1116 1.1116 0 0 0 -1.1248-1.0964zm-9.57 12.9565h-2.2577v-7.2561h2.2579zm-1.1277-8.2475a1.3078 1.3078 0 1 1 1.3069-1.308 1.3072 1.3072 0 0 1 -1.3069 1.308zm9.5747 8.2476h-2.2547v-3.5287c0-.8415-.0145-1.924-1.1719-1.924-1.1735 0-1.3525.9171-1.3525 1.8638v3.5889h-2.2529v-7.2562h2.1617v.9923h.0309a2.3689 2.3689 0 0 1 2.1337-1.1721c2.2835 0 2.7054 1.5024 2.7054 3.4567z"
  }));
};

IconLogoLinkedIn.displayName = "IconLogoLinkedIn";
IconLogoLinkedIn.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoLinkedIn"
};
var LogoLinkedIn = withIcon(IconLogoLinkedIn);

const IconLogoMailChimp = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.8486 7.9624a.4584.4584 0 0 0 -.3658-.1735h-.0028c-.201 0-.3877.1526-.5707.4664l-.0145.025-.0094-.0274c-.0462-.1345-.0874-.2466-.112-.3135-.0171-.0468-.0248-.0677-.0266-.0745a.0739.0739 0 0 0 -.0759-.0551.1356.1356 0 0 0 -.0718.0229.0839.0839 0 0 0 -.0409.0572l-.0012.0036c-.1831.5712-.3342.8487-.4621.8487a.057.057 0 0 1 -.044-.0182c-.0335-.0371-.0253-.1252-.0139-.2472a2.2622 2.2622 0 0 0 .0139-.2986.3017.3017 0 0 0 -.3027-.3325c-.2313 0-.3812.2463-.45.3931l-.0206.0439-.0046-.0483c-.0172-.1817-.0782-.3987-.2708-.4-.2246 0-.3957.2636-.5.4848l-.028.0593.0028-.0655c.0125-.295.0226-.4691.03-.5175a.125.125 0 0 0 -.0123-.1.1.1 0 0 0 -.0848-.0213c-.0719.0046-.1087.0483-.164.195-.0909.241-.3222.8014-.5069.8014a.1105.1105 0 0 1 -.0869-.0371c-.0774-.0889-.0465-.3288-.0036-.661l.0032-.0251c.02-.1573-.0143-.1908-.1-.2065a.1434.1434 0 0 0 -.026-.0026c-.0682 0-.1.0653-.1638.2468-.1133.3231-.2591.6628-.43.6628l-.0187-.0015c-.09-.0153-.0884-.1494-.0859-.3191a1.8166 1.8166 0 0 0 -.0113-.29c-.0254-.1686-.1248-.274-.2593-.275-.2685 0-.45.3438-.5337.5487l-.0191.0464-.0064-.05c-.0113-.0889-.0261-.23-.0326-.3963v-.0049l.0031-.0038a2.6277 2.6277 0 0 0 .7308-1.48c.0019-.25-.1133-.4-.3083-.4014-.1425 0-.4815.1187-.5993 1.2186-.0218.204-.0338.4131-.0356.6216v.004l-.0024.0034c-.23.3327-.72.905-1.2007.905-.434 0-.78-.3511-.78-.9708a1.2485 1.2485 0 0 1 1.0307-1.3404h.0036a.3706.3706 0 0 1 .2385.0776.2629.2629 0 0 1 .0939.2108.3839.3839 0 0 1 -.0743.2306c-.0093.0146-.0185.0292-.027.0447a.0589.0589 0 0 0 -.005.0576.09.09 0 0 0 .0776.0325.3637.3637 0 0 0 .2664-.3822.5147.5147 0 0 0 -.5621-.4883 1.4961 1.4961 0 0 0 -1.2833 1.5743 1.0567 1.0567 0 0 0 1.0167 1.1652 1.6058 1.6058 0 0 0 1.19-.7936l.0223-.03.0016.0375c.0077.1816.0209.3249.0305.4295.0062.0673.0107.1158.0106.1426a.1953.1953 0 0 0 .0294.1244.1215.1215 0 0 0 .1011.0373c.0817 0 .098-.0557.13-.1931l.0091-.0398c.04-.1665.1886-.71.4119-.71a.112.112 0 0 1 .0846.0323c.0493.0523.044.16.0377.2839a1.5841 1.5841 0 0 0 0 .2358c.021.2107.112.3138.2782.315.1707 0 .3487-.1837.4426-.3546l.0178-.0322.0068.0361c.0174.0914.095.3915.3378.3915.22 0 .4089-.2318.6159-.7087l.027-.0624-.0016.0679c-.0044.1808-.0142.4016-.0142.5722 0 .132.0132.1823.0742.1823l.0138.0007c.0586 0 .0983-.035.1181-.1041.1557-.5427.29-.7955.4236-.7955.1306.001.1429.24.1534.6616l.0007.03c.0032.1476.0084.2128.1.2136.07 0 .0869-.0546.1228-.1891.0068-.0257.0144-.0538.0231-.0842.1257-.4363.23-.6143.36-.6143.1111.0008.1173.1154.1193.1531a1.65 1.65 0 0 1 -.012.2465 1.6857 1.6857 0 0 0 -.0122.2269.2463.2463 0 0 0 .2631.2665c.2114 0 .4022-.2472.5693-.7347l.0117-.0339.0132.0334c.03.0765.07.18.1064.304l.0014.0047-.002.0045a2.9569 2.9569 0 0 0 -.2775.9732c-.0022.2817.1252.4647.3246.4662h.0035c.1085 0 .3613-.0435.3661-.4466a2.7759 2.7759 0 0 0 -.1159-.6942l-.009-.0338.0292.0194a.6021.6021 0 0 0 .3284.1034h.0054a.5339.5339 0 0 0 .56-.5507.651.651 0 0 0 -.1314-.4133zm-5.8826-.4024c.0045-.101.013-.1993.0251-.2923.0782-.5991.2415-1.0176.3972-1.0176h.0007c.0742.0006.1114.0567.1105.1668a2.3986 2.3986 0 0 1 -.5095 1.1512l-.0262.0386zm4.9649 2.1541a.1218.1218 0 0 1 -.1025.05.1329.1329 0 0 1 -.1122-.0516c-.071-.095-.051-.3181.0649-.7233.0138-.0483.0289-.097.0447-.1446l.0135-.04.0118.0409c.135.4669.1619.7593.0798.8686zm.6931-1.0641a.36.36 0 0 1 -.2275.0816.5569.5569 0 0 1 -.4237-.2159l-.0028-.0041-.0049-.0155.0023-.0047c.154-.3173.3305-.4993.4841-.4993h.0016c.1734.0014.2933.1574.2916.3793a.35.35 0 0 1 -.1207.2786z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m7.3411 8.5746-.0106.01a.4713.4713 0 0 1 -.2644.1542c-.1343.0066-.2336-.0737-.2978-.2394l-.0027-.007.0044-.0057a2.99 2.99 0 0 0 .6029-1.7143c-.0118-.2288-.0784-.5015-.3282-.5015h-.0187a.3691.3691 0 0 0 -.2587.1356 2.5312 2.5312 0 0 0 -.3091 1.6265 1.6361 1.6361 0 0 0 .0781.42l.0024.0071-.0049.0057a.5851.5851 0 0 1 -.3807.2484c-.2611 0-.2523-.4376-.2316-.8007.0045-.0782.0111-.1281-.0121-.1564a.104.104 0 0 0 -.0846-.0306h-.0048c-.0928 0-.1122.0191-.16.1569-.0988.2864-.294.767-.49.767a.15.15 0 0 1 -.1244-.0553c-.0952-.1216-.023-.4248.0352-.6685.01-.0438.0205-.0859.029-.1248a.15.15 0 0 0 -.0116-.1254.1313.1313 0 0 0 -.1153-.0314c-.0841.0067-.1379.0419-.1638.2018l-.0052.0333-.02-.0237a.2745.2745 0 0 0 -.2412-.1188l-.0356.0024a.6553.6553 0 0 0 -.4215.2579.8632.8632 0 0 0 -.1856.6137l.0044.0345.001.0062-.0043.0047a.3506.3506 0 0 1 -.25.1356c-.1287-.001-.2025-.0967-.2025-.2559 0-.2681.1816-1.0761.1816-1.3686 0-.3113-.1387-.4808-.38-.4827h-.0032c-.3517 0-.6259.3992-.8882 1.22-.024.0752-.05.1621-.0726.2372l-.0368.1235.0106-.1283c.0243-.2861.0411-.5644.05-.8273a1.1083 1.1083 0 0 0 -.164-.7805.3566.3566 0 0 0 -.2693-.1043h-.0031c-.43 0-.6367.7259-.7868 1.2382-.0344.1175-.0942.3466-.1471.5547.032-.3469.0908-.9671.0863-1.3306-.0054-.438-.1512-.6343-.4726-.6368h-.004a.4978.4978 0 0 0 -.3606.157.6256.6256 0 0 0 -.1777.4279c.0023.0881.0565.1666.0975.1666.0267 0 .0415-.0356.0479-.0569.0994-.3288.2281-.4887.3935-.4887a.2049.2049 0 0 1 .1577.0617c.129.1385.1056.5123.063 1.191-.0137.2188-.0293.4665-.0424.7485-.0023.0483-.0052.0952-.0081.14-.0126.1958-.0225.35.0518.362a.157.157 0 0 0 .1916-.12c.0477-.1419.1547-.53.3618-1.2383.1987-.68.3489-.9275.5409-.9275.2219 0 .2385.381.1978 1.1851-.0087.17-.02.3635-.0292.5736l-.004.0857c-.0085.1739-.0153.3113.0344.3639a.0847.0847 0 0 0 .0645.0247c.0962 0 .1251-.0843.173-.2241.0084-.0247.0175-.051.0275-.0787.33-.9093.5733-1.6981.9-1.6981.0981 0 .1744.08.1744.2352 0 .3588-.1754 1.0521-.1754 1.4035a.4846.4846 0 0 0 .1047.3184.367.367 0 0 0 .2844.1259h.0025a.5171.5171 0 0 0 .3391-.158l.0108-.01.0087.0117a.3716.3716 0 0 0 .3314.147.5606.5606 0 0 0 .4373-.3675l.0125-.0334.0124.0334a.3511.3511 0 0 0 .3671.2617c.2377-.0191.4263-.3424.4964-.48l.0181-.0356.0068.0394c.0336.1943.1393.5208.47.5232h.0027a.7113.7113 0 0 0 .4692-.2529l.012-.0121.0087.0147a.4748.4748 0 0 0 .4161.2746.3943.3943 0 0 0 .3854-.2908.0964.0964 0 0 0 -.0212-.075.0489.0489 0 0 0 -.0733-.0054zm-2.5731-.4751c-.0712.2864-.2076.6319-.4536.6517l-.02.0008c-.102 0-.1592-.0668-.17-.1986a.6838.6838 0 0 1 .1811-.4922.3565.3565 0 0 1 .25-.1324h.0017a.2456.2456 0 0 1 .2105.1622l.0018.0041zm1.9349.1343-.0049-.0376c-.0144-.11-.0215-.2237-.0265-.3209-.0354-.6851.1718-1.3675.3524-1.3769h.0035a.0747.0747 0 0 1 .0606.0346 1.4766 1.4766 0 0 1 -.0569.9608 3.2342 3.2342 0 0 1 -.3085.7076z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.9066 7.3713.0013.0132v-.0132a.1631.1631 0 1 0 -.0013 0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.6161 7.3713.0013.0132v-.0132a.163.163 0 1 0 -.0013 0z"
  }));
};

IconLogoMailChimp.displayName = "IconLogoMailChimp";
IconLogoMailChimp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoMailChimp"
};
var LogoMailChimp = withIcon(IconLogoMailChimp);

const IconLogoMessenger = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .3147a7.3851 7.3851 0 0 0 -7.5824 7.1707 7.0108 7.0108 0 0 0 2.7448 5.5216v2.6783l2.5813-1.3521a7.9818 7.9818 0 0 0 2.2563.3228 7.3851 7.3851 0 0 0 7.5824-7.1707 7.3851 7.3851 0 0 0 -7.5824-7.1706zm.7891 9.6753-1.99-2.1272-3.8426 2.0585 4.1171-4.3913 1.99 1.99 3.8426-2.0586z"
  }));
};

IconLogoMessenger.displayName = "IconLogoMessenger";
IconLogoMessenger.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoMessenger"
};
var LogoMessenger = withIcon(IconLogoMessenger);

const IconLogoOffice = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m9.879.16-8.434 3.256v9.406l.004.001 3.018-1.259v-7.409l5.464-1.261v11.034l-8.472-1.101 8.42 3.013 4.676-1.509v-12.596z"
  }));
};

IconLogoOffice.displayName = "IconLogoOffice";
IconLogoOffice.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoOffice"
};
var LogoOffice = withIcon(IconLogoOffice);

const IconLogoOutlook = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.9909 7.834a1.5044 1.5044 0 0 1 -2.075-.2048v4.0976h5.1559s.7546-.1049.6917-1.0947l.0037-5.3533z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.7254 3.9976a.571.571 0 0 0 -.6116-.6243h-5.1979v3.046l1.1108.7081z"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "4.7894",
    cy: "7.8613",
    rx: ".998",
    ry: "1.6371"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m.2328 14.0021 9.1172 1.7864v-15.577l-9.1172 1.5732zm4.581-8.8994c1.1464 0 2.0757 1.237 2.0757 2.763s-.9294 2.763-2.0757 2.763-2.0759-1.2369-2.0759-2.7629.9294-2.763 2.0758-2.763z"
  }));
};

IconLogoOutlook.displayName = "IconLogoOutlook";
IconLogoOutlook.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoOutlook"
};
var LogoOutlook = withIcon(IconLogoOutlook);

const IconLogoPinterest = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .16a7.84 7.84 0 0 0 -3.147 15.0206 6.8852 6.8852 0 0 1 .1365-1.8c.1507-.6365 1.0088-4.2719 1.0088-4.2719a3.0115 3.0115 0 0 1 -.25-1.24c0-1.1618.6734-2.0295 1.512-2.0295a1.05 1.05 0 0 1 1.0576 1.177 16.8753 16.8753 0 0 1 -.6929 2.7815 1.2134 1.2134 0 0 0 1.2374 1.51c1.4854 0 2.4858-1.9078 2.4858-4.1682a2.9349 2.9349 0 0 0 -3.2622-3.0044 3.7124 3.7124 0 0 0 -3.8597 3.7549 2.2633 2.2633 0 0 0 .5168 1.5377.38.38 0 0 1 .1127.437c-.0376.1443-.124.4915-.16.6292a.27.27 0 0 1 -.3925.1962 3.0194 3.0194 0 0 1 -1.6052-2.9957c0-2.227 1.8783-4.8975 5.6031-4.8975a4.6858 4.6858 0 0 1 4.9632 4.491c0 3.0754-1.71 5.3731-4.23 5.3731a2.2482 2.2482 0 0 1 -1.9153-.9772s-.4553 1.8062-.5517 2.1552a6.5628 6.5628 0 0 1 -.7891 1.6795 7.84 7.84 0 1 0 2.2217-15.3585z"
  }));
};

IconLogoPinterest.displayName = "IconLogoPinterest";
IconLogoPinterest.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoPinterest"
};
var LogoPinterest = withIcon(IconLogoPinterest);

const IconLogoTwitter = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.6 3.2859a6.2331 6.2331 0 0 1 -1.791.491 3.1278 3.1278 0 0 0 1.371-1.7252 6.2435 6.2435 0 0 1 -1.98.7567 3.1214 3.1214 0 0 0 -5.314 2.8443 8.8531 8.8531 0 0 1 -6.4278-3.2583 3.1219 3.1219 0 0 0 .9651 4.1634 3.1063 3.1063 0 0 1 -1.4125-.39v.0393a3.12 3.12 0 0 0 2.5012 3.0577 3.1237 3.1237 0 0 1 -1.4083.0535 3.1214 3.1214 0 0 0 2.9131 2.1656 6.2564 6.2564 0 0 1 -3.8729 1.335 6.328 6.328 0 0 1 -.7439-.0437 8.8264 8.8264 0 0 0 4.78 1.4011 8.8122 8.8122 0 0 0 8.873-8.8727q0-.2028-.009-.4034a6.3362 6.3362 0 0 0 1.556-1.6143z"
  }));
};

IconLogoTwitter.displayName = "IconLogoTwitter";
IconLogoTwitter.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoTwitter"
};
var LogoTwitter = withIcon(IconLogoTwitter);

const IconLogoWhatsApp = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8.1135.16a7.6428 7.6428 0 0 0 -7.6428 7.6428 8.8145 8.8145 0 0 0 1.1093 3.9672l-1.3363 4.07 4.1878-1.3383a7.7472 7.7472 0 0 0 3.682.9438 7.6428 7.6428 0 1 0 0-15.2855zm0 14.0628a7.0167 7.0167 0 0 1 -3.5381-1.0621l-2.4139.7877.7833-2.3371a7.2986 7.2986 0 0 1 -1.2512-3.8085 6.42 6.42 0 1 1 6.42 6.42z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.87 9.419s-1.2522-.6065-1.51-.7242a.3.3 0 0 0 -.3883.0742s-.38.48-.5018.637-.1919.2967-.51.192a4.9275 4.9275 0 0 1 -1.4791-.9163 6.1161 6.1161 0 0 1 -1.0341-1.274.2356.2356 0 0 1 .0175-.3055s.3306-.3807.4887-.5847a.503.503 0 0 0 .0219-.5236c-.0378-.092-.5454-1.2847-.62-1.4529a.3423.3423 0 0 0 -.2356-.1964h-.6019a.8961.8961 0 0 0 -.3141.1309 2.1144 2.1144 0 0 0 -.7416 1.52 2.5162 2.5162 0 0 0 .29 1.3361 9.6893 9.6893 0 0 0 2.11 2.6 7.3472 7.3472 0 0 0 2.9535 1.442 2.2844 2.2844 0 0 0 1.876-.5889 1.3689 1.3689 0 0 0 .34-1.1692.2035.2035 0 0 0 -.1611-.1965z"
  }));
};

IconLogoWhatsApp.displayName = "IconLogoWhatsApp";
IconLogoWhatsApp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoWhatsApp"
};
var LogoWhatsApp = withIcon(IconLogoWhatsApp);

const IconLogoYahooMail = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m4.62 7.8574c.0776-.01.2133-.0291.2424-.0291a1.6112 1.6112 0 0 1 .2327.0291c-.4849-.97-.8632-1.9495-1.2415-2.9-.0679.01-.1454.029-.1745.029a.9247.9247 0 0 1 -.165-.0388c-.3976.97-.7661 1.94-1.2414 2.91a1.4689 1.4689 0 0 1 .2327-.0291 1.87 1.87 0 0 1 .2425.0291l.3491-.8535h1.1737c.1163.281.2327.572.3493.8533zm-1.4453-1.0574.5045-1.3288.5139 1.3288z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m2.5248 5.2677c.1358-.2424.2911-.4655.4365-.6984a1.431 1.431 0 0 1 -.2424.0292 1.3726 1.3726 0 0 1 -.2232-.0292l-.9893 1.61-.96-1.61a1.7778 1.7778 0 0 1 -.2521.0292 1.4577 1.4577 0 0 1 -.2327-.0292c.446.6887.8632 1.4355 1.2511 2.1339l-.0387 1.542a1.4744 1.4744 0 0 1 .2327-.029 1.4457 1.4457 0 0 1 .2424.029l-.0291-1.5614c.2617-.4753.5236-.9602.8048-1.4161z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.9386 4.8313a.2224.2224 0 0 0 -.2521-.223c-.32 0-.2038.3879-.2523.543-.01.0776-.01.1552-.0193.2231-.01.0776-.01.1553-.0194.2329l-.0291.3491c-.01.1067-.0194.223-.029.33-.0388.3784-.0873.7565-.1358 1.1446a.5488.5488 0 0 1 .1746.0291c.0968-.7472.5624-2.5124.5624-2.6288z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.7546 7.8574-.029-1.3481h1.62l-.0295 1.3481a1.4725 1.4725 0 0 1 .2329-.0291 1.3516 1.3516 0 0 1 .2328.0291v-2.9a1.4748 1.4748 0 0 1 -.2328.0291 1.8713 1.8713 0 0 1 -.2329-.0388.03.03 0 0 0 -.01.0194l.0389 1.3384h-1.62l.029-1.3578a1.7047 1.7047 0 0 1 -.2424.0388 1.866 1.866 0 0 1 -.2329-.0388v2.91a1.6112 1.6112 0 0 1 .2329-.0291 1.5585 1.5585 0 0 1 .243.0288z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.07 6.4122a1.4213 1.4213 0 0 0 -1.5715-1.4936 1.4206 1.4206 0 0 0 -1.5616 1.3772v.2329a2.0192 2.0192 0 0 0 .1163.5334 1.454 1.454 0 0 0 1.4453.8341 1.414 1.414 0 0 0 1.5715-1.484zm-1.5715 1.3191a1.1088 1.1088 0 0 1 -1.0766-1.2313v-.1751c.0388-.8049.4364-1.2221 1.0766-1.2221.8632 0 1.0862.6984 1.0862 1.3093 0 .6209-.2424 1.3192-1.0862 1.3192z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.182 7.7895a.2114.2114 0 0 0 -.223.2327c0 .1649.1164.223.2521.223a.207.207 0 0 0 .223-.2327.2265.2265 0 0 0 -.2521-.223z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.058 4.744a1.6328 1.6328 0 0 0 -1.804 1.4742v.3977a2.8436 2.8436 0 0 0 .1261.514 1.7024 1.7024 0 0 0 1.6779.9408 1.6087 1.6087 0 0 0 1.8137-1.6585 1.627 1.627 0 0 0 -1.8137-1.6682zm0 3.1521a1.2329 1.2329 0 0 1 -1.2415-.8729c0-.01-.0776-.3685-.0776-.4364v-.359a1.2332 1.2332 0 0 1 1.3191-1.3 1.2944 1.2944 0 0 1 1.3287 1.4742 1.3125 1.3125 0 0 1 -1.3287 1.4941z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.53 10.911h-.018l-.869-1.713h-.383v2.233h.166v-1.816h.025l.926 1.816h.077l.915-1.829h.013v1.829h.376v-2.233h-.376z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8.1224 9.1475-1.0754 2.2832h.1876l.3524-.7451h.9568l.3414.7451h.3813l-1.0514-2.2832zm-.47 1.394.4165-.883.4055.883z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m9.5546 9.1985h.3751v2.2322h-.3751z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m10.866 9.198h-.376v2.233h1.499v-.144h-1.123z"
  }));
};

IconLogoYahooMail.displayName = "IconLogoYahooMail";
IconLogoYahooMail.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoYahooMail"
};
var LogoYahooMail = withIcon(IconLogoYahooMail);

const IconLogoYouTube = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.5361 4.1894a1.9764 1.9764 0 0 0 -1.3906-1.4 46.7 46.7 0 0 0 -6.1455-.3305 46.7 46.7 0 0 0 -6.1454.3311 1.9764 1.9764 0 0 0 -1.3906 1.4 20.7343 20.7343 0 0 0 -.3287 3.81 20.7338 20.7338 0 0 0 .3287 3.8105 1.9764 1.9764 0 0 0 1.3906 1.4 46.7 46.7 0 0 0 6.1454.3306 46.7 46.7 0 0 0 6.1455-.3309 1.9764 1.9764 0 0 0 1.3906-1.4 20.7338 20.7338 0 0 0 .3286-3.8102 20.7341 20.7341 0 0 0 -.3286-3.8106zm-9.0893 6.1377v-4.6542l4.0907 2.3271z"
  }));
};

IconLogoYouTube.displayName = "IconLogoYouTube";
IconLogoYouTube.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconLogoYouTube"
};
var LogoYouTube = withIcon(IconLogoYouTube);

const IconMegaphone = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.0146 1.6543a.7.7 0 0 0 -.7.7v.7484l-10.9982 2.0173a.6936.6936 0 0 0 -.4283.2687l-1.685-.5513a.7.7 0 0 0 -.918.665v4.7251a.699.699 0 0 0 .918.665l1.6849-.5514a.6936.6936 0 0 0 .4283.2687l.9512.1744v1.8978a.7005.7005 0 0 0 .5762.689l5.3506.9634a.71.71 0 0 0 .124.0112.7.7 0 0 0 .7-.7v-1.623l3.2959.6045v.7484a.7.7 0 0 0 1.4 0v-11.021a.7.7 0 0 0 -.6996-.7002zm-5.3964 11.1538-3.95-.7114v-1.0318l3.95.7114zm-7.9326-6.34 1.0566.3457v2.1022l-1.0566.3457zm2.457 2.87v-2.946l10.1719-1.8662v6.6782z"
  }));
};

IconMegaphone.displayName = "IconMegaphone";
IconMegaphone.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconMegaphone"
};
var Megaphone = withIcon(IconMegaphone);

const IconMenu = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m.8555 3.9287h14.289a.75.75 0 0 0 0-1.5h-14.289a.75.75 0 0 0 0 1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.1445 7.25h-14.289a.75.75 0 0 0 0 1.5h14.289a.75.75 0 0 0 0-1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.1445 12.0713h-14.289a.75.75 0 1 0 0 1.5h14.289a.75.75 0 0 0 0-1.5z"
  }));
};

IconMenu.displayName = "IconMenu";
IconMenu.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconMenu"
};
var Menu = withIcon(IconMenu);

const IconMinus = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.2012 7.25h-14.4024a.75.75 0 0 0 0 1.5h14.4024a.75.75 0 0 0 0-1.5z"
  }));
};

IconMinus.displayName = "IconMinus";
IconMinus.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconMinus"
};
var Minus = withIcon(IconMinus);

const IconMinusCircle = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .03a7.97 7.97 0 1 0 7.9707 7.97 7.9794 7.9794 0 0 0 -7.9707-7.97zm0 14.44a6.47 6.47 0 1 1 6.4707-6.47 6.4781 6.4781 0 0 1 -6.4707 6.47z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.3569 7.25h-6.7133a.75.75 0 0 0 0 1.5h6.7134a.75.75 0 1 0 0-1.5z"
  }));
};

IconMinusCircle.displayName = "IconMinusCircle";
IconMinusCircle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconMinusCircle"
};
var MinusCircle = withIcon(IconMinusCircle);

const IconMobile = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.4248.0893h-6.85a1.7021 1.7021 0 0 0 -1.7 1.7v12.4207a1.7021 1.7021 0 0 0 1.7 1.7h6.85a1.7021 1.7021 0 0 0 1.7-1.7v-12.42a1.7021 1.7021 0 0 0 -1.7-1.7007zm-6.85 1.4h6.85a.3.3 0 0 1 .3.3v.9189h-7.4494v-.9182a.3.3 0 0 1 .2998-.3zm7.1494 2.6191v6.6826h-7.4488v-6.6821zm-.3 10.4014h-6.85a.3.3 0 0 1 -.3-.3v-2.0179h7.4492v2.0181a.3005.3005 0 0 1 -.2986.3z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8.8906 12.647h-1.7806a.7.7 0 1 0 0 1.4h1.78a.7.7 0 1 0 0-1.4z"
  }));
};

IconMobile.displayName = "IconMobile";
IconMobile.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconMobile"
};
var Mobile = withIcon(IconMobile);

const IconMore = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("circle", {
    cx: "1.9236",
    cy: "8",
    r: "1.8436"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "8",
    cy: "8",
    r: "1.8436"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "14.0764",
    cy: "8",
    r: "1.8436"
  }));
};

IconMore.displayName = "IconMore";
IconMore.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconMore"
};
var More = withIcon(IconMore);

const IconMoreAndroid = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("circle", {
    cx: "8",
    cy: "1.9236",
    r: "1.8436"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "8",
    cy: "8",
    r: "1.8436"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "8",
    cy: "14.0764",
    r: "1.8436"
  }));
};

IconMoreAndroid.displayName = "IconMoreAndroid";
IconMoreAndroid.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconMoreAndroid"
};
var MoreAndroid = withIcon(IconMoreAndroid);

const IconMove = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("g", {
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M2 12.997a1.959 1.959 0 110 3.918 1.959 1.959 0 010-3.918zm0-6.456a1.959 1.959 0 110 3.918A1.959 1.959 0 012 6.54zM2 .085a1.959 1.959 0 110 3.918A1.959 1.959 0 012 .085zM9 12.997a1.959 1.959 0 110 3.918 1.959 1.959 0 010-3.918zm0-6.456a1.959 1.959 0 110 3.918A1.959 1.959 0 019 6.54zM9 .085a1.959 1.959 0 110 3.918A1.959 1.959 0 019 .085z"
  })));
};

IconMove.displayName = "IconMove";
IconMove.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconMove"
};
var Move = withIcon(IconMove);

const IconNews = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m2.4427 15.1711.6325.003h10.4922a2.3733 2.3733 0 0 0 2.3706-2.3706v-11.2276a.75.75 0 0 0 -.75-.75h-11.1543a.75.75 0 0 0 -.75.75v2.8872h-2.4717a.75.75 0 0 0 -.75.75v7.59a2.3711 2.3711 0 0 0 2.3315 2.3669c.0069 0 .0126.004.02.004l.01-.0009.0092.0009.0156-.0031m-.0253-1.4988a.8706.8706 0 0 1 -.8608-.8687v-6.84h1.7215v6.84a.8706.8706 0 0 1 -.8609.8687zm2.3607-11.3462h9.6543v10.4776a.8717.8717 0 0 1 -.8706.8706h-8.9492a2.3564 2.3564 0 0 0 .1655-.8706z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.9346 8.6965h5.4009a.75.75 0 0 0 0-1.5h-5.4009a.75.75 0 0 0 0 1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.9346 11.7234h5.4009a.75.75 0 0 0 0-1.5h-5.4009a.75.75 0 0 0 0 1.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.9346 5.7068h5.4009a.75.75 0 0 0 0-1.5h-5.4009a.75.75 0 0 0 0 1.5z"
  }));
};

IconNews.displayName = "IconNews";
IconNews.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNews"
};
var News = withIcon(IconNews);

const IconNoWifi = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.1025 7.7486a.7366.7366 0 0 1 -.5231-.2167 6.4767 6.4767 0 0 0 -9.1589 0 .74.74 0 0 1 -1.0461-1.0462 7.9558 7.9558 0 0 1 11.2511 0 .74.74 0 0 1 -.5231 1.2629z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.1466 4.5861a.7366.7366 0 0 1 -.5231-.2167 9.3674 9.3674 0 0 0 -13.2471 0 .74.74 0 0 1 -1.0464-1.0461 10.8465 10.8465 0 0 1 15.3393 0 .74.74 0 0 1 -.5231 1.2629z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.534 15.8536a.74.74 0 0 1 -.5231-1.2629l4.932-4.932a.74.74 0 0 1 1.0461 1.0461l-4.932 4.932a.7366.7366 0 0 1 -.523.2168z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m10.466 15.8536a.7366.7366 0 0 1 -.5231-.2167l-4.932-4.932a.74.74 0 0 1 1.0462-1.0461l4.932 4.932a.74.74 0 0 1 -.5231 1.2629z"
  }));
};

IconNoWifi.displayName = "IconNoWifi";
IconNoWifi.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNoWifi"
};
var NoWifi = withIcon(IconNoWifi);

const IconNumber1 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.8181 7.8181 7.818 7.818 0 0 0 -7.8181-7.8181zm.9717 11.631h-1.4375v-6.2099l-1.7242.4258v-1.2764l3.1621-.7481z"
  }));
};

IconNumber1.displayName = "IconNumber1";
IconNumber1.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber1"
};
var Number1 = withIcon(IconNumber1);

const IconNumber10 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm-2.1058 11.3649h-1.375v-5.94l-1.65.4063v-1.2212l3.025-.7149zm4.2129.1318c-1.87 0-2.8935-1.6611-2.8935-3.8066 0-2.1562 1.0234-3.8066 2.8935-3.8066s2.8935 1.65 2.8935 3.8066c0 2.1455-1.0234 3.8066-2.8935 3.8066z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m10.1071 5.2206c-1.0674 0-1.5185 1.1006-1.5185 2.6514s.4512 2.6514 1.5185 2.6514c1.0781 0 1.5293-1.1 1.5293-2.6514s-.4512-2.6514-1.5293-2.6514z"
  }));
};

IconNumber10.displayName = "IconNumber10";
IconNumber10.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber10"
};
var Number10 = withIcon(IconNumber10);

const IconNumber2 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm2.8063 11.6314h-5.3955v-1.0585c2.1972-1.6797 3.7617-2.7608 3.7617-4.2569a1.1908 1.1908 0 0 0 -1.2425-1.3457 1.1447 1.1447 0 0 0 -1.1852 1.2188 1.3668 1.3668 0 0 0 .23.748l-1.1963.4834a2.0961 2.0961 0 0 1 -.3916-1.3115 2.3853 2.3853 0 0 1 2.5893-2.3009 2.3743 2.3743 0 0 1 2.623 2.4736c0 1.7715-1.875 3.2207-3.2441 4.1182h3.4512z"
  }));
};

IconNumber2.displayName = "IconNumber2";
IconNumber2.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber2"
};
var Number2 = withIcon(IconNumber2);

const IconNumber3 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm-.1981 11.7692a2.5763 2.5763 0 0 1 -2.7842-1.99l1.2315-.4717a1.49 1.49 0 0 0 1.542 1.3467 1.2383 1.2383 0 0 0 1.3915-1.2434c0-.7588-.5752-1.1728-1.4375-1.1728h-.7832v-1.0245h.7256c.8975 0 1.2881-.4717 1.2881-1.208a1.0614 1.0614 0 0 0 -1.1621-1.0474 1.3524 1.3524 0 0 0 -1.3574 1.2544l-1.208-.4834a2.5238 2.5238 0 0 1 2.6118-1.921c1.5185 0 2.4853.794 2.4853 2.082a1.6881 1.6881 0 0 1 -1.4614 1.7833v.0234a1.7685 1.7685 0 0 1 1.668 1.7949c0 1.4264-1.208 2.2775-2.75 2.2775z"
  }));
};

IconNumber3.displayName = "IconNumber3";
IconNumber3.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber3"
};
var Number3 = withIcon(IconNumber3);

const IconNumber4 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m5.917 8.569v.046h2.186v-2.876h-.046z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm2.9562 9.48h-1.4727v2.1514h-1.3808v-2.1513h-3.4278v-1.1045l3.417-4.43h1.3916v4.4876h1.4727z"
  }));
};

IconNumber4.displayName = "IconNumber4";
IconNumber4.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber4"
};
var Number4 = withIcon(IconNumber4);

const IconNumber5 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm-.03 11.7692a2.613 2.613 0 0 1 -2.7838-2.0362l1.2538-.4482a1.5052 1.5052 0 0 0 1.53 1.3574 1.3438 1.3438 0 0 0 1.4037-1.4961 1.2671 1.2671 0 0 0 -1.3-1.4033 1.3677 1.3677 0 0 0 -1.0586.4482h-1.1619l.2068-4.2451h4.292v1.208h-3.1756l-.1035 2.0254a2.1587 2.1587 0 0 1 1.3691-.4951 2.2726 2.2726 0 0 1 2.3242 2.4394 2.5983 2.5983 0 0 1 -2.7962 2.6456z"
  }));
};

IconNumber5.displayName = "IconNumber5";
IconNumber5.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber5"
};
var Number5 = withIcon(IconNumber5);

const IconNumber6 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8.025 7.994a1.3285 1.3285 0 0 0 -1.3691 1.415 1.329 1.329 0 0 0 1.3691 1.415 1.3378 1.3378 0 0 0 1.4033-1.415 1.3373 1.3373 0 0 0 -1.4033-1.415z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm.0826 11.7691a2.6606 2.6606 0 0 1 -2.3359-1.2187 4.5691 4.5691 0 0 1 -.6787-2.5664c0-2.4619 1.2539-4.1759 3.2676-4.1759a2.4 2.4 0 0 1 2.3809 1.4492l-1.0117.667a1.4185 1.4185 0 0 0 -1.3809-.9775c-1.2422 0-1.9326 1.22-1.9326 2.8418v.1953h.0342a1.9914 1.9914 0 0 1 1.9101-1.2658 2.3065 2.3065 0 0 1 2.4044 2.4514 2.5032 2.5032 0 0 1 -2.6574 2.5997z"
  }));
};

IconNumber6.displayName = "IconNumber6";
IconNumber6.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber6"
};
var Number6 = withIcon(IconNumber6);

const IconNumber7 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm2.8854 5.0045-2.6924 6.627h-1.4961l2.6924-6.5h-2.75v1.23h-1.2422v-2.4156h5.4883z"
  }));
};

IconNumber7.displayName = "IconNumber7";
IconNumber7.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber7"
};
var Number7 = withIcon(IconNumber7);

const IconNumber8 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m7.9518 8.4081c-.8518 0-1.4268.4482-1.4268 1.2419s.575 1.2317 1.4268 1.2317c.8633 0 1.4385-.4375 1.4385-1.2315s-.5752-1.2421-1.4385-1.2421z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m7.9518 7.3378a1.119 1.119 0 0 0 1.2539-1.1621 1.1052 1.1052 0 0 0 -1.2539-1.15 1.096 1.096 0 0 0 -1.2418 1.15 1.11 1.11 0 0 0 1.2418 1.1621z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm-.0482 11.7692c-1.7139 0-2.8418-.8511-2.8418-2.1973a1.957 1.957 0 0 1 1.542-1.875v-.0235a1.7811 1.7811 0 0 1 -1.2891-1.7832c0-1.22 1.0127-2.082 2.5889-2.082 1.5879 0 2.6006.8623 2.6006 2.082a1.7811 1.7811 0 0 1 -1.2891 1.7833v.0234a1.957 1.957 0 0 1 1.542 1.875c0 1.3462-1.1279 2.1973-2.8535 2.1973z"
  }));
};

IconNumber8.displayName = "IconNumber8";
IconNumber8.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber8"
};
var Number8 = withIcon(IconNumber8);

const IconNumber9 = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .1819a7.8181 7.8181 0 1 0 7.818 7.8181 7.818 7.818 0 0 0 -7.818-7.8181zm-.3129 11.7692a2.4015 2.4015 0 0 1 -2.3818-1.4492l1.0127-.667a1.4173 1.4173 0 0 0 1.38.9775c1.2432 0 1.9336-1.22 1.9336-2.8418v-.1953h-.0353a1.99 1.99 0 0 1 -1.9092 1.2656 2.3067 2.3067 0 0 1 -2.4052-2.4509 2.504 2.504 0 0 1 2.6581-2.6 2.6587 2.6587 0 0 1 2.335 1.22 4.5579 4.5579 0 0 1 .6787 2.5654c0 2.4618-1.2537 4.1757-3.2666 4.1757z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m7.9977 5.1171a1.3381 1.3381 0 0 0 -1.4043 1.415 1.3383 1.3383 0 0 0 1.4043 1.416 1.33 1.33 0 0 0 1.3691-1.416 1.33 1.33 0 0 0 -1.3691-1.415z"
  }));
};

IconNumber9.displayName = "IconNumber9";
IconNumber9.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconNumber9"
};
var Number9 = withIcon(IconNumber9);

const IconParagraph = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.0029.1411h-8.3955a4.36 4.36 0 0 0 0 8.7207h1.439v6.2471a.75.75 0 0 0 1.5 0v-13.4678h2.1685v13.4678a.75.75 0 0 0 1.5 0v-13.4678h1.7881a.75.75 0 1 0 0-1.5zm-6.9565 7.2207h-1.439a2.86 2.86 0 1 1 0-5.7207h1.439z"
  }));
};

IconParagraph.displayName = "IconParagraph";
IconParagraph.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconParagraph"
};
var Paragraph = withIcon(IconParagraph);

const IconPencilBox = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m12.1787 8.1489a.75.75 0 0 0 -.75.75v5.3462h-9.8467v-9.8467h5.9629a.75.75 0 0 0 0-1.5h-6.7129a.75.75 0 0 0 -.75.75v11.3467a.75.75 0 0 0 .75.75h11.3467a.75.75 0 0 0 .75-.75v-6.0962a.75.75 0 0 0 -.75-.75z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.6982 2.6489-2.1738-2.1743a.75.75 0 0 0 -1.0605 0l-1.64 1.64-.0015.001-.001.0015-4.8458 4.8458a.75.75 0 0 0 -.22.53v2.1746a.75.75 0 0 0 .75.75h2.1744a.7494.7494 0 0 0 .53-.22l4.8462-4.8462.001-.0005.0014-.0019 1.64-1.64a.75.75 0 0 0 -.0004-1.06zm-2.7041-.5835 1.1133 1.1138-.58.58-1.114-1.113zm-4.625 6.8521h-1.1132v-1.1138l4.097-4.097 1.1136 1.1133z"
  }));
};

IconPencilBox.displayName = "IconPencilBox";
IconPencilBox.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconPencilBox"
};
var PencilBox = withIcon(IconPencilBox);

const IconPencilPaper = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m1.95 12.915h4.0085a.7033.7033 0 0 0 .4951-.2051l.5527-.5527.0029-.002.0019-.0029 4.8662-4.8662.0029-.0019.002-.0029 1.9024-1.9023a.7.7 0 0 0 0-.99l-4.0087-4.009a.7.7 0 0 0 -.99 0l-1.9026 1.9022-.0029.002-.0019.0029-4.8663 4.8662-.0029.0019-.002.0029-.5527.5527a.7033.7033 0 0 0 -.2051.4951v4.0088a.7.7 0 0 0 .7005.7003zm.7-4.1289 2.7285 2.7285h-2.7285zm4.726-5.0161 3.0185 3.0186-3.8808 3.8808-3.0186-3.0185zm1.9048-1.9048 3.0185 3.0185-.9145.9146-3.0186-3.0183z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.05 14.4248h-12.1a.7.7 0 1 0 0 1.4h12.1a.7.7 0 1 0 0-1.4z"
  }));
};

IconPencilPaper.displayName = "IconPencilPaper";
IconPencilPaper.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconPencilPaper"
};
var PencilPaper = withIcon(IconPencilPaper);

const IconPlus = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.2012 7.25h-6.4512v-6.4512a.75.75 0 0 0 -1.5 0v6.4512h-6.4512a.75.75 0 0 0 0 1.5h6.4512v6.4512a.75.75 0 0 0 1.5 0v-6.4512h6.4512a.75.75 0 0 0 0-1.5z"
  }));
};

IconPlus.displayName = "IconPlus";
IconPlus.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconPlus"
};
var Plus = withIcon(IconPlus);

const IconPlusCircle = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 .03a7.97 7.97 0 1 0 7.9707 7.97 7.979 7.979 0 0 0 -7.9707-7.97zm0 14.44a6.47 6.47 0 1 1 6.4707-6.47 6.4778 6.4778 0 0 1 -6.4707 6.47z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m11.3564 7.25h-2.6064v-2.6067a.75.75 0 1 0 -1.5 0v2.6067h-2.6064a.75.75 0 1 0 0 1.5h2.6064v2.6069a.75.75 0 1 0 1.5 0v-2.6069h2.6065a.75.75 0 1 0 0-1.5z"
  }));
};

IconPlusCircle.displayName = "IconPlusCircle";
IconPlusCircle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconPlusCircle"
};
var PlusCircle = withIcon(IconPlusCircle);

const IconPrinter = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1406 4.1345h-1.6806v-3.2754a.7.7 0 0 0 -.7-.7h-9.5208a.7.7 0 0 0 -.7.7v3.2754h-1.6798a.7.7 0 0 0 -.7.7v6.9736a.7.7 0 0 0 .7.7h1.68v2.6323a.7.7 0 0 0 .7.7h9.5206a.7.7 0 0 0 .7-.7v-2.6319h1.6807a.7.7 0 0 0 .7-.7v-6.9738a.7.7 0 0 0 -.7001-.7002zm-11.2012-2.5752h8.12v2.5752h-8.12zm8.12 12.8813h-8.12v-4.4106h8.12zm2.3806-3.3324h-.98v-1.7782a.7.7 0 0 0 -.7-.7h-9.52a.7.7 0 0 0 -.7.7v1.7783h-.98v-5.5734h12.88z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m3.2334 7.7947a.7025.7025 0 0 0 .5-.2.6887.6887 0 0 0 0-.99.7253.7253 0 0 0 -.9893 0 .6887.6887 0 0 0 0 .99.6959.6959 0 0 0 .4893.2z"
  }));
};

IconPrinter.displayName = "IconPrinter";
IconPrinter.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconPrinter"
};
var Printer = withIcon(IconPrinter);

const IconPulse = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m9.0752 15.5062h-.0185a.725.725 0 0 1 -.6953-.6l-1.7755-10.1436-1.582 6.5a.7237.7237 0 0 1 -.5879.5444.7143.7143 0 0 1 -.7295-.33l-1.5565-2.4752h-1.3a.7251.7251 0 0 1 0-1.45h1.7a.7228.7228 0 0 1 .6133.3394l.8577 1.3621 1.997-8.2051a.7246.7246 0 0 1 1.418.0464l1.7529 10.0137 1.6885-7.4136a.7252.7252 0 0 1 1.3779-.1147l1.6309 3.9688h1.3038a.7251.7251 0 0 1 0 1.45h-1.79a.7262.7262 0 0 1 -.6709-.45l-.9464-2.3018-1.98 8.6953a.7249.7249 0 0 1 -.7075.5639z"
  }));
};

IconPulse.displayName = "IconPulse";
IconPulse.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconPulse"
};
var Pulse = withIcon(IconPulse);

const IconSearch = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.7 14.6392-4.09-4.0892a6.4833 6.4833 0 1 0 -1.06 1.0605l4.0892 4.0895a.75.75 0 0 0 1.0608-1.0608zm-14.1194-8.0938a4.9646 4.9646 0 1 1 4.9648 4.9646 4.9706 4.9706 0 0 1 -4.9648-4.9646z"
  }));
};

IconSearch.displayName = "IconSearch";
IconSearch.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconSearch"
};
var Search = withIcon(IconSearch);

const IconShapes = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 7.8437a3.5591 3.5591 0 1 0 -3.559-3.559 3.5632 3.5632 0 0 0 3.559 3.559zm0-5.6181a2.0591 2.0591 0 1 1 -2.059 2.0591 2.0616 2.0616 0 0 1 2.059-2.0591z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.0635 8.6909h-5.0835a.75.75 0 0 0 -.75.75v5.0835a.75.75 0 0 0 .75.75h5.0835a.75.75 0 0 0 .75-.75v-5.0835a.75.75 0 0 0 -.75-.75zm-.75 5.0835h-3.5835v-3.5835h3.5835z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.83 8.5312a.78.78 0 0 0 -1.2988 0l-3.244 5.6182a.75.75 0 0 0 .6494 1.125h6.4878a.75.75 0 0 0 .6494-1.125zm-2.5946 5.2432 1.9446-3.3682 1.9448 3.3682z"
  }));
};

IconShapes.displayName = "IconShapes";
IconShapes.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconShapes"
};
var Shapes = withIcon(IconShapes);

const IconShare = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.791 5.314a.7266.7266 0 0 0 .0519-.077.7066.7066 0 0 0 0-.6249.7223.7223 0 0 0 -.0525-.0779.7358.7358 0 0 0 -.1411-.1715c-.0112-.01-.0157-.0245-.0275-.0335l-4.2812-3.2807a.75.75 0 1 0 -.9121 1.19l2.5257 1.9356h-3.3412c-3.8208 0-6.93 2.3926-6.93 5.3335a.75.75 0 1 0 1.5 0c0-2.1138 2.4356-3.8335 5.43-3.8335h3.3413l-2.5259 1.936a.75.75 0 1 0 .9121 1.19l4.2813-3.2801c.0118-.009.0163-.0238.0275-.0335a.7364.7364 0 0 0 .1417-.1725z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.2869 9.6066a.75.75 0 0 0 -.75.75v3.25h-9.9527v-9.9526h4.9912a.75.75 0 0 0 0-1.5h-5.7412a.75.75 0 0 0 -.75.75v11.4526a.75.75 0 0 0 .75.75h11.4527a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 0 -.75-.75z"
  }));
};

IconShare.displayName = "IconShare";
IconShare.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconShare"
};
var Share = withIcon(IconShare);

const IconShareAndroid = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m12.65 9.3564a3.2646 3.2646 0 0 0 -2.5011 1.18l-3.6189-1.7537a3.0256 3.0256 0 0 0 .0181-1.5073l3.629-1.7778a3.268 3.268 0 1 0 -.8121-2.1392 3.2482 3.2482 0 0 0 .1133.8107l-3.5975 1.7623a3.2852 3.2852 0 1 0 -.0389 4.1843l3.6235 1.7565a3.2556 3.2556 0 0 0 -.1.7694 3.2852 3.2852 0 1 0 3.2846-3.2852zm0-7.7832a1.7852 1.7852 0 1 1 -1.785 1.7852 1.7871 1.7871 0 0 1 1.785-1.7852zm-9.311 8.2119a1.7871 1.7871 0 0 1 -1.7852-1.7851 1.7618 1.7618 0 0 1 .2087-.82l.0018-.0027v-.0004a1.7816 1.7816 0 1 1 1.5747 2.6082zm9.311 4.6417a1.7871 1.7871 0 0 1 -1.7852-1.7852 1.7618 1.7618 0 0 1 .2087-.82l.0018-.0027v-.0008a1.7816 1.7816 0 1 1 1.5747 2.6087z"
  }));
};

IconShareAndroid.displayName = "IconShareAndroid";
IconShareAndroid.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconShareAndroid"
};
var ShareAndroid = withIcon(IconShareAndroid);

const IconShareIOS = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.2988 6.5522h-3.6826a.75.75 0 0 0 0 1.5h2.9326v6.36h-11.0976v-6.36h2.9326a.75.75 0 0 0 0-1.5h-3.6826a.75.75 0 0 0 -.75.75v7.86a.75.75 0 0 0 .75.75h12.5976a.75.75 0 0 0 .75-.75v-7.86a.75.75 0 0 0 -.75-.75z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.22 4.6768 2.03-2.03v7.9049a.75.75 0 0 0 1.5 0v-7.9048l2.03 2.03a.75.75 0 1 0 1.0605-1.0605l-3.3105-3.3102a.7527.7527 0 0 0 -1.0605 0l-3.3106 3.31a.75.75 0 0 0 1.0611 1.0606z"
  }));
};

IconShareIOS.displayName = "IconShareIOS";
IconShareIOS.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconShareIOS"
};
var ShareIOS = withIcon(IconShareIOS);

const IconShuffle = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.8811 12.09a.7506.7506 0 0 0 -.1614-.2416l-2.3975-2.397a.75.75 0 0 0 -1.0605 1.0605l1.1167 1.1164h-1.4077l-.0057.0012-.0051-.0012c-1.249.0483-3.4619-.4619-4.6035-3.8662-1.8935-5.6521-6.6357-4.8485-6.6826-4.8378a.75.75 0 0 0 .2685 1.4757c.1406-.023 3.5215-.5513 4.9922 3.8384 1.4688 4.3843 4.6045 4.89 5.9688 4.89h.0779 1.3978l-1.1172 1.1169a.75.75 0 1 0 1.0605 1.0605l2.3975-2.397a.7476.7476 0 0 0 .1614-.819z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m7.7666 6.523a.747.747 0 0 0 .6034-.3038 4.227 4.227 0 0 1 3.59-1.8472l.0057-.001.0051.001h1.4077l-1.1168 1.1163a.75.75 0 1 0 1.0605 1.0605l2.3978-2.3969a.7513.7513 0 0 0 -.0006-1.0611l-2.3972-2.3965a.75.75 0 1 0 -1.0605 1.0605l1.1173 1.1169h-1.4083a5.8241 5.8241 0 0 0 -4.8057 2.455.75.75 0 0 0 .6016 1.1963z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.9394 9.7544a4.2617 4.2617 0 0 1 -3.9971 1.8456.75.75 0 0 0 -.2676 1.4756 4.2116 4.2116 0 0 0 .7178.0532 5.792 5.792 0 0 0 4.7575-2.4888.75.75 0 0 0 -1.2109-.8857z"
  }));
};

IconShuffle.displayName = "IconShuffle";
IconShuffle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconShuffle"
};
var Shuffle = withIcon(IconShuffle);

const IconShuffleBox = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.2.1h-14.4a.7.7 0 0 0 -.7.7v14.4a.7.7 0 0 0 .7.7h14.4a.7.7 0 0 0 .7-.7v-14.4a.7.7 0 0 0 -.7-.7zm-.7 14.4h-13v-13h13z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m5.5576 8.9414a3.33 3.33 0 0 1 -2.8618 1.1206.7.7 0 0 0 -.2158 1.3833 3.6491 3.6491 0 0 0 .5352.0342 4.6867 4.6867 0 0 0 3.6088-1.6309.6887.6887 0 0 0 .151-.5579 4.6624 4.6624 0 0 0 4.0189 2.1888h.7383l-.5777.5775a.7.7 0 0 0 .99.99l1.7725-1.7725c.0054-.0054.007-.0129.0122-.0184a.681.681 0 0 0 .0033-.9485c-.0065-.0071-.0086-.0164-.0155-.0233l-1.7722-1.7721a.7.7 0 0 0 -.99.99l.577.5768h-.6893-.0083a3.3521 3.3521 0 0 1 -3.2769-2.334l-.0016-.0024a5.9911 5.9911 0 0 0 -.3221-.683.6529.6529 0 0 0 .0439.0566.7.7 0 0 0 .9873-.0732 3.29 3.29 0 0 1 2.5272-1.123h.05.69l-.5775.5773a.7.7 0 0 0 .99.99l1.7725-1.7725a.6522.6522 0 0 0 .0361-.0544.6252.6252 0 0 0 .1689-.4409l-.002-.0108a.69.69 0 0 0 -.2-.48l-.0023-.0034-.0006-.0006-1.7723-1.7716a.7.7 0 1 0 -.99.99l.5772.577h-.69l-.0029.0006a4.6157 4.6157 0 0 0 -3.635 1.6087.6883.6883 0 0 0 -.13.6486 4.69 4.69 0 0 0 -4.5941-2.2233.7.7 0 0 0 .22 1.3828 3.3245 3.3245 0 0 1 3.5521 2.3151v.0018a6.3017 6.3017 0 0 0 .2716.5961.6976.6976 0 0 0 -.9661.0909z"
  }));
};

IconShuffleBox.displayName = "IconShuffleBox";
IconShuffleBox.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconShuffleBox"
};
var ShuffleBox = withIcon(IconShuffleBox);

const IconShuffleBoxFilled = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.15 0h-14.4a.75.75 0 0 0 -.75.75v14.4a.75.75 0 0 0 .75.75h14.4a.75.75 0 0 0 .75-.75v-14.4a.75.75 0 0 0 -.75-.75zm-1.253 5.1707a.685.685 0 0 1 -.1908.48c-.0089.01-.012.0229-.0216.0325l-1.772 1.7729a.7251.7251 0 0 1 -1.0254-1.0254l.5348-.5348h-.63-.05a3.2657 3.2657 0 0 0 -2.5081 1.1141.7032.7032 0 0 1 -.9568.1147 6.1626 6.1626 0 0 1 .2532.5621 3.2918 3.2918 0 0 0 3.2534 2.3179l.0049.0008.0039-.0008h.629l-.5343-.5347a.7251.7251 0 1 1 1.0254-1.0254l1.7725 1.7725a.7277.7277 0 0 1 0 1.0254l-1.7725 1.7725a.7251.7251 0 1 1 -1.0254-1.025l.535-.535h-.6781a4.6892 4.6892 0 0 1 -3.9858-2.12.7159.7159 0 0 1 -.1656.4811 4.71 4.71 0 0 1 -3.6274 1.6387 3.6929 3.6929 0 0 1 -.5386-.0342.725.725 0 0 1 .2233-1.4333 3.3007 3.3007 0 0 0 2.8393-1.1123.7155.7155 0 0 1 .9221-.1371c-.0814-.1648-.1594-.3389-.2314-.524a3.2986 3.2986 0 0 0 -3.5257-2.3018.7248.7248 0 1 1 -.2275-1.4321 4.7114 4.7114 0 0 1 4.5351 2.1082.7222.7222 0 0 1 .1733-.5247 4.7475 4.7475 0 0 1 3.6548-1.6173h.0013.63l-.5345-.5345a.7251.7251 0 0 1 1.0254-1.0254l1.7709 1.7709a.7174.7174 0 0 1 .2113.5008l.0026.0129z"
  }));
};

IconShuffleBoxFilled.displayName = "IconShuffleBoxFilled";
IconShuffleBoxFilled.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconShuffleBoxFilled"
};
var ShuffleBoxFilled = withIcon(IconShuffleBoxFilled);

const IconSlider = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.3677 6.24h-4.7531a3.5191 3.5191 0 0 0 -6.1369.0082c-.0143-.0006-.0263-.0082-.0407-.0082h-1.8047a1.5717 1.5717 0 0 0 -1.57 1.57v.3789a1.5717 1.5717 0 0 0 1.57 1.57h1.7813c.0219 0 .04-.0106.0618-.0125a3.5191 3.5191 0 0 0 6.1392.0136h4.7531a1.5717 1.5717 0 0 0 1.57-1.57v-.3794a1.5716 1.5716 0 0 0 -1.57-1.5706zm-12.8057 2.02v-.52h1.4609c-.0065.0873-.0263.1708-.0263.26s.02.1724.0263.26zm4.9863 1.792a2.0518 2.0518 0 1 1 2.0517-2.052 2.0547 2.0547 0 0 1 -2.0516 2.0518zm7.89-1.792h-4.3645c.0062-.0876.0262-.1711.0262-.26s-.02-.1724-.0263-.26h4.3643z"
  }));
};

IconSlider.displayName = "IconSlider";
IconSlider.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconSlider"
};
var Slider = withIcon(IconSlider);

const IconSlidersVert = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M14.417 10.2363H9.6162a.75.75 0 0 1 0-1.5h4.8008a.75.75 0 1 1 0 1.5zM12.1128 15.8057a.75.75 0 0 1-.75-.75V11.791a.75.75 0 0 1 1.5 0v3.2647a.75.75 0 0 1-.75.75z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M12.1128 10.2363a.75.75 0 0 1-.75-.75V.8442a.75.75 0 0 1 1.5 0v8.6421a.75.75 0 0 1-.75.75zM6.7354 6.3955H2.1265a.75.75 0 0 1 0-1.5h4.6089a.75.75 0 0 1 0 1.5zM4.4307 15.8057a.75.75 0 0 1-.75-.75V7.95a.75.75 0 0 1 1.5 0v7.1055a.75.75 0 0 1-.75.7502z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.4307 6.3955a.75.75 0 0 1-.75-.75V.8442a.75.75 0 0 1 1.5 0v4.8013a.75.75 0 0 1-.75.75z"
  }));
};

IconSlidersVert.displayName = "IconSlidersVert";
IconSlidersVert.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconSlidersVert"
};
var SlidersVert = withIcon(IconSlidersVert);

const IconStar = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.874 6.13a.75.75 0 0 0 -.7134-.519h-4.84l-1.615-4.5138a.75.75 0 0 0 -1.4121 0l-1.615 4.5141h-4.8391a.75.75 0 0 0 -.4394 1.3574l3.807 2.7547-1.6723 4.6745a.75.75 0 0 0 1.1162.88l4.3491-2.8394 4.3491 2.84a.75.75 0 0 0 1.1162-.88l-1.6726-4.6745 3.8073-2.7553a.7494.7494 0 0 0 .274-.8387zm-5.4165 2.709c-.0118.0085-.0173.0218-.0285.0309a.7183.7183 0 0 0 -.1079.1168.6315.6315 0 0 0 -.1589.5143.7215.7215 0 0 0 .0226.1552c.0042.014.0012.0283.0062.0422l1.11 3.1045-2.891-1.8879a.7488.7488 0 0 0 -.82 0l-2.8918 1.8877 1.11-3.104c.0049-.0137.0019-.0276.006-.0414a.7184.7184 0 0 0 .0233-.16.7392.7392 0 0 0 .0043-.1291.6374.6374 0 0 0 -.1675-.386.7192.7192 0 0 0 -.1021-.11c-.0118-.01-.0176-.0236-.03-.0327l-2.3869-1.7282h3.0512a.7419.7419 0 0 0 .2626-.053.7252.7252 0 0 0 .0748-.0366.6923.6923 0 0 0 .3192-.3174.7081.7081 0 0 0 .0383-.07c.0029-.0074.0085-.0129.0112-.02l1.0874-3.0386 1.0859 3.0356c.0026.0071.0085.0117.0113.0187a.73.73 0 0 0 .0952.1574.7558.7558 0 0 0 .0585.0867.6491.6491 0 0 0 .4216.2129.7214.7214 0 0 0 .0906.0183c.0105 0 .0193.006.03.006h3.0518z"
  }));
};

IconStar.displayName = "IconStar";
IconStar.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconStar"
};
var Star = withIcon(IconStar);

const IconStarFilled = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.874 6.13a.7507.7507 0 0 0 -.7139-.5191h-4.8388l-1.6152-4.5137a.75.75 0 0 0 -1.4121 0l-1.6163 4.5141h-4.8377a.75.75 0 0 0 -.44 1.3575l3.807 2.7548-1.6728 4.6739a.75.75 0 0 0 1.1162.8809l4.3496-2.8399 4.3486 2.84a.75.75 0 0 0 1.1162-.8809l-1.6728-4.6735 3.808-2.7554a.75.75 0 0 0 .274-.8387z"
  }));
};

IconStarFilled.displayName = "IconStarFilled";
IconStarFilled.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconStarFilled"
};
var StarFilled = withIcon(IconStarFilled);

const IconTableCol = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M1.6678 16.0038a1.6437 1.6437 0 0 1-1.096-.4313A1.7177 1.7177 0 0 1 0 14.3057V1.6943A1.6963 1.6963 0 0 1 1.6943 0h12.6114A1.6963 1.6963 0 0 1 16 1.6943v12.6114A1.6963 1.6963 0 0 1 14.3057 16H1.779c-.0369.0027-.0743.0038-.1112.0038zm.0265-14.6149a.302.302 0 0 0-.3054.3054v12.6114a.3361.3361 0 0 0 .115.237.2452.2452 0 0 0 .1769.0717l.0689-.0033h12.556a.302.302 0 0 0 .3054-.3054V1.6943a.302.302 0 0 0-.3054-.3054z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.7224.6668h1.3889v14.6664H3.7224z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M.23679062 12.69208759l.7378489-1.17619835 4.00974785 2.5153819-.7378489 1.17619835zM.23653838 9.1358777l.73833393-1.17674962 4.01045281 2.51629857-.73833392 1.17674961zM.23652695 5.58003623l.73833392-1.17674961L4.9853137 6.91958518l-.73833392 1.17674961zM.23652376 2.02481365L.97485768.84806404 4.9853105 3.3643626 4.24697658 4.5411122z"
  }));
};

IconTableCol.displayName = "IconTableCol";
IconTableCol.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTableCol"
};
var TableCol = withIcon(IconTableCol);

const IconTableRow = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M14.3061 15.9959H1.6983A1.6959 1.6959 0 0 1 .0041 14.302V1.7785A1.63 1.63 0 0 1 .4312.5722 1.7188 1.7188 0 0 1 1.6983 0h12.6078A1.6958 1.6958 0 0 1 16 1.6939V14.302a1.6958 1.6958 0 0 1-1.6939 1.6939zM1.6983 1.3885a.3369.3369 0 0 0-.2373.1156.2475.2475 0 0 0-.0719.1763l.0035.0688V14.302a.3022.3022 0 0 0 .3057.3054h12.6078a.302.302 0 0 0 .3054-.3054V1.6939a.302.302 0 0 0-.3054-.3054z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M.6707 3.7214h14.6627v1.3885H.6707z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M.79621001 4.24571892L3.31230328.2367901l1.17622688.73822626L1.9724369 4.98394518zM4.35097781 4.24562379L6.86701792.23677967 8.0432448.97500593 5.5272047 4.98385005zM7.9056546 4.2456449L10.42180101.23663138 11.5981126.9749108 9.08196617 4.98392432zM11.46010098 4.24587677l2.51578674-4.0093573 1.1764723.73821144-2.51578674 4.0093573z"
  }));
};

IconTableRow.displayName = "IconTableRow";
IconTableRow.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTableRow"
};
var TableRow = withIcon(IconTableRow);

const IconTablet = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.1072.1226h-10.2144a1.7021 1.7021 0 0 0 -1.7 1.7v12.3546a1.7021 1.7021 0 0 0 1.7 1.7h10.2144a1.7021 1.7021 0 0 0 1.7-1.7v-12.3544a1.7021 1.7021 0 0 0 -1.7-1.7002zm-10.2144 1.4h10.2144a.3.3 0 0 1 .3.3v.9074h-10.8142v-.9072a.3.3 0 0 1 .2998-.2999zm10.5142 2.6074v6.6455h-10.814v-6.6455zm-.3 10.3467h-10.2142a.3.3 0 0 1 -.3-.3v-2.001h10.8142v2.001a.3.3 0 0 1 -.2998.3004z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8.886 12.6216h-1.7715a.7.7 0 1 0 0 1.4h1.7715a.7.7 0 1 0 0-1.4z"
  }));
};

IconTablet.displayName = "IconTablet";
IconTablet.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTablet"
};
var Tablet = withIcon(IconTablet);

const IconTabletCheck = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m4.9468 8.5571 1.665 1.665a.75.75 0 0 0 1.0605 0l3.3809-3.3809a.75.75 0 1 0 -1.0605-1.0604l-2.8506 2.8505-1.1348-1.1347a.75.75 0 0 0 -1.0605 1.0605z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.1772.0405h-10.3544a1.7021 1.7021 0 0 0 -1.7 1.7v12.5188a1.7021 1.7021 0 0 0 1.7 1.7h10.3544a1.7021 1.7021 0 0 0 1.7-1.7v-12.5186a1.7021 1.7021 0 0 0 -1.7-1.7002zm-10.3545 1.4h10.3545a.3.3 0 0 1 .3.3v1.0666h-10.9543v-1.0664a.3.3 0 0 1 .2999-.2998zm10.6544 2.767v7.5859h-10.9542v-7.5859zm-.3 10.3516h-10.3543a.3.3 0 0 1 -.3-.3v-1.0653h10.9543v1.0654a.3.3 0 0 1 -.2999.2999z"
  }));
};

IconTabletCheck.displayName = "IconTabletCheck";
IconTabletCheck.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTabletCheck"
};
var TabletCheck = withIcon(IconTabletCheck);

const IconTag = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.7124.2871a.75.75 0 0 0 -1.0605 0l-1.4839 1.4839-.708-.7075a.7515.7515 0 0 0 -.51-.22l-4.1546-.116a.8061.8061 0 0 0 -.5508.22l-6.957 6.9558a.75.75 0 0 0 0 1.0606l6.7481 6.749a.75.75 0 0 0 1.0605 0l6.957-6.957a.75.75 0 0 0 .22-.5508l-.1165-4.1551a.7515.7515 0 0 0 -.22-.51l-.7085-.7085 1.4839-1.4839a.75.75 0 0 0 .0003-1.0605zm-2.0469 4.1029.0986 3.5342-6.1982 6.1979-5.6875-5.6885 6.1983-6.1973 3.5342.0986.4966.4966-.7216.7216a2.2343 2.2343 0 1 0 1.061 1.06l.7211-.7211zm-2.7109.6563a.7368.7368 0 1 1 -.52-.2139.7338.7338 0 0 1 .52.2135z"
  }));
};

IconTag.displayName = "IconTag";
IconTag.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTag"
};
var Tag = withIcon(IconTag);

const IconTemplate = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M14.1.2H1.9C1.4.2 1 .5 1 1v14c0 .5.4.8.8.8h12.3c.5 0 .8-.4.8-.8V1c0-.5-.3-.8-.8-.8zm-.4 1.2v2.5H2.3V1.4h11.4zM2.3 9V5.3h5.6V9H2.3zm0 1.4h5.6v4.2H2.3v-4.2zm6.9 4.2V5.3h4.5v9.3H9.2z"
  }));
};

IconTemplate.displayName = "IconTemplate";
IconTemplate.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTemplate"
};
var Template = withIcon(IconTemplate);

const IconTextbox = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1563 5.126h-14.3125a.75.75 0 0 0 -.75.75v4.248a.75.75 0 0 0 .75.75h14.3125a.75.75 0 0 0 .75-.75v-4.248a.75.75 0 0 0 -.75-.75zm-.75 4.248h-12.8125v-2.748h12.8125z"
  }));
};

IconTextbox.displayName = "IconTextbox";
IconTextbox.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTextbox"
};
var Textbox = withIcon(IconTextbox);

const IconTextboxMultiple = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1563 1.5332h-14.3125a.75.75 0 0 0 -.75.75v4.2468a.75.75 0 0 0 .75.75h14.3125a.75.75 0 0 0 .75-.75v-4.2468a.75.75 0 0 0 -.75-.75zm-.75 4.2471h-12.8125v-2.7471h12.8125z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.1563 8.72h-14.3125a.75.75 0 0 0 -.75.75v4.2471a.75.75 0 0 0 .75.75h14.3125a.75.75 0 0 0 .75-.75v-4.2471a.75.75 0 0 0 -.75-.75zm-.75 4.2471h-12.8125v-2.7471h12.8125z"
  }));
};

IconTextboxMultiple.displayName = "IconTextboxMultiple";
IconTextboxMultiple.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTextboxMultiple"
};
var TextboxMultiple = withIcon(IconTextboxMultiple);

const IconThumbsUp = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.23 8.3875a1.7682 1.7682 0 0 0 -1.107-3.1537h-.0225l-.0048-.001h-3.5552a7.5592 7.5592 0 0 0 .2266-1.3672c.0469-1.1729-.5-2.4668-1.813-2.5371-1.3672-.084-1.6941 1.2822-1.8641 2.0115a3.09 3.09 0 0 1 -.2236.7266 5.5528 5.5528 0 0 1 -1.83 1.5458.6877.6877 0 0 0 -.5431-.2762h-3.6925a.7.7 0 0 0 -.7.7v7.7338a.7.7 0 0 0 .7.7h3.6929a.6881.6881 0 0 0 .5384-.2694 4.5665 4.5665 0 0 0 1.8219.4744h5.876a1.6406 1.6406 0 0 0 1.6108-1.665 1.6938 1.6938 0 0 0 -.0772-.4832 1.6591 1.6591 0 0 0 .64-2.2564 1.6595 1.6595 0 0 0 .3267-1.883zm-11.4365 4.6825h-2.2925v-6.3333h2.2925zm8.9365.2051h-5.8579c-.0134 0-1.3372-.0631-1.6782-.7835v-5.3543a7.8387 7.8387 0 0 0 2.8554-2.3215 3.754 3.754 0 0 0 .4043-1.1572c.189-.8086.2876-.919.4258-.9316.4014.0215.5029.7383.49 1.0723a6.3983 6.3983 0 0 1 -.4429 1.8623.7005.7005 0 0 0 .645.9717h2.4781l.0048.001h2.0689a.3755.3755 0 1 1 0 .751h-2.0691a.7.7 0 0 0 0 1.4h1.5967a.3081.3081 0 1 1 0 .6133h-1.9966a.7.7 0 1 0 0 1.4h.45l.0048.001h1.36a.2736.2736 0 0 1 0 .5439h-1.8148a.7.7 0 1 0 0 1.4h1.0757a.2722.2722 0 0 1 0 .53z"
  }));
};

IconThumbsUp.displayName = "IconThumbsUp";
IconThumbsUp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconThumbsUp"
};
var ThumbsUp = withIcon(IconThumbsUp);

const IconThumbsUpFilled = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m3.1267 5.8447h-2.288a.7.7 0 0 0 -.7.7v7.2253a.7.7 0 0 0 .7.7h2.288a.7.7 0 0 0 .7-.7v-7.2251a.7.7 0 0 0 -.7-.7002z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.8615 6.8935a1.2052 1.2052 0 0 0 -1.2015-1.2017v-.0006h-5.0846a7.8507 7.8507 0 0 0 .5546-2.3419c.0334-.8345-.2845-1.9677-1.2861-2.0212-1.3154-.07-1.1233 1.8988-1.6292 2.6971a6.95 6.95 0 0 1 -2.3737 2.0164v8.17a4.3175 4.3175 0 0 0 1.6992.4622h6.5631a1.08 1.08 0 0 0 0-2.1565l.8259-.0006a1.0864 1.0864 0 0 0 0-2.1719l.2027-.0006a1.125 1.125 0 1 0 0-2.249h.528a1.2052 1.2052 0 0 0 1.2016-1.2017z"
  }));
};

IconThumbsUpFilled.displayName = "IconThumbsUpFilled";
IconThumbsUpFilled.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconThumbsUpFilled"
};
var ThumbsUpFilled = withIcon(IconThumbsUpFilled);

const IconTimer = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8.5977 2.1677v-.5823h1.1406a.75.75 0 1 0 0-1.5h-3.7813a.75.75 0 0 0 0 1.5h1.1407v.5846a6.9273 6.9273 0 0 0 -4.8177 2.7817 6.8959 6.8959 0 1 0 6.3177-2.784zm-.75 12.2468a5.3948 5.3948 0 1 1 5.3945-5.3945 5.401 5.401 0 0 1 -5.3945 5.3945z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m10.0957 5.9941-1.5051 1.4938a1.9617 1.9617 0 1 0 1.0411 1.08l1.5206-1.5092a.75.75 0 1 0 -1.0566-1.0644zm-2.248 3.7715a.52.52 0 0 1 0-1.04.5184.5184 0 0 1 .52.52.5206.5206 0 0 1 -.52.52z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m14.8272 3.3818-1.0372-1.0371a.75.75 0 0 0 -1.0605 1.0605l1.0371 1.0371a.75.75 0 0 0 1.0605-1.0605z"
  }));
};

IconTimer.displayName = "IconTimer";
IconTimer.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTimer"
};
var Timer = withIcon(IconTimer);

const IconToggle = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.1719 3.3008h-6.3438a4.6992 4.6992 0 0 0 0 9.3984h6.3438a4.6992 4.6992 0 0 0 0-9.3984zm-9.543 4.6992a3.203 3.203 0 0 1 3.1992-3.1992h2.9219a4.6454 4.6454 0 0 0 0 6.3984h-2.9219a3.203 3.203 0 0 1 -3.1992-3.1992z"
  }));
};

IconToggle.displayName = "IconToggle";
IconToggle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconToggle"
};
var Toggle = withIcon(IconToggle);

const IconToolbox = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.1941 3.4219h-4.1436v-2.4483a.75.75 0 0 0 -.75-.75h-4.601a.75.75 0 0 0 -.75.75v2.4483h-4.1436a.75.75 0 0 0 -.75.75v10.8545a.75.75 0 0 0 .75.75h14.3882a.75.75 0 0 0 .75-.75v-10.8545a.75.75 0 0 0 -.75-.75zm-8.7446-1.6983h3.101v1.6983h-3.101zm7.9946 3.1982v3.3468h-12.8882v-3.3467zm-12.8882 9.3546v-4.5078h2.5527v.86a.75.75 0 0 0 1.5 0v-.86h4.7827v.86a.75.75 0 0 0 1.5 0v-.86h2.5527v4.5078z"
  }));
};

IconToolbox.displayName = "IconToolbox";
IconToolbox.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconToolbox"
};
var Toolbox = withIcon(IconToolbox);

const IconTrash = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.0019 2.9858h-3.9575v-1.1093a1.82 1.82 0 0 0 -1.8178-1.8179h-2.4532a1.82 1.82 0 0 0 -1.8178 1.8179v1.1093h-3.9576a.75.75 0 0 0 0 1.5h1.0365l.2917 9.6548a1.7922 1.7922 0 0 0 1.7529 1.8008h7.7524a1.793 1.793 0 0 0 1.7525-1.7814l.38-9.6738h1.038a.75.75 0 1 0 0-1.5zm-8.5463-1.1093a.3182.3182 0 0 1 .3179-.3179h2.4531a.3182.3182 0 0 1 .3179.3179v1.1093h-3.0889zm5.6294 12.2285a.3163.3163 0 0 1 -.2534.3364h-7.7525c-.1182 0-.2485-.1367-.2539-.3428l-.29-9.6128h8.9276z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.3779 6.1816a.75.75 0 0 0 -.75.75v4.8477a.75.75 0 0 0 1.5 0v-4.8477a.75.75 0 0 0 -.75-.75z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m9.6221 6.1816a.75.75 0 0 0 -.75.75v4.8477a.75.75 0 1 0 1.5 0v-4.8477a.75.75 0 0 0 -.75-.75z"
  }));
};

IconTrash.displayName = "IconTrash";
IconTrash.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTrash"
};
var Trash = withIcon(IconTrash);

const IconTruck = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.59 1.2607h-6.2077a1.3186 1.3186 0 0 0 -1.3173 1.3169v.0664h-2.7364a1.9174 1.9174 0 0 0 -1.627.895l-2.2255 3.5152a2.4663 2.4663 0 0 0 -.2081.4622.67.67 0 0 0 -.0466.1036 2.4634 2.4634 0 0 0 -.1292.7593v3.4736a1.3963 1.3963 0 0 0 1.3945 1.3945h1.1785a2.17 2.17 0 0 0 4.0994 0h2.593a2.1692 2.1692 0 0 0 4.1217 0h1.1107a1.3186 1.3186 0 0 0 1.3177-1.3174v-9.3524a1.3185 1.3185 0 0 0 -1.3177-1.3169zm-10.7057 3.0274a.5248.5248 0 0 1 .4443-.2436l2.7364-.0009v3.0995h-4.9878zm.83 9.0166a.783.783 0 1 1 .7832-.7832.7832.7832 0 0 1 -.7831.7832zm6.7041.0342a.783.783 0 1 1 .7769-.8139c0 .0077-.0045.0142-.0045.0219 0 .0111.0058.02.0063.0312a.7814.7814 0 0 1 -.7786.7608zm3.0889-1.4922h-1.0337a2.17 2.17 0 0 0 -4.11 0h-2.5836a2.1683 2.1683 0 0 0 -4.1315.002l-1.1561.0039v-3.3091h6.2727a.7.7 0 0 0 .7-.7v-5.1824h6.042z"
  }));
};

IconTruck.displayName = "IconTruck";
IconTruck.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconTruck"
};
var Truck = withIcon(IconTruck);

const IconUnlock = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m12.3184.2507a3.7 3.7 0 0 0 -3.6514 3.4014v2.7437h-7.8711a.75.75 0 0 0 -.75.75v7.8535a.75.75 0 0 0 .75.75h11.4541a.75.75 0 0 0 .75-.75v-7.8535a.75.75 0 0 0 -.75-.75h-2.083v-2.7437a2.1594 2.1594 0 0 1 4.2871-.0068v3.5a.75.75 0 0 0 1.5 0v-3.5a3.6882 3.6882 0 0 0 -3.6357-3.3946zm-.8184 13.9986h-9.9541v-6.3535h9.9541z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.5234 9.05a.75.75 0 0 0 -.75.75v2.545a.75.75 0 0 0 1.5 0v-2.545a.75.75 0 0 0 -.75-.75z"
  }));
};

IconUnlock.displayName = "IconUnlock";
IconUnlock.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconUnlock"
};
var Unlock = withIcon(IconUnlock);

const IconUser = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m8 9.0117a3.3467 3.3467 0 0 0 2.8086-1.7031 5.789 5.789 0 0 0 .53-5.1592 3.6049 3.6049 0 0 0 -3.3386-2.0937 3.605 3.605 0 0 0 -3.3389 2.0937 5.789 5.789 0 0 0 .53 5.1592 3.3468 3.3468 0 0 0 2.8089 1.7031zm-1.9736-6.2412a2.0942 2.0942 0 0 1 1.9736-1.2148 2.0942 2.0942 0 0 1 1.9736 1.2148 4.3225 4.3225 0 0 1 -.4268 3.7256 1.9264 1.9264 0 0 1 -1.5468 1.0156 1.9265 1.9265 0 0 1 -1.5469-1.0156 4.3225 4.3225 0 0 1 -.4267-3.7256z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.1025 10.1055a7.1174 7.1174 0 0 1 -2.3135-1.1514.7514.7514 0 0 0 -1.0723.1621l-1.7167 2.4424-1.7168-2.4424a.7514.7514 0 0 0 -1.0723-.1621 7.1174 7.1174 0 0 1 -2.3135 1.1514c-1.7734.5938-2.86 1.1572-2.86 5.0889a.75.75 0 0 0 .75.75h14.4255a.75.75 0 0 0 .75-.75c0-3.9317-1.0869-4.4952-2.8604-5.0889zm-11.5525 4.3388c.0889-2.3359.6406-2.5205 1.8232-2.916a9.2307 9.2307 0 0 0 2.1121-.9383l1.9014 2.7041a.7787.7787 0 0 0 1.2266 0l1.9013-2.7041a9.2314 9.2314 0 0 0 2.1123.9385c1.1826.3955 1.7344.58 1.8232 2.916z"
  }));
};

IconUser.displayName = "IconUser";
IconUser.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconUser"
};
var User = withIcon(IconUser);

const IconUserArrow = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.0767 9.6787a5.8791 5.8791 0 0 1 -1.9023-.9453.6995.6995 0 0 0 -1.1192.4466 1.6175 1.6175 0 0 1 -1.2021.4766 1.8506 1.8506 0 0 1 -1.2931-.496.7.7 0 0 0 -1.1162-.4272 5.8792 5.8792 0 0 1 -1.9024.9453c-1.5114.5059-2.4306 1.0479-2.4306 4.3032a.7.7 0 0 0 .7.7h11.9963a.7.7 0 0 0 .7-.7c.0002-3.2553-.9187-3.7973-2.4304-4.3032zm-9.5527 3.603c.0781-1.8125.5-1.9536 1.4619-2.2754a7.8626 7.8626 0 0 0 1.6973-.7417 3.3275 3.3275 0 0 0 2.17.7925 3.161 3.161 0 0 0 2.0918-.7866 7.9062 7.9062 0 0 0 1.6875.7358c.9619.3218 1.3838.4629 1.4619 2.2754z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.8091 8.918a2.8562 2.8562 0 0 0 2.3994-1.4512 4.8954 4.8954 0 0 0 .4463-4.3643 3.0732 3.0732 0 0 0 -2.8457-1.7846 3.0694 3.0694 0 0 0 -2.8447 1.7851 4.8923 4.8923 0 0 0 .4456 4.3628 2.8552 2.8552 0 0 0 2.3991 1.4522zm-1.57-5.2353a1.6638 1.6638 0 0 1 1.57-.9644 1.668 1.668 0 0 1 1.5723.9648 3.5125 3.5125 0 0 1 -.35 3.0259 1.5321 1.5321 0 0 1 -1.2227.8086 1.5326 1.5326 0 0 1 -1.2227-.8091 3.5115 3.5115 0 0 1 -.3472-3.0259z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.8387 4.7439a.651.651 0 0 0 -.14-.7094l-2.4765-2.4759a.65.65 0 1 0 -.92.919l1.3678 1.3666h-2.314a.65.65 0 1 0 0 1.3h2.314l-1.3678 1.367a.65.65 0 0 0 .92.919l2.4764-2.4764a.65.65 0 0 0 .1401-.2099z"
  }));
};

IconUserArrow.displayName = "IconUserArrow";
IconUserArrow.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconUserArrow"
};
var UserArrow = withIcon(IconUserArrow);

const IconUserFilled = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.1924 2.1516a3.4447 3.4447 0 0 0 -3.1924-1.9995 3.4447 3.4447 0 0 0 -3.1924 2 5.602 5.602 0 0 0 .5161 4.99 3.1937 3.1937 0 0 0 2.6763 1.6291 3.1946 3.1946 0 0 0 2.6768-1.63 5.6 5.6 0 0 0 .5156-4.9896z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.1748 10.5749a7.76 7.76 0 0 1 -2.5254-1.2607.3711.3711 0 0 0 -.5308.0806l-2.1186 3.0127-2.1182-3.0127a.3711.3711 0 0 0 -.5308-.0806 7.76 7.76 0 0 1 -2.5254 1.2607c-1.7211.5757-2.6947 1.087-2.6947 4.9019a.3709.3709 0 0 0 .3711.3711h14.996a.3709.3709 0 0 0 .3711-.3711c0-3.8149-.9736-4.3262-2.6943-4.9019z"
  }));
};

IconUserFilled.displayName = "IconUserFilled";
IconUserFilled.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconUserFilled"
};
var UserFilled = withIcon(IconUserFilled);

const IconUserPlus = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m11.0769 9.6787a5.8726 5.8726 0 0 1 -1.9019-.9453.7.7 0 0 0 -1.12.4468 1.6166 1.6166 0 0 1 -1.2021.4766 1.8521 1.8521 0 0 1 -1.292-.4956.7.7 0 0 0 -1.1167-.4277 5.8727 5.8727 0 0 1 -1.9019.9453c-1.5123.5058-2.4307 1.0478-2.4307 4.3031a.7.7 0 0 0 .7.7h11.9958a.7.7 0 0 0 .7-.7c.0002-3.2553-.9183-3.7973-2.4305-4.3032zm-9.5518 3.603c.0771-1.8125.5-1.9536 1.4614-2.2754a7.8833 7.8833 0 0 0 1.6973-.7417 3.3238 3.3238 0 0 0 2.169.7925 3.1591 3.1591 0 0 0 2.0918-.7866 7.9087 7.9087 0 0 0 1.688.7358c.9619.3218 1.3843.4629 1.4614 2.2754z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m6.8093 8.918a2.859 2.859 0 0 0 2.4-1.4521 4.897 4.897 0 0 0 .4457-4.3629 3.0723 3.0723 0 0 0 -2.8457-1.7851 3.0733 3.0733 0 0 0 -2.8457 1.7851 4.8983 4.8983 0 0 0 .4458 4.3633 2.8577 2.8577 0 0 0 2.3999 1.4517zm-1.5718-5.2344a1.6679 1.6679 0 0 1 1.5718-.9653 1.6654 1.6654 0 0 1 1.5713.9648 3.5112 3.5112 0 0 1 -.3481 3.0249 1.5328 1.5328 0 0 1 -1.2232.81 1.5318 1.5318 0 0 1 -1.2222-.8091 3.51 3.51 0 0 1 -.3496-3.0253z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m15.2385 3.31h-1.2915v-1.2919a.65.65 0 0 0 -1.3 0v1.2919h-1.291a.65.65 0 1 0 0 1.3h1.291v1.2909a.65.65 0 0 0 1.3 0v-1.2915h1.2915a.65.65 0 1 0 0-1.3z"
  }));
};

IconUserPlus.displayName = "IconUserPlus";
IconUserPlus.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconUserPlus"
};
var UserPlus = withIcon(IconUserPlus);

const IconUsers = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m4.3755 11.1333-.9423-.9483a.4476.4476 0 0 0 -.5813-.0726c-1.6744 1.0883-2.7756.16-2.7756 3.3421h4.2764a7.1494 7.1494 0 0 1 .2781-2.2245c-.0054 0-.0081.0039-.0135.0039a.3763.3763 0 0 1 -.2418-.1006z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.6417 9.503a.529.529 0 0 0 -.6873.0859l-1.1139 1.1211a.4027.4027 0 0 1 -.5718 0l-1.1141-1.1211a.5291.5291 0 0 0 -.6873-.0859c-1.98 1.2867-3.2817.1893-3.2817 3.9515h10.738c0-3.7623-1.3018-2.6645-3.2819-3.9515z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.6172 9.3914c1.6375 0 2.497-2.5742 1.8393-4.0182a1.9705 1.9705 0 0 0 -1.8393-1.1452 1.9705 1.9705 0 0 0 -1.8392 1.1453c-.658 1.444.202 4.0181 1.8392 4.0181z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m10.5546 8.65c1.9361 0 2.9523-3.0435 2.1747-4.7509a2.33 2.33 0 0 0 -2.1747-1.3541 2.33 2.33 0 0 0 -2.1746 1.355c-.7776 1.707.2385 4.75 2.1746 4.75z"
  }));
};

IconUsers.displayName = "IconUsers";
IconUsers.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconUsers"
};
var Users = withIcon(IconUsers);

const IconUsersGroup = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m12.4665 9.1189c-1.2526 0-1.91-1.9691-1.407-3.0737a1.5678 1.5678 0 0 1 2.8139 0c.5031 1.1048-.1544 3.0737-1.4069 3.0737z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m12.66 12.1179h3.28c0-2.434-.8423-1.724-2.1234-2.5565a.3423.3423 0 0 0 -.4446.0556l-.7206.7254a.288.288 0 0 1 -.185.0771c-.0065 0-.01-.0041-.0159-.0046a5.41 5.41 0 0 1 .2095 1.703z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m3.5334 9.1189c-1.2526 0-1.91-1.9691-1.4069-3.0737a1.5074 1.5074 0 0 1 1.4069-.876 1.5074 1.5074 0 0 1 1.4069.876c.5031 1.1048-.1543 3.0737-1.4069 3.0737z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m3.5585 10.4121c-.01.0013-.015.0073-.0252.0073a.2879.2879 0 0 1 -.1849-.0771c-.2343-.2357-.7208-.7254-.7208-.7254a.3423.3423 0 0 0 -.4447-.0556c-1.2807.8325-2.1229.1225-2.1229 2.5566h3.2918a5.4688 5.4688 0 0 1 .2067-1.7058z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m9.6029 9.0951a.4048.4048 0 0 0 -.5258.0658l-.8521.8576a.308.308 0 0 1 -.4374 0l-.8522-.8576a.4048.4048 0 0 0 -.5254-.0658c-1.5144.9843-2.51.1448-2.51 3.0227h8.214c-.0006-2.8778-.9965-2.0384-2.5111-3.0227z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8.0064 8.5521c-1.481 0-2.2583-2.3281-1.6635-3.6342a1.7823 1.7823 0 0 1 1.6635-1.0358 1.7823 1.7823 0 0 1 1.6636 1.0358c.5947 1.3061-.1826 3.6342-1.6636 3.6342z"
  }));
};

IconUsersGroup.displayName = "IconUsersGroup";
IconUsersGroup.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconUsersGroup"
};
var UsersGroup = withIcon(IconUsersGroup);

const IconVariable = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m5.8438 2.125a.75.75 0 0 0 0-1.5h-3.8125a.75.75 0 0 0 -.75.75v13.25a.75.75 0 0 0 .75.75h3.8125a.75.75 0 0 0 0-1.5h-3.0625v-11.75z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m13.9688.625h-3.8125a.75.75 0 0 0 0 1.5h3.0625v11.75h-3.0625a.75.75 0 0 0 0 1.5h3.8125a.75.75 0 0 0 .75-.75v-13.25a.75.75 0 0 0 -.75-.75z"
  }));
};

IconVariable.displayName = "IconVariable";
IconVariable.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconVariable"
};
var Variable = withIcon(IconVariable);

const IconVideo = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.4724 3.0515a.75.75 0 0 0 -.7685.0342l-4.0093 2.6443v-2.0589a.75.75 0 0 0 -.75-.75h-9.0615a.75.75 0 0 0 -.75.75v8.6577a.75.75 0 0 0 .75.75h9.0615a.75.75 0 0 0 .75-.75v-2.0588l4.0092 2.6447a.75.75 0 0 0 1.1631-.626v-8.577a.7489.7489 0 0 0 -.3945-.6602zm-6.2778 8.5274h-7.5615v-7.1578h7.5615zm5.1723-.6836-3.6631-2.4165v-.9576l3.6631-2.4165z"
  }));
};

IconVideo.displayName = "IconVideo";
IconVideo.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconVideo"
};
var Video = withIcon(IconVideo);

const IconVideoEnd = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.165.1309a.75.75 0 0 0 -.75.75v5.82l-11.205-6.4695a.75.75 0 0 0 -1.125.6495v14.2382a.75.75 0 0 0 1.125.6494l11.2051-6.47v5.82a.75.75 0 0 0 1.5 0v-14.2376a.75.75 0 0 0 -.7501-.75zm-11.58 13.6891v-11.64l10.08 5.82z"
  }));
};

IconVideoEnd.displayName = "IconVideoEnd";
IconVideoEnd.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconVideoEnd"
};
var VideoEnd = withIcon(IconVideoEnd);

const IconVideoNext = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m1.46 15.7687a.7509.7509 0 0 0 .75 0l12.33-7.1187a.75.75 0 0 0 0-1.2988l-12.33-7.1196a.75.75 0 0 0 -1.125.6494v14.2383a.7487.7487 0 0 0 .375.6494zm1.125-13.5887 10.08 5.82-10.08 5.82z"
  }));
};

IconVideoNext.displayName = "IconVideoNext";
IconVideoNext.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconVideoNext"
};
var VideoNext = withIcon(IconVideoNext);

const IconVideoPrev = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.54.2313a.7509.7509 0 0 0 -.75 0l-12.33 7.1187a.75.75 0 0 0 0 1.2988l12.33 7.1191a.75.75 0 0 0 1.125-.6494v-14.2378a.7487.7487 0 0 0 -.375-.6494zm-1.125 13.5887-10.08-5.82 10.08-5.82z"
  }));
};

IconVideoPrev.displayName = "IconVideoPrev";
IconVideoPrev.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconVideoPrev"
};
var VideoPrev = withIcon(IconVideoPrev);

const IconVideoStart = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.5405.2314a.7509.7509 0 0 0 -.75 0l-11.206 6.4697v-5.8202a.75.75 0 0 0 -1.5 0v14.2382a.75.75 0 0 0 1.5 0v-5.82l11.206 6.47a.75.75 0 0 0 1.125-.6494v-14.2388a.75.75 0 0 0 -.375-.6495zm-1.125 13.5886-10.081-5.82 10.081-5.82z"
  }));
};

IconVideoStart.displayName = "IconVideoStart";
IconVideoStart.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconVideoStart"
};
var VideoStart = withIcon(IconVideoStart);

const IconVideoStop = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.49.0464h-12.98a.75.75 0 0 0 -.75.75v14.4072a.75.75 0 0 0 .75.75h12.98a.75.75 0 0 0 .75-.75v-14.4072a.75.75 0 0 0 -.75-.75zm-.75 14.4072h-11.48v-12.9072h11.48z"
  }));
};

IconVideoStop.displayName = "IconVideoStop";
IconVideoStop.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconVideoStop"
};
var VideoStop = withIcon(IconVideoStop);

const IconWarning = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.7051 12.9691-6.5513-11.2378a1.3042 1.3042 0 0 0 -2.3076 0l-6.5513 11.2387a1.3386 1.3386 0 0 0 -.0649 1.3638 1.3379 1.3379 0 0 0 1.2109.6313h13.1177a1.2682 1.2682 0 0 0 1.1465-1.9961zm-8.4551-7.5152a.75.75 0 0 1 1.5 0v4.3023a.75.75 0 1 1 -1.5 0zm.75 7.765a.875.875 0 1 1 .875-.875.875.875 0 0 1 -.875.875z"
  }));
};

IconWarning.displayName = "IconWarning";
IconWarning.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconWarning"
};
var Warning = withIcon(IconWarning);

const IconWorkgroup = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m14.5235 9.27c-.0154-.0092-.0323-.0139-.0478-.0228a6.4808 6.4808 0 0 0 -3.1207-5.7864 6.5189 6.5189 0 0 0 -.64-.3136 2.7625 2.7625 0 0 0 -5.5227.0574 6.4851 6.4851 0 0 0 -2.733 2.4686 6.3907 6.3907 0 0 0 -.9023 3.5756 2.7591 2.7591 0 0 0 -.0781 4.7784 2.7507 2.7507 0 0 0 1.4233.398 2.7844 2.7844 0 0 0 1.1992-.2722 6.5339 6.5339 0 0 0 .57.4162 6.4471 6.4471 0 0 0 2.3359.8511 6.3818 6.3818 0 0 0 .9956.0776 6.5282 6.5282 0 0 0 3.97-1.349 2.731 2.731 0 0 0 1.1174.266 2.8026 2.8026 0 0 0 .6733-.083 2.77 2.77 0 0 0 .76-5.062zm-7.6616-6.6519a1.2705 1.2705 0 1 1 -.144.9614 1.2593 1.2593 0 0 1 .144-.9614zm-3.6484 10.268a1.27 1.27 0 0 1 -1.395-1.8872 1.27 1.27 0 1 1 1.395 1.8872zm4.0259 1.0527a4.96 4.96 0 0 1 -1.7954-.6553c-.0721-.0431-.1334-.1015-.2032-.1484.012-.0189.028-.0341.04-.0533a2.7493 2.7493 0 0 0 -2.244-4.1818 4.9551 4.9551 0 0 1 2.5376-4.226 2.7289 2.7289 0 0 0 2.3705 1.37 2.8025 2.8025 0 0 0 .6733-.083 2.7527 2.7527 0 0 0 1.7051-1.26c.0129-.0214.019-.0446.0313-.0662.0748.04.1548.0679.2275.1116a4.9887 4.9887 0 0 1 2.4108 4.1309 2.826 2.826 0 0 0 -.5661.0776 2.7584 2.7584 0 0 0 -1.6518 4.1812 5.0105 5.0105 0 0 1 -3.5357.8028zm6.9439-1.6406a1.27 1.27 0 1 1 -.4331-1.7432 1.2721 1.2721 0 0 1 .433 1.7432z"
  }));
};

IconWorkgroup.displayName = "IconWorkgroup";
IconWorkgroup.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconWorkgroup"
};
var Workgroup = withIcon(IconWorkgroup);

const IconWrench = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m15.353 2.6743a.7.7 0 0 0 -1.1206-.1807l-2.1055 2.1055-.7269-.7265 2.1055-2.1055a.7.7 0 0 0 -.1807-1.12 4.56 4.56 0 0 0 -6.35 5.6708l-3.8757 3.8759c-.0138.0139-.0187.0322-.0311.0469a2.8047 2.8047 0 1 0 1.8793 4.7861 2.7957 2.7957 0 0 0 .8112-2.0949c.0146-.0124.033-.0173.0468-.0311l3.8758-3.8758a4.5673 4.5673 0 0 0 5.6719-6.3507zm-11.3955 11.3623a1.4092 1.4092 0 1 1 0-1.9941 1.4134 1.4134 0 0 1 0 1.9941zm1.3165-2.5846a2.8 2.8 0 0 0 -.3262-.4 2.7973 2.7973 0 0 0 -.4-.3266l3.148-3.1484a4.5956 4.5956 0 0 0 .3277.3988 4.6 4.6 0 0 0 .3985.3276zm8.25-4.4662a3.2659 3.2659 0 0 1 -4.5107 0 3.1768 3.1768 0 0 1 2.6938-5.4l-1.792 1.792a.7.7 0 0 0 0 .99l1.7163 1.7168a.7.7 0 0 0 .99 0l1.7925-1.793a3.16 3.16 0 0 1 -.8895 2.6942z"
  }));
};

IconWrench.displayName = "IconWrench";
IconWrench.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconWrench"
};
var Wrench = withIcon(IconWrench);

const IconX = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m9.0605 8 4.1395-4.1394a.75.75 0 0 0 -1.06-1.0606l-4.14 4.1394-4.14-4.1394a.75.75 0 0 0 -1.06 1.0606l4.1395 4.1394-4.1395 4.1394a.75.75 0 1 0 1.06 1.0606l4.14-4.1395 4.14 4.1395a.75.75 0 0 0 1.06-1.0606z"
  }));
};

IconX.displayName = "IconX";
IconX.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconX"
};
var X = withIcon(IconX);

const IconXCircle = () => {
  return /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "m13.6233 2.3787a7.95 7.95 0 1 0 -11.2427 11.2426 7.95 7.95 0 0 0 11.2427-11.2426zm-1.0606 10.1821a6.45 6.45 0 1 1 -9.1215-9.1216 6.45 6.45 0 1 1 9.1216 9.1216z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m10.8987 5.1033a.75.75 0 0 0 -1.0605 0l-1.8365 1.8367-1.8364-1.8367a.75.75 0 1 0 -1.0606 1.0605l1.8365 1.8362-1.8365 1.8367a.75.75 0 1 0 1.0605 1.0605l1.8365-1.8364 1.8364 1.8364a.75.75 0 1 0 1.0605-1.0605l-1.8363-1.8367 1.8364-1.8364a.75.75 0 0 0 0-1.0603z"
  }));
};

IconXCircle.displayName = "IconXCircle";
IconXCircle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "IconXCircle"
};
var XCircle = withIcon(IconXCircle);

export { Airplane as IconAirplane, Arrow180 as IconArrow180, Arrow360 as IconArrow360, ArrowBox as IconArrowBox, ArrowCombine as IconArrowCombine, ArrowDashed as IconArrowDashed, ArrowDown as IconArrowDown, ArrowExit as IconArrowExit, ArrowForward as IconArrowForward, ArrowIntegrate as IconArrowIntegrate, ArrowLeft as IconArrowLeft, ArrowRandom as IconArrowRandom, ArrowRefresh as IconArrowRefresh, ArrowRight as IconArrowRight, ArrowSkip as IconArrowSkip, ArrowSpread as IconArrowSpread, ArrowUp as IconArrowUp, Badge as IconBadge, Balloon as IconBalloon, Bell as IconBell, Blocks as IconBlocks, Books as IconBooks, Brush as IconBrush, Calendar as IconCalendar, Camera as IconCamera, CaretDown as IconCaretDown, CaretLeft as IconCaretLeft, CaretRight as IconCaretRight, CaretUp as IconCaretUp, CaretUpDown as IconCaretUpDown, Cart as IconCart, ChartBarHorz as IconChartBarHorz, ChartBarVert as IconChartBarVert, ChartDonut as IconChartDonut, ChartDonutFilled as IconChartDonutFilled, ChartLine as IconChartLine, ChartPie as IconChartPie, ChartSegment as IconChartSegment, Chat as IconChat, Check as IconCheck, CheckBox as IconCheckBox, CheckCircle as IconCheckCircle, ChevronDown as IconChevronDown, ChevronLeft as IconChevronLeft, ChevronRight as IconChevronRight, ChevronUp as IconChevronUp, Circle as IconCircle, Clock as IconClock, Clone as IconClone, Cloud as IconCloud, CloudArrow as IconCloudArrow, Comment as IconComment, CommentCheck as IconCommentCheck, CommentFilled as IconCommentFilled, Copyright as IconCopyright, Danger as IconDanger, Dashboard as IconDashboard, Desktop as IconDesktop, Divisions as IconDivisions, Document as IconDocument, DocumentArrow as IconDocumentArrow, DocumentClone as IconDocumentClone, DocumentDocx as IconDocumentDocx, DocumentList as IconDocumentList, DocumentPdf as IconDocumentPdf, DocumentPlus as IconDocumentPlus, DocumentSearch as IconDocumentSearch, DocumentUser as IconDocumentUser, DocumentX as IconDocumentX, Email as IconEmail, Eye as IconEye, Filter as IconFilter, Flag as IconFlag, Flask as IconFlask, FlowChart as IconFlowChart, Folder as IconFolder, FolderArrow as IconFolderArrow, FolderPencil as IconFolderPencil, FolderUser as IconFolderUser, Footprint as IconFootprint, Gear as IconGear, Globe as IconGlobe, GovBuilding as IconGovBuilding, GraduationHat as IconGraduationHat, Grid as IconGrid, GridCarets as IconGridCarets, GridCircles as IconGridCircles, Happy as IconHappy, HappyFilled as IconHappyFilled, Health as IconHealth, Heart as IconHeart, HeartFilled as IconHeartFilled, HeartHand as IconHeartHand, Help as IconHelp, House as IconHouse, Image as IconImage, Inbox as IconInbox, Info as IconInfo, Key as IconKey, Keyboard as IconKeyboard, Launchpad as IconLaunchpad, Lighting as IconLighting, Link as IconLink, List as IconList, ListBulleted as IconListBulleted, ListChecks as IconListChecks, Lock as IconLock, LogoDrive as IconLogoDrive, LogoFacebook as IconLogoFacebook, LogoGmail as IconLogoGmail, LogoGoldie as IconLogoGoldie, LogoGoogle as IconLogoGoogle, LogoGooglePlus as IconLogoGooglePlus, LogoLinkedIn as IconLogoLinkedIn, LogoMailChimp as IconLogoMailChimp, LogoMessenger as IconLogoMessenger, LogoOffice as IconLogoOffice, LogoOutlook as IconLogoOutlook, LogoPinterest as IconLogoPinterest, LogoTwitter as IconLogoTwitter, LogoWhatsApp as IconLogoWhatsApp, LogoYahooMail as IconLogoYahooMail, LogoYouTube as IconLogoYouTube, Megaphone as IconMegaphone, Menu as IconMenu, Minus as IconMinus, MinusCircle as IconMinusCircle, Mobile as IconMobile, More as IconMore, MoreAndroid as IconMoreAndroid, Move as IconMove, News as IconNews, NoWifi as IconNoWifi, Number1 as IconNumber1, Number10 as IconNumber10, Number2 as IconNumber2, Number3 as IconNumber3, Number4 as IconNumber4, Number5 as IconNumber5, Number6 as IconNumber6, Number7 as IconNumber7, Number8 as IconNumber8, Number9 as IconNumber9, Paragraph as IconParagraph, PencilBox as IconPencilBox, PencilPaper as IconPencilPaper, Plus as IconPlus, PlusCircle as IconPlusCircle, Printer as IconPrinter, Pulse as IconPulse, Search as IconSearch, Shapes as IconShapes, Share as IconShare, ShareAndroid as IconShareAndroid, ShareIOS as IconShareIOS, Shuffle as IconShuffle, ShuffleBox as IconShuffleBox, ShuffleBoxFilled as IconShuffleBoxFilled, Slider as IconSlider, SlidersVert as IconSlidersVert, Star as IconStar, StarFilled as IconStarFilled, TableCol as IconTableCol, TableRow as IconTableRow, Tablet as IconTablet, TabletCheck as IconTabletCheck, Tag as IconTag, Template as IconTemplate, Textbox as IconTextbox, TextboxMultiple as IconTextboxMultiple, ThumbsUp as IconThumbsUp, ThumbsUpFilled as IconThumbsUpFilled, Timer as IconTimer, Toggle as IconToggle, Toolbox as IconToolbox, Trash as IconTrash, Truck as IconTruck, Unlock as IconUnlock, User as IconUser, UserArrow as IconUserArrow, UserFilled as IconUserFilled, UserPlus as IconUserPlus, Users as IconUsers, UsersGroup as IconUsersGroup, Variable as IconVariable, Video as IconVideo, VideoEnd as IconVideoEnd, VideoNext as IconVideoNext, VideoPrev as IconVideoPrev, VideoStart as IconVideoStart, VideoStop as IconVideoStop, Warning as IconWarning, Workgroup as IconWorkgroup, Wrench as IconWrench, X as IconX, XCircle as IconXCircle };
//# sourceMappingURL=index.es.js.map
